import gql from 'graphql-tag';
import apolloClient from '../apollo';
import Vue from 'vue';
import isPlainObject from 'lodash/isPlainObject';
import { PROJECT_REQUEST_FIELDS, PROJECT_FIN_MODEL_FIELDS } from './projectFragment';
import { PROJECT_LIST_REQUEST_FIELDS } from './projectFragmentList';
import { PROJECT_POST_FIELDS } from './projectPostFragment';
import { PROJECT_LIST_MOBILE_REQUEST_FIELDS } from './projectFragmentMobileList';
import { PROJECT_PAGINATED_LIST_REQUEST_FIELDS } from './projectFragmentPaginatedList';
import { deepObjectValue } from '@/helpers/object';
import { preparePayloadForGraphQL } from '../helpers/validate';
import { toItemCountInputs } from '@/helpers/solarPlant';
import { PROJECT_BASE_CONFIGURATION_FIELDS } from '@/blocks/project/projectBaseConfigurationFragment';
import { PROJECT_EXTENDED_CONFIGURATION_FIELDS } from '@/blocks/project/projectExtendedConfigurationFragment';
import { PROJECT_BASE_INFORMATION_FIELDS } from '@/blocks/project/projectBaseInformationFragment';
import { PROJECT_PRESENTATIONS_FIELDS } from '@/blocks/project/projectPresentationsFragment';
import { PROJECT_CLARIFICATION_FIELDS } from '@/blocks/project/projectClarificationFragment';
import { PROJECT_CALCULATION_INTERNAL_FIELDS } from '@/blocks/project/projectCalculationInternalFragment';
import { PROJECT_FINANCIAL_MODEL_FIELDS } from '@/blocks/project/projectFinancialModelFragment';
import { PROJECT_PLANT_DATA_FIELDS } from '@/blocks/project/projectPlantDataFragment';
import { PROJECT_FINANCING_FIELDS } from '@/blocks/project/projectFinancingFragment';
import { PROJECT_IAB_TAX_DATA_FIELDS } from '@/blocks/project/projectIabTaxDataFragment';
import { PROJECT_CUSTOMER_MASTER_DATA_FIELDS } from '@/blocks/project/projectCustomerMasterDataFragment';
import { PROJECT_CUSTOMER_INSTALLATION_ADDRESS_FIELDS } from '@/blocks/project/projectCustomerInstallationAddressFragment';
import { PROJECT_CUSTOMER_TAX_BANK_FIELDS } from '@/blocks/project/projectCustomerTaxBankFragment';
import { PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_FIELDS } from '@/blocks/project/projectCustomerAdditionalInformationFragment';
import { PROJECT_PARTIES_FIELDS } from '@/blocks/project/projectPartiesFragment';
import { toProjectPartiesInput } from '@/blocks/project/projectPartiesInput';
import { PROJECT_EMPLOYER_COMMUNICATION_FIELDS } from '@/blocks/project/projectEmployerCommunicationFragment';
import { PROJECT_EMPLOYER_COMMUNICATION_FIELD_TYPES } from '@/blocks/project/projectEmployerCommunicationInput';
import { PROJECT_INVOICE_APPROVAL_FIELDS } from '@/blocks/project/projectInvoiceApprovalFragment';
import { PROJECT_INVOICE_APPROVAL_FIELD_TYPES } from '@/blocks/project/projectInvoiceApprovalInput';
import { PROJECT_POSTS_FIELDS } from '@/blocks/project/projectPostsFragment';
import { PROJECT_TECHNICAL_REVIEW_DC_FIELDS } from '@/blocks/project/projectTechnicalReviewDcFragment';
import { PROJECT_TECHNICAL_REVIEW_AC_FIELDS } from '@/blocks/project/projectTechnicalReviewAcFragment';
import { PROJECT_TIME_TRACKING_SUMMARY_FIELDS } from '@/blocks/project/projectTimeTrackingSummaryFragment';
import { PROJECT_ROUGH_PLANNING_FIELDS } from '@/blocks/project/projectRoughPlanningFragment';
import { PROJECT_APPOINTMENT_NOTES_FIELDS } from '@/blocks/project/projectAppointmentNotesFragment';
import { PROJECT_POWER_COMPANY_FIELDS } from '@/blocks/project/projectPowerCompanyFragment';
import { PROJECT_POWER_COMPANY_FIELD_TYPES } from '@/blocks/project/projectPowerCompanyInput';
import { PROJECT_POWER_COMPANY_COMMUNICATION_FIELDS } from '@/blocks/project/projectPowerCompanyCommunicationFragment';
import { PROJECT_POWER_COMPANY_COMMUNICATION_FIELD_TYPES } from '@/blocks/project/projectPowerCompanyCommunicationInput';
import { PROJECT_MEASUREMENT_POINT_OPERATOR_FIELDS } from '@/blocks/project/projectMeasurementPointOperatorFragment';
import { PROJECT_BANK_CUSTOMER_FIELDS } from '@/blocks/project/projectBankCustomerFragment';
import { PROJECT_BANK_SELF_DISCLOSURE_FIELDS } from '@/blocks/project/projectBankSelfDisclosureFragment';
import { PROJECT_BANK_EMPLOYMENT_INCOME_FIELDS } from '@/blocks/project/projectBankEmploymentIncomeFragment';
import { PROJECT_BANK_SELF_EMPLOYMENT_FIELDS } from '@/blocks/project/projectBankSelfEmploymentFragment';
import { PROJECT_BANK_RENTAL_INCOME_FIELDS } from '@/blocks/project/projectBankRentalIncomeFragment';
import { PROJECT_BANK_CAPITAL_ASSETS_FIELDS } from '@/blocks/project/projectBankCapitalAssetsFragment';
import { PROJECT_BANK_SUMMARY_FIELDS } from '@/blocks/project/projectBankSummaryFragment';
import { PROJECT_DOCUMENTS_FIELDS } from '@/blocks/project/projectDocumentsFragment';
import { PROJECT_MEASUREMENT_REPORT_BUILD_FIELDS } from '@/blocks/project/projectMeasurementReportBuildFragment';
import { PROJECT_MEASUREMENT_REPORT_VERIFICATION_FIELDS } from '@/blocks/project/projectMeasurementReportVerificationFragment';
import { PROJECT_MEASUREMENT_REPORT_APPROVAL_FIELDS } from '@/blocks/project/projectMeasurementReportApprovalFragment';
import { PROJECT_MEASUREMENT_REPORT_DATA_FIELDS } from '@/blocks/project/projectMeasurementReportDataFragment';
import { PROJECT_ACCEPTANCE_REPORT_BUILD_FIELDS } from '@/blocks/project/projectAcceptanceReportBuildFragment';
import { PROJECT_ACCEPTANCE_REPORT_VERIFICATION_FIELDS } from '@/blocks/project/projectAcceptanceReportVerificationFragment';
import { PROJECT_ACCEPTANCE_REPORT_APPROVAL_FIELDS } from '@/blocks/project/projectAcceptanceReportApprovalFragment';
import { PROJECT_ACCEPTANCE_REPORT_DATA_FIELDS } from '@/blocks/project/projectAcceptanceReportDataFragment';
import { PROJECT_SERVICE_REPORT_BUILD_FIELDS } from '@/blocks/project/projectServiceReportBuildFragment';
import { PROJECT_SERVICE_REPORT_VERIFICATION_FIELDS } from '@/blocks/project/projectServiceReportVerificationFragment';
import { PROJECT_SERVICE_REPORT_APPROVAL_FIELDS } from '@/blocks/project/projectServiceReportApprovalFragment';
import { PROJECT_SERVICE_REPORT_DATA_FIELDS } from '@/blocks/project/projectServiceReportDataFragment';
import { PROJECT_QUALITY_REPORT_DATA_FIELDS } from '@/blocks/project/projectQualityReportDataFragment';
import { PROJECT_SALES_REPORT_BUILD_FIELDS } from '@/blocks/project/projectSalesReportBuildFragment';
import { PROJECT_SALES_REPORT_DATA_FIELDS } from '@/blocks/project/projectSalesReportDataFragment';
import { ENPAL_REPORT_SELECTION } from './acceptanceReportSelection';
import { SERVICE_REPORT_SELECTION } from './serviceReportSelection';
import { QUALITY_REPORT_SELECTION } from './qualityReportSelection';
import { SALES_REPORT_SELECTION } from './salesReportSelection';
import { MEASUREMENT_SELECTION } from '@/store/measurementSelection';
import { PROJECT_MEASUREMENT_POINT_OPERATOR_FIELD_TYPES } from '@/blocks/project/projectMeasurementPointOperatorInput';
import { POWER_COMPANY_OF_PROJECT_FIELDS } from '@/store/powerCompanyOfProjectFragment';
import { MEASUREMENT_POINT_OPERATOR_OF_PROJECT_FIELDS } from '@/store/measurementPointOperatorOfProjectFragment';
import {
  PROJECT_EMAIL_EXCERPT_FIELDS,
  PROJECT_EMAIL_FIELDS,
} from '@/blocks/project/projectEmailFragment';
import { EMPTY_INSTALLATION_ADDRESS } from '@/blocks/project/projectCustomerInstallationAddressInput';
import { PROJECT_CALCULATION_RECOMPUTED_FIELDS } from '@/blocks/project/projectCalculationRecomputedFragment';
import { PROJECT_CALCULATION_INTERNAL_FIELD_TYPES } from '@/blocks/project/projectCalculationInternalInput';
import {
  PROJECT_BASE_INFORMATION_FIELD_TYPES,
  toCustomerNumberInput,
} from '@/blocks/project/projectBaseInformationInput';
import { PROJECT_CONFIGURATION_RECOMPUTED_FIELDS } from '@/blocks/project/projectConfigurationRecomputedFragment';
import { PROJECT_FIELDS_SAVED_BY_BLOCKS } from '@/blocks/project/projectFieldsSavedByBlocks';
import {
  PROJECT_BASE_CONFIGURATION_BLOCK,
  PROJECT_EXTENDED_CONFIGURATION_BLOCK,
  PROJECT_BASE_INFORMATION_BLOCK,
  PROJECT_PRESENTATIONS_BLOCK,
  PROJECT_CLARIFICATION_BLOCK,
  PROJECT_CALCULATION_BLOCK,
  PROJECT_FINANCIAL_MODEL_BLOCK,
  PROJECT_PLANT_DATA_BLOCK,
  PROJECT_FINANCING_BLOCK,
  PROJECT_IAB_TAX_DATA_BLOCK,
  PROJECT_CUSTOMER_MASTER_DATA_BLOCK,
  PROJECT_CUSTOMER_INSTALLATION_ADDRESS_BLOCK,
  PROJECT_CUSTOMER_TAX_BANK_BLOCK,
  PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_BLOCK,
  PROJECT_PARTIES_BLOCK,
  PROJECT_EMPLOYER_COMMUNICATION_BLOCK,
  PROJECT_INVOICE_APPROVAL_BLOCK,
  PROJECT_POSTS_BLOCK,
  PROJECT_EMAIL_BLOCK,
  PROJECT_TECHNICAL_REVIEW_DC_BLOCK,
  PROJECT_TECHNICAL_REVIEW_AC_BLOCK,
  PROJECT_TIME_TRACKING_SUMMARY_BLOCK,
  PROJECT_ROUGH_PLANNING_BLOCK,
  PROJECT_APPOINTMENT_NOTES_BLOCK,
  PROJECT_POWER_COMPANY_BLOCK,
  PROJECT_POWER_COMPANY_COMMUNICATION_BLOCK,
  PROJECT_MEASUREMENT_POINT_OPERATOR_BLOCK,
  PROJECT_BANK_CUSTOMER_BLOCK,
  PROJECT_BANK_SELF_DISCLOSURE_BLOCK,
  PROJECT_BANK_EMPLOYMENT_INCOME_BLOCK,
  PROJECT_BANK_SELF_EMPLOYMENT_BLOCK,
  PROJECT_BANK_RENTAL_INCOME_BLOCK,
  PROJECT_BANK_CAPITAL_ASSETS_BLOCK,
  PROJECT_BANK_SUMMARY_BLOCK,
  PROJECT_DOCUMENTS_BLOCK,
  PROJECT_MEASUREMENT_REPORT_BUILD_BLOCK,
  PROJECT_MEASUREMENT_REPORT_VERIFICATION_BLOCK,
  PROJECT_MEASUREMENT_REPORT_APPROVAL_BLOCK,
  PROJECT_MEASUREMENT_REPORT_DATA_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_BUILD_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_VERIFICATION_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_APPROVAL_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_DATA_BLOCK,
  PROJECT_SERVICE_REPORT_BUILD_BLOCK,
  PROJECT_SERVICE_REPORT_VERIFICATION_BLOCK,
  PROJECT_SERVICE_REPORT_APPROVAL_BLOCK,
  PROJECT_SERVICE_REPORT_DATA_BLOCK,
  PROJECT_QUALITY_REPORT_DATA_BLOCK,
  PROJECT_SALES_REPORT_BUILD_BLOCK,
  PROJECT_SALES_REPORT_DATA_BLOCK,
} from '@/navigation/accessNodes';

/** Must match {@link PROJECT_ATTACHMENT_TYPES} in apolloserver (unified `attachments` array) */
const UNIFIED_PROJECT_ATTACHMENT_TYPE_VALUES = [
  'SERVICE_CONTRACT',
  'SHORT_TEASER',
  'SHORT_TEASER_PROPERTY_IMAGE',
  'MASTER_PRESENTATION',
  'SALES_REPORT',
  'FIN_MODEL',
];

/**
 * State
 */
const defaultProject = () => {
  return {
    id: '_new',
    number: '_new',
    employerProjectNumber: '',
    client: null,
    employer: null,
    measurement: {
      roofType: 'PITCHED_ROOF',
      pitchedRoof: {},
      flatRoof: {},
      commonRoof: {},
      ac: {},
      customer: {},
      completed: [],
    },
    enpalReport: { completed: [] },
    serviceReport: { completed: [] },
    salesReport: { completed: [] },
    assessmentReport: { completed: [] },

    qualityReport: { completed: [] },

    bricksPlasteredAttachments: [],
    customer: {
      birthdayAt: null,
    },
    installationAddress: {
      street: '',
      streetNumber: '',
      streetNumberSuffix: '',
      zip: '',
      city: '',
      cityPart: '',
    },
    installation: {
      electricMeterChangedAt: null,
      operationRegistrationAt: null,
    },
    sales: {
      phase: 'SALES_PHASE_LEAD',
      state: null,
      package: '',
      inCharge: null,
      usage: '',
      kwhprice: '',
      notes: '',
      callState: 'SALES_CALL_STATE_ZERO',
      householdMembers: '',
      electricMeter: '',
      notesAnalysis: '',
      notesAssembly: '',
      notesPlanning: '',
      yearlyExpense: '',
      handover: {
        handedOverAt: null,
        handedOverBy: null,
        note: '',
      },
    },
    solarPlant: {
      netOutputStates: ['L1', 'L2', 'L3'],
      networkSupplyManagementState: 'NONE',
      houseFuseAmpere: 35,
      solarPlantOverallPower: 0,
    },
    items: [],
    mandateAttachments: [],
    overviewSchematicAttachments: [],
    overviewSchematicAttachmentsGenerated: [],
    openMateboxAttachments: [],
    installedComponentsInMeterCabinetAttachments: [],
    openMeterCabinetPrePostAreaAttachments: [],
    closedMeterCabinetInscribedComponentsAttachments: [],
    closedPreMeterAreaWithPlombsAttachments: [],
    breakthroughsWithSealingsMeterCabinetAttachments: [],
    openHAKAttachments: [],
    closedHAKWithSealAttachments: [],
    photoOfWallboxAttachments: [],
    accessCardsOfWallboxAttachments: [],
    circuitBreakerAndSafetySwitchForWallboxAttachments: [],
    cablingBetweenWallboxAndDistributionAttachments: [],
    acceptanceProtocolCustomerSignatureAttachments: [],
    warrantyQCellsAttachments: [],
    cableManagementBetweenMateboxAndMeterCabinetAttachments: [],
    variousBreakthroughsWithPossibleSealingsAttachments: [],
    solarModuleTypePlateAttachments: [],
    reportAttachments: [],
    layoutPlanAttachments: [],
    projectDescriptionAttachments: [],
    installationImageAttachments: [],
    installationTeamDocumentationAttachments: [],
    netRegistrationAttachments: [],
    netRegistrationAttachmentsGenerated: [],
    operationRegistrationAttachments: [],
    operationRegistrationImageAttachments: [],
    operationRegistrationAttachmentsGenerated: [],
    miscAttachments: [],
    measurementAttachments: [],
    teamDCAttachments: [],
    roofAttachments: [],
    roofTileAttachments: [],
    meterCabinetAttachments: [],
    teamACAttachments: [],
    damageAttachments: [],
    netRegistrationRequirements: [],
    operationRegistrationRequirements: [],
    projectState: 'PROJECT_STATE_NEW',
    projectTypeState: 'NEW_PROJECT',
    askEmployerCategoryState: null,
    mandateState: 'MISSING',
    layoutPlanState: 'MISSING',
    projectDescriptionState: 'MISSING',
    installationImagesState: 'MISSING',
    installationTeamDocumentationState: 'MISSING',
    netRegistrationRequirementsFullfilledState: 'NO',
    operationRegistrationRequirementsFullfilledState: 'NO',
    installationAddressState: 'NO',
    itemPlanningFinishedState: 'NO',
    netRegistrationAttachmentsBuildState: 'NOT_STARTED',
    operationRegistrationAttachmentsBuildState: 'NOT_STARTED',
    projectOfferAttachmentsBuildState: 'NOT_STARTED',
    consumablesListAttachmentsBuildState: 'NOT_STARTED',
    reportAttachmentsBuildState: 'NOT_STARTED',
    consumablesOrderedState: 'NO',
    consumablesInStockState: 'NO',
    technicalReviewDescription: '',
    onHoldReason: '',
    accountingDescription: '',
    powerCompanyDescription: '',
    customerDescription: '',
    scaffoldAttachments: [],
    jobSafetyOptionalAttachments: [],
    ballastAttachments: [],
    brickAndSubstructureOptionalAttachments: [],
    brickAndSubstructureAttachments: [],
    solarPanelAttachments: [],
    cableManagementAttachments: [],
    equipotentialBusBarAttachments: [],
    inverterAttachments: [],
    storageAttachments: [],
    electricMeterAttachments: [],
    systemPowerOnAttachments: [],
    electricMeterChangeAttachments: [],
    signatureAttachments: [],
    deliveryAt: null,
    technichalReviewAt: null,
    offerSentAt: null,
    orderConfirmationSentAt: null,
    consumablesOrderedAt: null,
    consumablesDeliveredAt: null,
    acceptancePlannedAt: null,
    acceptanceCompletedAt: null,
    calculatedExpectedTimeExpenditureInDays: null,
    calculatedExpectedTimeExpenditureInDaysOverridden: false,
    profitMargin: null,
    powerCompanyClearanceAt: null,
    invoiceSentToEmployerAt: null,
    employerPayedAt: null,
    installationTeamPayedAt: null,
    documentationSentToEmployerAt: null,
    completionMessageSentAt: null,
    injectionConfirmationReceivedAt: null,
    operationRegistrationSentAt: null,
    netRegistrationSentAt: null,
    brickCoverWidthAttachments: [],
    correctBrickHandlingAttachments: [],
    roofHookWithEarthingAttachments: [],
    correctCableInputAttachments: [],
    protectedUnderBrickCableRoutingAttachments: [],
    stringPlanAttachments: [],
    allPanelsVisibleAttachments: [],
    panelBottomViewAttachments: [],
    solarPanelsOptionalAttachments: [],
    railOverlapAttachments: [],
    clampAreaAttachments: [],
    trayfDistanceAttachments: [],
    cableAlongFacadeAttachments: [],
    cableBetweenMateboxAndElectricMeterAttachments: [],
    cableManagementOptionalAttachments: [],
    inverterOptionalAttachments: [],
    inverterSerialNumberAttachments: [],
    mateboxSerialNumberAttachments: [],
    storageSerialNumberAttachments: [],
    dongleSerialNumberAttachments: [],
    potentialGroundRodAttachments: [],
    potentialOptionalAttachments: [],
    electricMeterReadingAttachments: [],
    gakAttachments: [],
    screenshotDeviceFlow: [],
    screenshotModeOfOperation: [],
    netConnectionTerms: [],
    monitoringOptionalAttachments: [],
    electricMeterAndGakOptionalAttachments: [],
    measurementResultAttachments: [],
    measurementProtocolInclPhotosAttachments: [],
    measurementsOptionalAttachments: [],
    properSleeveUsedAtRoofPenetrationAttachments: [],
    underlaySealedWellAtCableEntryAttachments: [],
    underlayDamagedOrWaterStainsAttachments: [],
    labelingOfStringLinesAttachments: [],
    pvStringCableCorrectCrossSectionAttachments: [],
    peCableCorrectlySizedAttachments: [],
    cablesProperlySecuredAttachments: [],
    crimpingProfessionallyDoneAttachments: [],
    correctSelectionOfPvConnectorsOnStringCableAttachments: [],
    allPlugConnectionsProfessionallyMadeAttachments: [],
    cablesNeatlyLaidInsideBuildingAttachments: [],
    spdBoxPresentIfCableInBuildingLongerThan10mInstalledBeforeInverterAttachments: [],
    surgeProtectionBoxProperlyWiredInstallSpdAtRoofEntryAttachments: [],
    documentationProjectReportAvailableAttachments: [],
    specialCustomerRequestsDocumentedAttachments: [],
    tileTypeAndWidthDocumentedAttachments: [],
    replacementTileInstalledAttachments: [],
    lightningProtectionPresentAttachments: [],
    substructureTensionFreeCheckedAttachments: [],
    substructureInstalledAsPerReportAttachments: [],
    eachRailSecuredWithMinTwoHooksAttachments: [],
    distanceBetweenHooksMoreThan140cmAttachments: [],
    correctRoofHooksUsedAttachments: [],
    distanceRoofHookToTile35mmAttachments: [],
    minScrewDistanceToRafterEdge24mmAttachments: [],
    roofHookProperlyFixedToRafterAttachments: [],
    overhangObservedAttachments: [],
    moduleFieldProperlyGroundedAttachments: [],
    approvedAuxiliaryRafterUsedAttachments: [],
    rafterWidthMeasuredAndComparedToDocsAttachments: [],
    noOpenOrDamagedTilesAttachments: [],
    noShiftedOrIncorrectlyLaidTilesAttachments: [],
    tilesProperlyCleanedAttachments: [],
    correctPanHeadScrewsUsedAttachments: [],
    minRailOverhangAfterEndClamp20mmAttachments: [],
    distancesToRidgeEavesNotLessThan20cmAttachments: [],
    moduleClampAreasObservedAttachments: [],
    modulesNoDamagesDirtShadingAttachments: [],
    moduleClampsProperlyTightenedAttachments: [],
    thermalSeparationAfter12mModuleFieldAttachments: [],
    breakthroughProperlySealedAttachments: [],
    distanceLightningProtectionToConductivePartsLessThan05mAttachments: [],
    gutterUndamagedAttachments: [],
    notBuiltOverRoofOverhangAttachments: [],
    cableProperlyLaidInChimneyAttachments: [],
    distanceToDisturbingSurfaces10cmObservedAttachments: [],
    cableEntryWithReplacementPlateAndFlexedTileAttachments: [],
    cablesDoNotRestOnTilesAttachments: [],
    cablesLaidProtectedUnderTilesAttachments: [],
    cableEntryOnRoofProperlyDoneAttachments: [],
    cableLaidThroughChimneyAttachments: [],
    cableLaidInProtectiveSleeveAgainstUvRadiationAttachments: [],
    peCableProperlySecuredAttachments: [],
    documentationProjectReportPresentAttachments: [],
    customerSpecialRequestsConsideredAttachments: [],
    flatRooflightningProtectionPresentAttachments: [],
    properGroundingFlatRoofSystemAttachments: [],
    ballastingAccordingToProjectReportAttachments: [],
    minimumModuleBearingAreaMaintainedAttachments: [],
    qFlatProperlyConnectedAttachments: [],
    distanceToDisturbingSurfacesMaintainedAttachments: [],
    flatRoofmoduleClampsProperlyTightenedAttachments: [],
    modulesNoDamageDirtShadingAttachments: [],
    typeOfCableProtectionOutdoorsAttachments: [],
    correctSelectionOfPvConnectorsAttachments: [],
    cablesDoNotRestOnRoofAttachments: [],
    cableEntryDoneProperlyAttachments: [],
    cablesProtectedFromUvAttachments: [],
    pvStringCableProperlySizedAttachments: [],
    stringCableLabelingAttachments: [],
    peCableProperlySizedAttachments: [],
    flatRoofcablesProperlySecuredAttachments: [],
    crimpingProperlyDoneAttachments: [],
    pvSystemLabeledInOrAtDistributionBoardAttachments: [],
    testReportAvailableWithAllMeasurementsAttachments: [],
    rcdTypeB300maAvailableInMeterAreaAttachments: [],
    stringPlanFiledInMeterCabinetAttachments: [],
    safetyDistancesMaintainedAttachments: [],
    correctLocationForSystemAttachments: [],
    correctLocationForWallboxAttachments: [],
    customerRequestsDocumentedAttachments: [],
    slsInstalledAttachments: [],
    componentsDamagedAttachments: [],
    crimpingDoneProperlyAttachments: [],
    ussWiredProperlyInCabinetAttachments: [],
    ussBoxWiredProperlyAttachments: [],
    bendingRadiiMaintainedAttachments: [],
    correctWiringOfMateboxAttachments: [],
    correctWiringOfBatteryAttachments: [],
    allComponentsProperlyGroundedAttachments: [],
    correctColorCodingAndLabelingAttachments: [],
    correctWiringOfPvConnectorsAttachments: [],
    cableDuctProperlySizedAndCutAttachments: [],
    nhHakSelectiveToSlsAttachments: [],
    checkNetworkTypeAttachments: [],
    earthRodConnectedToHpasAttachments: [],
    connectionBetweenHpasAndPasProperlyMadeAttachments: [],
    insulationProperlyStrippedAttachments: [],
    polarityCheckAttachments: [],
    stringOpenCircuitVoltageAttachments: [],
    shortCircuitCurrentAttachments: [],
    insulationResistanceAttachments: [],

    // Sales Report Attachments
    salesRoofSidesAttachments: [],
    salesHakAttachments: [],
    salesZsWithCoverAttachments: [],

    projectDeferredState: {
      deferred: 'NO',
      reason: 'MISC',
      description: '',
      comment: '',
    },
    technicalReview: {},
    investmentDeductionAllowance: {},
    bank: { gbrStatus: 'NONE' },
    posts: [],
    vlink: {},
  };
};

const getDefaultState = () => {
  return {
    projects: [],
    documentStates: [],
    projectStates: [],
    networkSupplyManagementStates: [],
    networkSupplyManagementPermanentRestrictionStates: [],
    networkSupplyManagementXOverStates: [],
    storageCouplingStates: [],
    netOutputStates: [],
    electricMeterMountingStates: [],
    houseFuseStates: [],
    salutations: [],
    installationTeams: [],
    installationTypes: [],
    originalInstallationTypes: [],
    solarSystemTypes: [],
    projectAttachmentTypes: [],
    items: [],
    projectDescriptionState: 'MISSING',
    projectCount: 0,
    isProjectCacheOutdated: false,
    projectCacheActive: false,
    projectsItemsList: [],
    projectsItemsListCount: 0,
  };
};
const state = getDefaultState();

/** Views read `installationAddress` fields directly; a project without one gets empty fields. */
const setDefaultInstallationAddress = (project) => {
  if (!project.installationAddress) {
    project.installationAddress = { ...EMPTY_INSTALLATION_ADDRESS };
  }
};

/** Shows the first GraphQL error of a failed project save and rethrows. */
const handleProjectSaveError = (dispatch, err) => {
  const message = err.graphQLErrors[0].message;
  dispatch('message', {
    message: message,
    type: 'danger',
    lifetime: 1,
  });
  throw new Error(err);
};

/** Saves the page payload with `updateProject` and returns the saved project. */
const saveProject = async ({ dispatch }, payload, projectNumber) => {
  try {
    const response = await apolloClient.mutate({
      mutation: gql`
        mutation updateProject($projectNumber: Int!, $payload: ProjectInput!) {
          updateProject(projectNumber: $projectNumber, projectInput: $payload) {
            ...projectFields
          }
        }
        ${PROJECT_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber: projectNumber,
        payload: payload,
      },
    });
    dispatch('message', {
      message: 'Projekt aktualisiert!',
      type: 'success',
      lifetime: 1,
    });
    return response.data.updateProject;
  } catch (err) {
    handleProjectSaveError(dispatch, err);
  }
};

/** Fields of a block object that identify it; they are not merged into the legacy fields. */
const BLOCK_IDENTITY_FIELDS = ['__typename', 'id', 'number'];

/**
 * Saves one block of the project with its own mutation (ADR 0006 D2) and merges the result into
 * the stored project, so unsaved edits elsewhere on the page stay. The saved block fields are
 * also merged into the legacy fields of the project (e.g. `project.solarPlant`,
 * `project.customer`), which the other tabs still read (legacy path, ADR 0006 addendum
 * 2026-10-08). Subdocuments are merged key by key, never replaced.
 * `isMergedIntoStoredBlock`: the result has only some fields of the block (e.g. a state changed
 * immediately); they are set on the stored block object, which stays with its unsaved edits.
 */
const saveProjectBlock = async (
  { commit, dispatch },
  { mutation, mutationName, variables, blockField, isMergedIntoStoredBlock = false }
) => {
  try {
    const response = await apolloClient.mutate({ mutation, variables });
    dispatch('message', {
      message: 'Projekt aktualisiert!',
      type: 'success',
      lifetime: 1,
    });
    const savedProject = response.data[mutationName];
    const savedBlockFields = Object.fromEntries(
      Object.entries(savedProject.blocks[blockField]).filter(
        ([field]) => !BLOCK_IDENTITY_FIELDS.includes(field)
      )
    );
    if (isMergedIntoStoredBlock) {
      commit('MERGE_PROJECT_BLOCK_FIELDS', {
        id: savedProject.id,
        blockField,
        blockFields: savedBlockFields,
      });
    } else {
      commit('MERGE_PROJECT_FIELDS', savedProject);
    }
    commit('MERGE_PROJECT_FIELDS', { id: savedProject.id, ...savedBlockFields });
  } catch (err) {
    handleProjectSaveError(dispatch, err);
  }
};

/**
 * As `saveProjectBlock` with `isMergedIntoStoredBlock`, for a result that selects some fields of
 * several blocks (e.g. a state shown by two blocks): sets them on each stored block object present
 * in the result and on the legacy fields. A block missing from the result (no level) stays.
 */
const saveProjectBlocks = async (
  { commit, dispatch },
  { mutation, mutationName, variables, blockFields }
) => {
  try {
    const response = await apolloClient.mutate({ mutation, variables });
    dispatch('message', {
      message: 'Projekt aktualisiert!',
      type: 'success',
      lifetime: 1,
    });
    const savedProject = response.data[mutationName];
    blockFields
      .filter((blockField) => savedProject.blocks?.[blockField])
      .forEach((blockField) => {
        const savedBlockFields = Object.fromEntries(
          Object.entries(savedProject.blocks[blockField]).filter(
            ([field]) => !BLOCK_IDENTITY_FIELDS.includes(field)
          )
        );
        commit('MERGE_PROJECT_BLOCK_FIELDS', {
          id: savedProject.id,
          blockField,
          blockFields: savedBlockFields,
        });
        commit('MERGE_PROJECT_FIELDS', { id: savedProject.id, ...savedBlockFields });
      });
  } catch (err) {
    handleProjectSaveError(dispatch, err);
  }
};

/**
 * Selection of the measurement verification states in the results of the request and the
 * decision: both blocks show them; each is selected only with a level (as the blocks query).
 */
const MEASUREMENT_VERIFICATION_STATE_BLOCKS_SELECTION = `
  blocks {
    measurementReportData @include(if: $withMeasurementReportData) {
      id
      number
      measurementAcVerificationState
      measurementDcVerificationState
    }
    measurementReportVerification @include(if: $withMeasurementReportVerification) {
      ...projectMeasurementReportVerificationFields
    }
  }
`;

const measurementVerificationStateVariables = (rootGetters) => ({
  withMeasurementReportData:
    rootGetters.getAccessLevel(PROJECT_MEASUREMENT_REPORT_DATA_BLOCK) !== null,
  withMeasurementReportVerification:
    rootGetters.getAccessLevel(PROJECT_MEASUREMENT_REPORT_VERIFICATION_BLOCK) !== null,
});

/**
 * Selection of the acceptance verification states in the results of the request and the
 * decision: both blocks show them; each is selected only with a level (as the blocks query).
 */
const ACCEPTANCE_VERIFICATION_STATE_BLOCKS_SELECTION = `
  blocks {
    acceptanceReportData @include(if: $withAcceptanceReportData) {
      id
      number
      substructureVerificationState
      dcVerificationState
      acVerificationState
      scaffoldingVerificationState
    }
    acceptanceReportVerification @include(if: $withAcceptanceReportVerification) {
      ...projectAcceptanceReportVerificationFields
    }
  }
`;

const acceptanceVerificationStateVariables = (rootGetters) => ({
  withAcceptanceReportData:
    rootGetters.getAccessLevel(PROJECT_ACCEPTANCE_REPORT_DATA_BLOCK) !== null,
  withAcceptanceReportVerification:
    rootGetters.getAccessLevel(PROJECT_ACCEPTANCE_REPORT_VERIFICATION_BLOCK) !== null,
});

/**
 * Selection of the service verification state in the results of the request and the decision:
 * both blocks show it; each is selected only with a level (as the blocks query).
 */
const SERVICE_VERIFICATION_STATE_BLOCKS_SELECTION = `
  blocks {
    serviceReportData @include(if: $withServiceReportData) {
      id
      number
      serviceVerificationState
    }
    serviceReportVerification @include(if: $withServiceReportVerification) {
      ...projectServiceReportVerificationFields
    }
  }
`;

const serviceVerificationStateVariables = (rootGetters) => ({
  withServiceReportData: rootGetters.getAccessLevel(PROJECT_SERVICE_REPORT_DATA_BLOCK) !== null,
  withServiceReportVerification:
    rootGetters.getAccessLevel(PROJECT_SERVICE_REPORT_VERIFICATION_BLOCK) !== null,
});

/**
 * True if the user has a level on the time tracking summary block. Without one, a save must not
 * select the block: its read check would fail the save response.
 */
const hasTimeTrackingSummaryLevel = (rootGetters) =>
  rootGetters.getAccessLevel(PROJECT_TIME_TRACKING_SUMMARY_BLOCK) !== null;

/** A package result has only the base block: keeps the other blocks of the stored project. */
const withStoredBlocks = (projects, savedProject) => {
  const storedProject = projects.find((project) => project.id === savedProject.id);
  return { ...savedProject, blocks: { ...storedProject?.blocks, ...savedProject.blocks } };
};

/**
 * Queries one block of the project and merges only `blocks.<blockField>` into the stored project.
 * On errors nothing is stored and null is returned. Shared by the block reload actions; a plain
 * function, not a dispatched action, so the loading plugin sees only the calling action.
 */
const queryProjectBlock = async (commit, { projectNumber, blockField, fragment }) => {
  const [fragmentDefinition] = fragment.definitions;
  try {
    const { data, errors } = await apolloClient.query({
      query: gql`
        query projectBlockByNumber($projectNumber: Int!) {
          projectByNumber(projectNumber: $projectNumber) {
            id
            number
            blocks {
              ${blockField} {
                ...${fragmentDefinition.name.value}
              }
            }
          }
        }
        ${fragment}
      `,
      variables: { projectNumber: parseInt(projectNumber, 10) },
      fetchPolicy: 'network-only',
    });

    if (errors) {
      return null;
    }

    const { id, blocks } = data.projectByNumber;
    commit('MERGE_PROJECT_FIELDS', { id, blocks: { [blockField]: blocks[blockField] } });
    return blocks[blockField];
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} project
 * @param {{ excludedFields?: string[] }} options - top-level fields left out of the payload
 *   (e.g. fields saved by blocks, ADR 0006 D9)
 */
const createPayload = (project, { excludedFields = [] } = {}) => {
  // console.log(project.client.name);
  if (project.enpalReport) {
    delete project.enpalReport.__typename;
    project.enpalReport.uploadedSignatureApprovalCustomer =
      project.enpalReport.uploadedSignatureApprovalCustomer &&
      project.enpalReport.uploadedSignatureApprovalCustomer.id
        ? project.enpalReport.uploadedSignatureApprovalCustomer.id
        : null;
    project.enpalReport.uploadedSignatureApprovalTechnician =
      project.enpalReport.uploadedSignatureApprovalTechnician &&
      project.enpalReport.uploadedSignatureApprovalTechnician.id
        ? project.enpalReport.uploadedSignatureApprovalTechnician.id
        : null;
    project.enpalReport.uploadedSignatureOperationCustomer =
      project.enpalReport.uploadedSignatureOperationCustomer &&
      project.enpalReport.uploadedSignatureOperationCustomer.id
        ? project.enpalReport.uploadedSignatureOperationCustomer.id
        : null;
    project.enpalReport.uploadedSignatureOperationTechnician =
      project.enpalReport.uploadedSignatureOperationTechnician &&
      project.enpalReport.uploadedSignatureOperationTechnician.id
        ? project.enpalReport.uploadedSignatureOperationTechnician.id
        : null;
    project.enpalReport.scaffoldAttachment =
      project.enpalReport.scaffoldAttachment && project.enpalReport.scaffoldAttachment.id
        ? project.enpalReport.scaffoldAttachment.id
        : null;
    project.enpalReport.brickAndUkAttachment_X =
      project.enpalReport.brickAndUkAttachment_X && project.enpalReport.brickAndUkAttachment_X.id
        ? project.enpalReport.brickAndUkAttachment_X.id
        : null;
    project.enpalReport.brickAndUkAttachment_A =
      project.enpalReport.brickAndUkAttachment_A && project.enpalReport.brickAndUkAttachment_A.id
        ? project.enpalReport.brickAndUkAttachment_A.id
        : null;
    project.enpalReport.brickAndUkAttachment_C1 =
      project.enpalReport.brickAndUkAttachment_C1 && project.enpalReport.brickAndUkAttachment_C1.id
        ? project.enpalReport.brickAndUkAttachment_C1.id
        : null;
    project.enpalReport.brickAndUkAttachment_C1_2 =
      project.enpalReport.brickAndUkAttachment_C1_2 &&
      project.enpalReport.brickAndUkAttachment_C1_2.id
        ? project.enpalReport.brickAndUkAttachment_C1_2.id
        : null;
    project.enpalReport.brickAndUkAttachment_C1_3 =
      project.enpalReport.brickAndUkAttachment_C1_3 &&
      project.enpalReport.brickAndUkAttachment_C1_3.id
        ? project.enpalReport.brickAndUkAttachment_C1_3.id
        : null;
    project.enpalReport.brickAndUkAttachment_E =
      project.enpalReport.brickAndUkAttachment_E && project.enpalReport.brickAndUkAttachment_E.id
        ? project.enpalReport.brickAndUkAttachment_E.id
        : null;
    project.enpalReport.brickAndUkAttachment_G =
      project.enpalReport.brickAndUkAttachment_G && project.enpalReport.brickAndUkAttachment_G.id
        ? project.enpalReport.brickAndUkAttachment_G.id
        : null;
    project.enpalReport.brickAndUkAttachment_H =
      project.enpalReport.brickAndUkAttachment_H && project.enpalReport.brickAndUkAttachment_H.id
        ? project.enpalReport.brickAndUkAttachment_H.id
        : null;
    project.enpalReport.solarPanelAttachment_A =
      project.enpalReport.solarPanelAttachment_A && project.enpalReport.solarPanelAttachment_A.id
        ? project.enpalReport.solarPanelAttachment_A.id
        : null;
    project.enpalReport.solarPanelAttachment_B1 =
      project.enpalReport.solarPanelAttachment_B1 && project.enpalReport.solarPanelAttachment_B1.id
        ? project.enpalReport.solarPanelAttachment_B1.id
        : null;
    project.enpalReport.solarPanelAttachment_F =
      project.enpalReport.solarPanelAttachment_F && project.enpalReport.solarPanelAttachment_F.id
        ? project.enpalReport.solarPanelAttachment_F.id
        : null;
    project.enpalReport.solarPanelAttachment_G =
      project.enpalReport.solarPanelAttachment_G && project.enpalReport.solarPanelAttachment_G.id
        ? project.enpalReport.solarPanelAttachment_G.id
        : null;
    project.enpalReport.cableManagementAttachment_D =
      project.enpalReport.cableManagementAttachment_D &&
      project.enpalReport.cableManagementAttachment_D.id
        ? project.enpalReport.cableManagementAttachment_D.id
        : null;
    project.enpalReport.cableManagementAttachment_X =
      project.enpalReport.cableManagementAttachment_X &&
      project.enpalReport.cableManagementAttachment_X.id
        ? project.enpalReport.cableManagementAttachment_X.id
        : null;
    project.enpalReport.inverterAttachment_A =
      project.enpalReport.inverterAttachment_A && project.enpalReport.inverterAttachment_A.id
        ? project.enpalReport.inverterAttachment_A.id
        : null;
    project.enpalReport.inverterAttachment_B =
      project.enpalReport.inverterAttachment_B && project.enpalReport.inverterAttachment_B.id
        ? project.enpalReport.inverterAttachment_B.id
        : null;
    project.enpalReport.inverterAttachment_C =
      project.enpalReport.inverterAttachment_C && project.enpalReport.inverterAttachment_C.id
        ? project.enpalReport.inverterAttachment_C.id
        : null;
    project.enpalReport.inverterAttachment_D =
      project.enpalReport.inverterAttachment_D && project.enpalReport.inverterAttachment_D.id
        ? project.enpalReport.inverterAttachment_D.id
        : null;
    project.enpalReport.inverterAttachment_F =
      project.enpalReport.inverterAttachment_F && project.enpalReport.inverterAttachment_F.id
        ? project.enpalReport.inverterAttachment_F.id
        : null;
    project.enpalReport.inverterAttachment_G2 =
      project.enpalReport.inverterAttachment_G2 && project.enpalReport.inverterAttachment_G2.id
        ? project.enpalReport.inverterAttachment_G2.id
        : null;
    project.enpalReport.inverterAttachment_H =
      project.enpalReport.inverterAttachment_H && project.enpalReport.inverterAttachment_H.id
        ? project.enpalReport.inverterAttachment_H.id
        : null;
    project.enpalReport.storageAttachment_B1 =
      project.enpalReport.storageAttachment_B1 && project.enpalReport.storageAttachment_B1.id
        ? project.enpalReport.storageAttachment_B1.id
        : null;
    project.enpalReport.storageAttachment_B2 =
      project.enpalReport.storageAttachment_B2 && project.enpalReport.storageAttachment_B2.id
        ? project.enpalReport.storageAttachment_B2.id
        : null;
    project.enpalReport.storageAttachment_C1 =
      project.enpalReport.storageAttachment_C1 && project.enpalReport.storageAttachment_C1.id
        ? project.enpalReport.storageAttachment_C1.id
        : null;
    project.enpalReport.storageAttachment_C2 =
      project.enpalReport.storageAttachment_C2 && project.enpalReport.storageAttachment_C2.id
        ? project.enpalReport.storageAttachment_C2.id
        : null;
    project.enpalReport.storageAttachment_D =
      project.enpalReport.storageAttachment_D && project.enpalReport.storageAttachment_D.id
        ? project.enpalReport.storageAttachment_D.id
        : null;
    project.enpalReport.storageAttachment_F =
      project.enpalReport.storageAttachment_F && project.enpalReport.storageAttachment_F.id
        ? project.enpalReport.storageAttachment_F.id
        : null;
    project.enpalReport.storageAttachment_H =
      project.enpalReport.storageAttachment_H && project.enpalReport.storageAttachment_H.id
        ? project.enpalReport.storageAttachment_H.id
        : null;
    project.enpalReport.equipotentialBusBarAttachment_A =
      project.enpalReport.equipotentialBusBarAttachment_A &&
      project.enpalReport.equipotentialBusBarAttachment_A.id
        ? project.enpalReport.equipotentialBusBarAttachment_A.id
        : null;
    project.enpalReport.equipotentialBusBarAttachment_A2 =
      project.enpalReport.equipotentialBusBarAttachment_A2 &&
      project.enpalReport.equipotentialBusBarAttachment_A2.id
        ? project.enpalReport.equipotentialBusBarAttachment_A2.id
        : null;
    project.enpalReport.equipotentialBusBarAttachment_B1 =
      project.enpalReport.equipotentialBusBarAttachment_B1 &&
      project.enpalReport.equipotentialBusBarAttachment_B1.id
        ? project.enpalReport.equipotentialBusBarAttachment_B1.id
        : null;
    project.enpalReport.electricMeterAttachment_B =
      project.enpalReport.electricMeterAttachment_B &&
      project.enpalReport.electricMeterAttachment_B.id
        ? project.enpalReport.electricMeterAttachment_B.id
        : null;
    project.enpalReport.electricMeterAttachment_D =
      project.enpalReport.electricMeterAttachment_D &&
      project.enpalReport.electricMeterAttachment_D.id
        ? project.enpalReport.electricMeterAttachment_D.id
        : null;
    project.enpalReport.electricMeterAttachment_E =
      project.enpalReport.electricMeterAttachment_E &&
      project.enpalReport.electricMeterAttachment_E.id
        ? project.enpalReport.electricMeterAttachment_E.id
        : null;
    project.enpalReport.electricMeterAttachment_H =
      project.enpalReport.electricMeterAttachment_H &&
      project.enpalReport.electricMeterAttachment_H.id
        ? project.enpalReport.electricMeterAttachment_H.id
        : null;
    project.enpalReport.electricMeterAttachment_J =
      project.enpalReport.electricMeterAttachment_J &&
      project.enpalReport.electricMeterAttachment_J.id
        ? project.enpalReport.electricMeterAttachment_J.id
        : null;
    project.enpalReport.electricMeterAttachment_K =
      project.enpalReport.electricMeterAttachment_K &&
      project.enpalReport.electricMeterAttachment_K.id
        ? project.enpalReport.electricMeterAttachment_K.id
        : null;
    project.enpalReport.electricMeterChangeAttachment_1 =
      project.enpalReport.electricMeterChangeAttachment_1 &&
      project.enpalReport.electricMeterChangeAttachment_1.id
        ? project.enpalReport.electricMeterChangeAttachment_1.id
        : null;
    project.enpalReport.electricMeterChangeAttachment_3 =
      project.enpalReport.electricMeterChangeAttachment_3 &&
      project.enpalReport.electricMeterChangeAttachment_3.id
        ? project.enpalReport.electricMeterChangeAttachment_3.id
        : null;
    project.enpalReport.electricMeterChangeAttachment_4 =
      project.enpalReport.electricMeterChangeAttachment_4 &&
      project.enpalReport.electricMeterChangeAttachment_4.id
        ? project.enpalReport.electricMeterChangeAttachment_4.id
        : null;
    project.enpalReport.electricMeterChangeAttachment_5 =
      project.enpalReport.electricMeterChangeAttachment_5 &&
      project.enpalReport.electricMeterChangeAttachment_5.id
        ? project.enpalReport.electricMeterChangeAttachment_5.id
        : null;
    project.enpalReport.electricMeterChangeAttachment_6 =
      project.enpalReport.electricMeterChangeAttachment_6 &&
      project.enpalReport.electricMeterChangeAttachment_6.id
        ? project.enpalReport.electricMeterChangeAttachment_6.id
        : null;
    project.enpalReport.systemPowerOnAttachment_1 =
      project.enpalReport.systemPowerOnAttachment_1 &&
      project.enpalReport.systemPowerOnAttachment_1.id
        ? project.enpalReport.systemPowerOnAttachment_1.id
        : null;
    project.enpalReport.systemPowerOnAttachment_2 =
      project.enpalReport.systemPowerOnAttachment_2 &&
      project.enpalReport.systemPowerOnAttachment_2.id
        ? project.enpalReport.systemPowerOnAttachment_2.id
        : null;
    project.enpalReport.systemPowerOnAttachment_3 =
      project.enpalReport.systemPowerOnAttachment_3 &&
      project.enpalReport.systemPowerOnAttachment_3.id
        ? project.enpalReport.systemPowerOnAttachment_3.id
        : null;

    /** Only send ID's for damageAttachments */
    if (project.enpalReport.damageAttachments) {
      const damageAttachmentIds = [];
      project.enpalReport.damageAttachments.map((damageAttachment) => {
        damageAttachmentIds.push(damageAttachment.id);
      });
      project.enpalReport.damageAttachments = damageAttachmentIds;
    }
    if (project.enpalReport.solarPanelAttachments_C) {
      const solarPanelAttachments_C_Ids = [];
      project.enpalReport.solarPanelAttachments_C.map((attachment) => {
        solarPanelAttachments_C_Ids.push(attachment.id);
      });
      project.enpalReport.solarPanelAttachments_C = solarPanelAttachments_C_Ids;
    }
    if (project.enpalReport.solarPanelAttachments_E1) {
      const solarPanelAttachments_E1_Ids = [];
      project.enpalReport.solarPanelAttachments_E1.map((attachment) => {
        solarPanelAttachments_E1_Ids.push(attachment.id);
      });
      project.enpalReport.solarPanelAttachments_E1 = solarPanelAttachments_E1_Ids;
    }
    if (project.enpalReport.solarPanelAttachments_E2) {
      const solarPanelAttachments_E2_Ids = [];
      project.enpalReport.solarPanelAttachments_E2.map((attachment) => {
        solarPanelAttachments_E2_Ids.push(attachment.id);
      });
      project.enpalReport.solarPanelAttachments_E2 = solarPanelAttachments_E2_Ids;
    }
    if (project.enpalReport.inverterAttachmentsAdditional_F) {
      const inverterAttachmentsAdditional_F_Ids = [];
      project.enpalReport.inverterAttachmentsAdditional_F.map((attachment) => {
        inverterAttachmentsAdditional_F_Ids.push(attachment.id);
      });
      project.enpalReport.inverterAttachmentsAdditional_F = inverterAttachmentsAdditional_F_Ids;
    }
    if (project.enpalReport.storageAttachments_D1) {
      const storageAttachments_D1_Ids = [];
      project.enpalReport.storageAttachments_D1.map((attachment) => {
        storageAttachments_D1_Ids.push(attachment.id);
      });
      project.enpalReport.storageAttachments_D1 = storageAttachments_D1_Ids;
    }
    if (project.enpalReport.additionalImageAttachments) {
      const additionalImageAttachments_Ids = [];
      project.enpalReport.additionalImageAttachments.map((attachment) => {
        additionalImageAttachments_Ids.push(attachment.id);
      });
      project.enpalReport.additionalImageAttachments = additionalImageAttachments_Ids;
    }
    if (project.enpalReport.journalMeasurementsAttachment_A) {
      const journalMeasurementsAttachment_A_Ids = [];
      project.enpalReport.journalMeasurementsAttachment_A.map((attachment) => {
        journalMeasurementsAttachment_A_Ids.push(attachment.id);
      });
      project.enpalReport.journalMeasurementsAttachment_A = journalMeasurementsAttachment_A_Ids;
    }
    if (project.enpalReport.electricMeterChangeAttachments_2) {
      const electricMeterChangeAttachments_2_Ids = [];
      project.enpalReport.electricMeterChangeAttachments_2.map((attachment) => {
        electricMeterChangeAttachments_2_Ids.push(attachment.id);
      });
      project.enpalReport.electricMeterChangeAttachments_2 = electricMeterChangeAttachments_2_Ids;
    }
  }
  if (project.installation) {
    delete project.installation.__typename;
    delete project.installation.createdAt;
    delete project.installation.updatedAt;
    project.installation.electricMeterChangedAt = project.installation.electricMeterChangedAt
      ? project.installation.electricMeterChangedAt
      : null;
    project.installation.operationRegistrationAt = project.installation.operationRegistrationAt
      ? project.installation.operationRegistrationAt
      : null;
  }

  if (project.installation && project.installation.operationRegistrationInstallationAppointment) {
    delete project.installation.operationRegistrationInstallationAppointment.__typename;
    delete project.installation.operationRegistrationInstallationAppointment.id;
  }

  // Meter Cabinet Attachments
  if (project.meterCabinetAttachments) {
    const meterCabinetAttachmentIds = project.meterCabinetAttachments.map(
      (attachment) => attachment.id
    );
    project.meterCabinetAttachments = meterCabinetAttachmentIds;
  }

  // Roof Tile Attachments
  if (project.roofTileAttachments) {
    const roofTileAttachmentIds = project.roofTileAttachments.map((attachment) => attachment.id);
    project.roofTileAttachments = roofTileAttachmentIds;
  }

  // Roof Attachments
  if (project.roofAttachments) {
    const roofAttachmentIds = project.roofAttachments.map((attachment) => attachment.id);
    project.roofAttachments = roofAttachmentIds;
  }
  if (project.presentationAttachments) {
    const presentationAttachmentIds = project.presentationAttachments.map(
      (attachment) => attachment.id
    );
    project.presentationAttachments = presentationAttachmentIds;
  }
  if (project.blueprintAttachments) {
    const blueprintAttachmentIds = project.blueprintAttachments.map((attachment) => attachment.id);
    project.blueprintAttachments = blueprintAttachmentIds;
  }
  if (project.salesMiscAttachments) {
    const salesMiscAttachmentIds = project.salesMiscAttachments.map((attachment) => attachment.id);
    project.salesMiscAttachments = salesMiscAttachmentIds;
  }

  if (project.offerSalesCallOneAttachments) {
    const offerSalesCallOneAttachmentIds = project.offerSalesCallOneAttachments.map(
      (attachment) => attachment.id
    );
    project.offerSalesCallOneAttachments = offerSalesCallOneAttachmentIds;
  }
  if (project.offerSalesCallTwoAttachments) {
    const offerSalesCallTwoAttachmentIds = project.offerSalesCallTwoAttachments.map(
      (attachment) => attachment.id
    );
    project.offerSalesCallTwoAttachments = offerSalesCallTwoAttachmentIds;
  }
  if (project.installationLocationAttachments) {
    const installationLocationAttachmentIds = project.installationLocationAttachments.map(
      (attachment) => attachment.id
    );
    project.installationLocationAttachments = installationLocationAttachmentIds;
  }
  if (project.planningDdAttachments) {
    const planningDdAttachmentIds = project.planningDdAttachments.map(
      (attachment) => attachment.id
    );
    project.planningDdAttachments = planningDdAttachmentIds;
  }

  const itemIds = [];
  if (project.items) {
    project.items.map((item) => {
      itemIds.push({ item: item.item._id, count: item.count });
    });
  }

  /**
   * for text fields differentiate between "" and null: "" are sent to the server as empty value
   * deadlineAt date field: send NULL to server to remove a date
   */
  const payload = {
    consumablesOrderedState: project.consumablesOrderedState,
    consumablesInStockState: project.consumablesInStockState,
    itemPlanningFinishedState: project.itemPlanningFinishedState,
    installationAddressState: project.installationAddressState,
    items: itemIds,
    ...toProjectPartiesInput(project),
    // customer: project.customer,
    installation: project.installation,
    enpalReport: project.enpalReport,

    /** Reset Build States on save to prevent endless loading icon on errors */
    netRegistrationAttachmentsBuildState: project.netRegistrationAttachmentsBuildState,
    operationRegistrationAttachmentsBuildState: project.operationRegistrationAttachmentsBuildState,
    reportAttachmentsBuildState: project.reportAttachmentsBuildState,
  };
  // Persist sales on-hold state when present
  if (project.onHoldState) {
    Object.assign(payload, {
      onHoldState: {
        onHold: project.onHoldState.onHold || null,
        reason: project.onHoldState.reason || null,
        description: project.onHoldState.description || '',
        comment: project.onHoldState.comment || '',
      },
    });
  }
  if (project.installationAddress) {
    payload.installationAddress = {
      street: project.installationAddress.street,
      streetNumber: project.installationAddress.streetNumber,
      streetNumberSuffix: project.installationAddress.streetNumberSuffix,
      zip: project.installationAddress.zip,
      city: project.installationAddress.city,
      cityPart: project.installationAddress.cityPart,
    };
  }

  if (project.solarPlant) {
    Object.assign(payload, {
      solarPlant: {
        solarPanelCount: parseInt(project.solarPlant.solarPanelCount),
        guaranteeCode: project.solarPlant.guaranteeCode,
        qcellsKit: project.solarPlant.qcellsKit,
        extendedCode: project.solarPlant.extendedCode,
        systemNumber: project.solarPlant.systemNumber,
        yearOfConstruction: project.solarPlant.yearOfConstruction,
        emergencyPowerState: project.solarPlant.emergencyPowerState,
        cascadeConnectionState: project.solarPlant.cascadeConnectionState,
        networkSupplyManagementState: project.solarPlant.networkSupplyManagementState,
        networkSupplyManagementPermanentRestrictionState:
          project.solarPlant.networkSupplyManagementPermanentRestrictionState,
        networkSupplyManagementXOverState: project.solarPlant.networkSupplyManagementXOverState,
        netOutputState: project.solarPlant.netOutputState,
        netOutputStates: project.solarPlant.netOutputStates,
        storageCouplingState: project.solarPlant.storageCouplingState,
        electricMeterMountingState: project.solarPlant.electricMeterMountingState,
        yearlyPower: project.solarPlant.yearlyPower,
        houseFuseState: project.solarPlant.houseFuseState,
        houseFuseAmpere: project.solarPlant.houseFuseAmpere,
        wireCrossSectionMainBus: project.solarPlant.wireCrossSectionMainBus,
        electricMeterCombinationState: project.solarPlant.electricMeterCombinationState,
        electricMeterDeregistrationState: project.solarPlant.electricMeterDeregistrationState,
        solarPanel: project.solarPlant.solarPanel ? project.solarPlant.solarPanel.id : null,
        solarPanels: toItemCountInputs(project.solarPlant.solarPanels),
        inverter1: project.solarPlant.inverter1 ? project.solarPlant.inverter1.id : null,
        inverter2: project.solarPlant.inverter2 ? project.solarPlant.inverter2.id : null,
        inverters: toItemCountInputs(project.solarPlant.inverters),
        batteries: toItemCountInputs(project.solarPlant.batteries),
        wallboxes: toItemCountInputs(project.solarPlant.wallboxes),
        inverter1Item: project.solarPlant.inverter1Item
          ? project.solarPlant.inverter1Item._id
          : null,
        inverter2Item: project.solarPlant.inverter2Item
          ? project.solarPlant.inverter2Item._id
          : null,
        energyFlowDirectionSensorItem: project.solarPlant.energyFlowDirectionSensorItem
          ? project.solarPlant.energyFlowDirectionSensorItem._id
          : null,
        solarPanelItem: project.solarPlant.solarPanelItem
          ? project.solarPlant.solarPanelItem._id
          : null,
        battery: project.solarPlant.battery ? project.solarPlant.battery.id : null,
        energyFlowDirectionSensor: project.solarPlant.energyFlowDirectionSensor
          ? project.solarPlant.energyFlowDirectionSensor.id
          : null,
        wallbox: project.solarPlant.wallbox ? project.solarPlant.wallbox.id : null,
        storageSystem: project.solarPlant.storageSystem
          ? project.solarPlant.storageSystem.id
          : null,
        solarPlantType: project.solarPlant.solarPlantType,
        islandOperationIntended: project.solarPlant.islandOperationIntended,
        motorStartup: project.solarPlant.motorStartup,
        networkSupplyType: project.solarPlant.networkSupplyType,
        measuringConcept: project.solarPlant.measuringConcept,
      },
    });
  }

  if (project.measurement && project.measurement.completed) {
    project.measurement.completed.map((field) => {
      delete field.__typename;
    });
  }
  if (project.enpalReport && project.enpalReport.completed) {
    project.enpalReport.completed.map((field) => {
      delete field.__typename;
    });
  }

  if (project.measurement) {
    Object.assign(payload, {
      measurement: {
        measurementApprovementNote: project.measurement.measurementApprovementNote,
        completed: project.measurement.completed,
        roofType: project.measurement.roofType,
        pitchedRoof: {
          brick:
            project.measurement.pitchedRoof.brick && project.measurement.pitchedRoof.brick.id
              ? project.measurement.pitchedRoof.brick.id
              : null,
          conditionState: project.measurement.pitchedRoof.conditionState,
          bricksPlasteredState: project.measurement.pitchedRoof.bricksPlasteredState,
          cardboardPoppetState: project.measurement.pitchedRoof.cardboardPoppetState,
          notes: project.measurement.pitchedRoof.notes,
          rafterVisibleState: project.measurement.pitchedRoof.rafterVisibleState,
          rafterWidth: project.measurement.pitchedRoof.rafterWidth,
          rafterGap: project.measurement.pitchedRoof.rafterGap,
          rafterNotes: project.measurement.pitchedRoof.rafterNotes,
          eavesHeight1: project.measurement.pitchedRoof.eavesHeight1,
          eavesHeight2: project.measurement.pitchedRoof.eavesHeight2,
          rafterInsulationState: project.measurement.pitchedRoof.rafterInsulationState,
          rafterInsulationNotes: project.measurement.pitchedRoof.rafterInsulationNotes,
          lightningProtectionState: project.measurement.pitchedRoof.lightningProtectionState,
          integrateLightningProtectionState:
            project.measurement.pitchedRoof.integrateLightningProtectionState,
          bricksRemoveableState: project.measurement.pitchedRoof.bricksRemoveableState,
          bricksRemoveableNotes: project.measurement.pitchedRoof.bricksRemoveableNotes,
          roofPitchInDegrees: project.measurement.pitchedRoof.roofPitchInDegrees,
          outerRoofNotes: project.measurement.pitchedRoof.outerRoofNotes,
          bricksConditionState: project.measurement.pitchedRoof.bricksConditionState,
          brickManufacturer: project.measurement.pitchedRoof.brickManufacturer,
          brickType: project.measurement.pitchedRoof.brickType,
          tinBricksRequiredState: project.measurement.pitchedRoof.tinBricksRequiredState,
          bricksNotes: project.measurement.pitchedRoof.bricksNotes,
          roofSidesAssignedState: project.measurement.pitchedRoof.roofSidesAssignedState,
          roofSidesMeasurements: project.measurement.pitchedRoof.roofSidesMeasurements,
          roofSidesShadowsState: project.measurement.pitchedRoof.roofSidesShadowsState,
          roofSidesNotes: project.measurement.pitchedRoof.roofSidesNotes,
          scaffoldingPossibleState: project.measurement.pitchedRoof.scaffoldingPossibleState,
          scaffoldingPossibleNotes: project.measurement.pitchedRoof.scaffoldingPossibleNotes,
          rainPipeMaterialType: project.measurement.pitchedRoof.rainPipeMaterialType,
          rssUsableState: project.measurement.pitchedRoof.rssUsableState,
          rssUsableNotes: project.measurement.pitchedRoof.rssUsableNotes,
          roadBlockRequiredState: project.measurement.pitchedRoof.roadBlockRequiredState,
          overheadLineState: project.measurement.pitchedRoof.overheadLineState,
          roofAccessNotes: project.measurement.pitchedRoof.roofAccessNotes,
        },
        ac: {
          hakLocationState: project.measurement.ac.hakLocationState,
          hakAndZsSameRoomState: project.measurement.ac.hakAndZsSameRoomState,
          hakLockedState: project.measurement.ac.hakLockedState,
          hakToZsDistance: project.measurement.ac.hakToZsDistance,
          fireServiceSwitchPresentState: project.measurement.ac.fireServiceSwitchPresentState,
          hakNotes: project.measurement.ac.hakNotes,
          zsLocationState: project.measurement.ac.zsLocationState,
          newZsRequiredState: project.measurement.ac.newZsRequiredState,
          zsSubDistributionRequiredState: project.measurement.ac.zsSubDistributionRequiredState,
          zsSubDistributionRequiredNotes: project.measurement.ac.zsSubDistributionRequiredNotes,
          netType: project.measurement.ac.netType,
          zsNotes: project.measurement.ac.zsNotes,
          electricMeterCount: project.measurement.ac.electricMeterCount,
          electricMeterList: project.measurement.ac.electricMeterList,
          intermediateMeterRequesteddState: project.measurement.ac.intermediateMeterRequesteddState,
          meterCombinationOrCascadeRequestedState:
            project.measurement.ac.meterCombinationOrCascadeRequestedState,
          electricMeterOptionalNotes: project.measurement.ac.electricMeterOptionalNotes,
          acInstallationLocationState: project.measurement.ac.acInstallationLocationState,
          mateboxToZsDistance: project.measurement.ac.mateboxToZsDistance,
          breakthroughCountState: project.measurement.ac.breakthroughCountState,
          cabelPathDescription: project.measurement.ac.cabelPathDescription,
          locationAndCablePathNotes: project.measurement.ac.locationAndCablePathNotes,
          equipotentialBusBarPresentState: project.measurement.ac.equipotentialBusBarPresentState,
          earthingPresentState: project.measurement.ac.earthingPresentState,
          earthinghNotes: project.measurement.ac.earthinghNotes,
          internetConnectionPresentState: project.measurement.ac.internetConnectionPresentState,
          lanConnectionPresentState: project.measurement.ac.lanConnectionPresentState,
          internetConnectionNotes: project.measurement.ac.internetConnectionNotes,
          wallboxRequiredState: project.measurement.ac.wallboxRequiredState,
          heavyCurrentPresentState: project.measurement.ac.heavyCurrentPresentState,
          wallboxWirelessInstallationPossibleState:
            project.measurement.ac.wallboxWirelessInstallationPossibleState,
          wallboxWirelessInstallationPossibleNotes:
            project.measurement.ac.wallboxWirelessInstallationPossibleNotes,
          wallboxCablePathNotes: project.measurement.ac.wallboxCablePathNotes,
          wallboxNotes: project.measurement.ac.wallboxNotes,
        },
        customer: {
          customerQuestions: project.measurement.customer.customerQuestions,
          customerWorking: project.measurement.customer.customerWorking,
          customerSignature: project.measurement.customer.customerSignature,
        },
        flatRoof: {
          buildingHeight: project.measurement.flatRoof.buildingHeight,
          roofDimension: project.measurement.flatRoof.roofDimension,
          flatRoofPitchInDegrees: project.measurement.flatRoof.flatRoofPitchInDegrees,
          flatRoofingMaterialType: project.measurement.flatRoof.flatRoofingMaterialType,
          flatRoofingMaterialNotes: project.measurement.flatRoof.flatRoofingMaterialNotes,
          atticPresentState: project.measurement.flatRoof.atticPresentState,
          atticPresentNotes: project.measurement.flatRoof.atticPresentNotes,
          flatRoofNotes: project.measurement.flatRoof.flatRoofNotes,
        },
      },
    });
  }

  /** Serviceprotokoll */
  if (project.serviceReport && project.serviceReport.completed) {
    project.serviceReport.completed.map((field) => {
      delete field.__typename;
    });
  }
  if (project.serviceReport) {
    Object.assign(payload, {
      serviceReport: {
        completed: project.serviceReport.completed,
        brickAndUkAssemblyType: project.serviceReport.brickAndUkAssemblyType,
        brickAndUkA2State: project.serviceReport.brickAndUkA2State,
        brickAndUkBState: project.serviceReport.brickAndUkBState,
        railsEarthed: project.serviceReport.railsEarthed,
        brickAndUkOptionalNotes: project.serviceReport.brickAndUkOptionalNotes,
        cableManagementC2State: project.serviceReport.cableManagementC2State,
        cableManagementOptionalNotes: project.serviceReport.cableManagementOptionalNotes,
        solarPaneldOptionalNotes: project.serviceReport.brickAndUkOptionalNotes,
        hakLocationState: project.serviceReport.hakLocationState,
        hakLockedState: project.serviceReport.hakLockedState,
        hakToZsDistance: project.serviceReport.hakToZsDistance,
        hakAndZsSameRoomState: project.serviceReport.hakAndZsSameRoomState,
        zsLocationState: project.serviceReport.zsLocationState,
        additionalSubDistributionState: project.serviceReport.additionalSubDistributionState,
        electricMeterCount: project.serviceReport.electricMeterCount,
        electricMeterList: project.serviceReport.electricMeterList,
        intermediateMeterRequestedState: project.serviceReport.intermediateMeterRequestedState,
        meterCombinationRequestedState: project.serviceReport.meterCombinationRequestedState,
        disassembledMetersOnSiteState: project.serviceReport.disassembledMetersOnSiteState,
        meterCascadeRequestedState: project.serviceReport.meterCascadeRequestedState,
        installationLocationWithInverterAndMateboxState:
          project.serviceReport.installationLocationWithInverterAndMateboxState,
        cablePathDistance: project.serviceReport.cablePathDistance,
        cableSelection: project.serviceReport.cableSelection,
        equipotentialBusBarPresentState: project.serviceReport.equipotentialBusBarPresentState,
        secondaryEquipotentialBusBarPresentState:
          project.serviceReport.secondaryEquipotentialBusBarPresentState,
        potentialGroundRodPresentState: project.serviceReport.potentialGroundRodPresentState,
        earthingSupply16mmState: project.serviceReport.earthingSupply16mmState,
        internetConnectionTypeState: project.serviceReport.internetConnectionTypeState,
        lanConnectionPresentState: project.serviceReport.lanConnectionPresentState,
        qcellsWallboxContractState: project.serviceReport.qcellsWallboxContractState,
        briefingAndDocumentationState: project.serviceReport.briefingAndDocumentationState,
        serviceOptionalNotes: project.serviceReport.serviceOptionalNotes,
        stringPlanAvailableState: project.serviceReport.stringPlanAvailableState,
      },
    });
  }

  /** Salesprotokoll */
  if (project.salesReport && project.salesReport.completed) {
    project.salesReport.completed.map((field) => {
      delete field.__typename;
    });
  }
  if (project.salesReport) {
    Object.assign(payload, {
      salesReport: {
        completed: project.salesReport.completed,
        salesHakLocationState: project.salesReport.salesHakLocationState,
        salesZsLocationState: project.salesReport.salesZsLocationState,
      },
    });
  }

  /** Qualityprotokoll */
  if (project.qualityReport && project.qualityReport.completed) {
    project.qualityReport.completed.map((field) => {
      delete field.__typename;
    });
  }
  if (project.qualityReport) {
    Object.assign(payload, {
      qualityReport: {
        completed: project.qualityReport.completed,
        properSleeveUsedAtRoofPenetrationState:
          project.qualityReport.properSleeveUsedAtRoofPenetrationState,
        isRoofAccessibleFromInsideState: project.qualityReport.isRoofAccessibleFromInsideState,
        isRoofAccessibleFromInsideNote: project.qualityReport.isRoofAccessibleFromInsideNote,
        properSleeveUsedAtRoofPenetrationNote:
          project.qualityReport.properSleeveUsedAtRoofPenetrationNote,
        underlaySealedWellAtCableEntryState:
          project.qualityReport.underlaySealedWellAtCableEntryState,
        underlayDamagedOrWaterStainsState: project.qualityReport.underlayDamagedOrWaterStainsState,
        labelingOfStringLinesState: project.qualityReport.labelingOfStringLinesState,
        pvStringCableCorrectCrossSectionState:
          project.qualityReport.pvStringCableCorrectCrossSectionState,
        peCableCorrectlySizedState: project.qualityReport.peCableCorrectlySizedState,
        cablesProperlySecuredState: project.qualityReport.cablesProperlySecuredState,
        crimpingProfessionallyDoneState: project.qualityReport.crimpingProfessionallyDoneState,
        correctSelectionOfPvConnectorsOnStringCableState:
          project.qualityReport.correctSelectionOfPvConnectorsOnStringCableState,
        allPlugConnectionsProfessionallyMadeState:
          project.qualityReport.allPlugConnectionsProfessionallyMadeState,
        cablesNeatlyLaidInsideBuildingState:
          project.qualityReport.cablesNeatlyLaidInsideBuildingState,
        spdBoxPresentIfCableInBuildingLongerThan10mInstalledBeforeInverterState:
          project.qualityReport
            .spdBoxPresentIfCableInBuildingLongerThan10mInstalledBeforeInverterState,
        surgeProtectionBoxProperlyWiredInstallSpdAtRoofEntryState:
          project.qualityReport.surgeProtectionBoxProperlyWiredInstallSpdAtRoofEntryState,
        acceptanceProtocolCustomerSignatureAttachments:
          project.qualityReport.acceptanceProtocolCustomerSignatureAttachments,
        underlaySealedWellAtCableEntryNote:
          project.qualityReport.underlaySealedWellAtCableEntryNote,
        underlayDamagedOrWaterStainsNote: project.qualityReport.underlayDamagedOrWaterStainsNote,
        labelingOfStringLinesNote: project.qualityReport.labelingOfStringLinesNote,
        pvStringCableCorrectCrossSectionNote:
          project.qualityReport.pvStringCableCorrectCrossSectionNote,
        peCableCorrectlySizedNote: project.qualityReport.peCableCorrectlySizedNote,
        cablesProperlySecuredNote: project.qualityReport.cablesProperlySecuredNote,
        crimpingProfessionallyDoneNote: project.qualityReport.crimpingProfessionallyDoneNote,
        correctSelectionOfPvConnectorsOnStringCableNote:
          project.qualityReport.correctSelectionOfPvConnectorsOnStringCableNote,
        allPlugConnectionsProfessionallyMadeNote:
          project.qualityReport.allPlugConnectionsProfessionallyMadeNote,
        cablesNeatlyLaidInsideBuildingNote:
          project.qualityReport.cablesNeatlyLaidInsideBuildingNote,
        spdBoxPresentIfCableInBuildingLongerThan10mInstalledBeforeInverterNote:
          project.qualityReport
            .spdBoxPresentIfCableInBuildingLongerThan10mInstalledBeforeInverterNote,
        surgeProtectionBoxProperlyWiredInstallSpdAtRoofEntryNote:
          project.qualityReport.surgeProtectionBoxProperlyWiredInstallSpdAtRoofEntryNote,
        documentationProjectReportAvailableState:
          project.qualityReport.documentationProjectReportAvailableState,
        documentationProjectReportAvailableNote:
          project.qualityReport.documentationProjectReportAvailableNote,
        specialCustomerRequestsDocumentedState:
          project.qualityReport.specialCustomerRequestsDocumentedState,
        specialCustomerRequestsDocumentedNote:
          project.qualityReport.specialCustomerRequestsDocumentedNote,
        tileTypeAndWidthDocumentedState: project.qualityReport.tileTypeAndWidthDocumentedState,
        tileTypeAndWidthDocumentedNote: project.qualityReport.tileTypeAndWidthDocumentedNote,
        replacementTileInstalledState: project.qualityReport.replacementTileInstalledState,
        replacementTileInstalledNote: project.qualityReport.replacementTileInstalledNote,
        lightningProtectionPresentState: project.qualityReport.lightningProtectionPresentState,
        lightningProtectionPresentNote: project.qualityReport.lightningProtectionPresentNote,
        substructureTensionFreeCheckedState:
          project.qualityReport.substructureTensionFreeCheckedState,
        substructureTensionFreeCheckedNote:
          project.qualityReport.substructureTensionFreeCheckedNote,
        substructureInstalledAsPerReportState:
          project.qualityReport.substructureInstalledAsPerReportState,
        substructureInstalledAsPerReportNote:
          project.qualityReport.substructureInstalledAsPerReportNote,
        eachRailSecuredWithMinTwoHooksState:
          project.qualityReport.eachRailSecuredWithMinTwoHooksState,
        eachRailSecuredWithMinTwoHooksNote:
          project.qualityReport.eachRailSecuredWithMinTwoHooksNote,
        distanceBetweenHooksMoreThan140cmState:
          project.qualityReport.distanceBetweenHooksMoreThan140cmState,
        distanceBetweenHooksMoreThan140cmNote:
          project.qualityReport.distanceBetweenHooksMoreThan140cmNote,
        correctRoofHooksUsedState: project.qualityReport.correctRoofHooksUsedState,
        correctRoofHooksUsedNote: project.qualityReport.correctRoofHooksUsedNote,
        distanceRoofHookToTile35mmState: project.qualityReport.distanceRoofHookToTile35mmState,
        distanceRoofHookToTile35mmNote: project.qualityReport.distanceRoofHookToTile35mmNote,
        minScrewDistanceToRafterEdge24mmState:
          project.qualityReport.minScrewDistanceToRafterEdge24mmState,
        minScrewDistanceToRafterEdge24mmNote:
          project.qualityReport.minScrewDistanceToRafterEdge24mmNote,
        roofHookProperlyFixedToRafterState:
          project.qualityReport.roofHookProperlyFixedToRafterState,
        roofHookProperlyFixedToRafterNote: project.qualityReport.roofHookProperlyFixedToRafterNote,
        overhangObservedState: project.qualityReport.overhangObservedState,
        overhangObservedNote: project.qualityReport.overhangObservedNote,
        moduleFieldProperlyGroundedState: project.qualityReport.moduleFieldProperlyGroundedState,
        moduleFieldProperlyGroundedNote: project.qualityReport.moduleFieldProperlyGroundedNote,
        approvedAuxiliaryRafterUsedState: project.qualityReport.approvedAuxiliaryRafterUsedState,
        approvedAuxiliaryRafterUsedNote: project.qualityReport.approvedAuxiliaryRafterUsedNote,
        rafterWidthMeasuredAndComparedToDocsState:
          project.qualityReport.rafterWidthMeasuredAndComparedToDocsState,
        rafterWidthMeasuredAndComparedToDocsNote:
          project.qualityReport.rafterWidthMeasuredAndComparedToDocsNote,
        noOpenOrDamagedTilesState: project.qualityReport.noOpenOrDamagedTilesState,
        noOpenOrDamagedTilesNote: project.qualityReport.noOpenOrDamagedTilesNote,
        noShiftedOrIncorrectlyLaidTilesState:
          project.qualityReport.noShiftedOrIncorrectlyLaidTilesState,
        noShiftedOrIncorrectlyLaidTilesNote:
          project.qualityReport.noShiftedOrIncorrectlyLaidTilesNote,
        tilesProperlyCleanedState: project.qualityReport.tilesProperlyCleanedState,
        tilesProperlyCleanedNote: project.qualityReport.tilesProperlyCleanedNote,
        correctPanHeadScrewsUsedState: project.qualityReport.correctPanHeadScrewsUsedState,
        correctPanHeadScrewsUsedNote: project.qualityReport.correctPanHeadScrewsUsedNote,
        minRailOverhangAfterEndClamp20mmState:
          project.qualityReport.minRailOverhangAfterEndClamp20mmState,
        minRailOverhangAfterEndClamp20mmNote:
          project.qualityReport.minRailOverhangAfterEndClamp20mmNote,
        distancesToRidgeEavesNotLessThan20cmState:
          project.qualityReport.distancesToRidgeEavesNotLessThan20cmState,
        distancesToRidgeEavesNotLessThan20cmNote:
          project.qualityReport.distancesToRidgeEavesNotLessThan20cmNote,
        moduleClampAreasObservedState: project.qualityReport.moduleClampAreasObservedState,
        moduleClampAreasObservedNote: project.qualityReport.moduleClampAreasObservedNote,
        modulesNoDamagesDirtShadingState: project.qualityReport.modulesNoDamagesDirtShadingState,
        modulesNoDamagesDirtShadingNote: project.qualityReport.modulesNoDamagesDirtShadingNote,
        moduleClampsProperlyTightenedState:
          project.qualityReport.moduleClampsProperlyTightenedState,
        moduleClampsProperlyTightenedNote: project.qualityReport.moduleClampsProperlyTightenedNote,
        thermalSeparationAfter12mModuleFieldState:
          project.qualityReport.thermalSeparationAfter12mModuleFieldState,
        thermalSeparationAfter12mModuleFieldNote:
          project.qualityReport.thermalSeparationAfter12mModuleFieldNote,
        breakthroughProperlySealedState: project.qualityReport.breakthroughProperlySealedState,
        breakthroughProperlySealedNote: project.qualityReport.breakthroughProperlySealedNote,
        distanceLightningProtectionToConductivePartsLessThan05mState:
          project.qualityReport.distanceLightningProtectionToConductivePartsLessThan05mState,
        distanceLightningProtectionToConductivePartsLessThan05mNote:
          project.qualityReport.distanceLightningProtectionToConductivePartsLessThan05mNote,
        gutterUndamagedState: project.qualityReport.gutterUndamagedState,
        gutterUndamagedNote: project.qualityReport.gutterUndamagedNote,
        notBuiltOverRoofOverhangState: project.qualityReport.notBuiltOverRoofOverhangState,
        notBuiltOverRoofOverhangNote: project.qualityReport.notBuiltOverRoofOverhangNote,
        cableProperlyLaidInChimneyState: project.qualityReport.cableProperlyLaidInChimneyState,
        cableProperlyLaidInChimneyNote: project.qualityReport.cableProperlyLaidInChimneyNote,
        distanceToDisturbingSurfaces10cmObservedState:
          project.qualityReport.distanceToDisturbingSurfaces10cmObservedState,
        distanceToDisturbingSurfaces10cmObservedNote:
          project.qualityReport.distanceToDisturbingSurfaces10cmObservedNote,
        cableEntryWithReplacementPlateAndFlexedTileState:
          project.qualityReport.cableEntryWithReplacementPlateAndFlexedTileState,
        cableEntryWithReplacementPlateAndFlexedTileNote:
          project.qualityReport.cableEntryWithReplacementPlateAndFlexedTileNote,
        cablesDoNotRestOnTilesState: project.qualityReport.cablesDoNotRestOnTilesState,
        cablesDoNotRestOnTilesNote: project.qualityReport.cablesDoNotRestOnTilesNote,
        cablesLaidProtectedUnderTilesState:
          project.qualityReport.cablesLaidProtectedUnderTilesState,
        cablesLaidProtectedUnderTilesNote: project.qualityReport.cablesLaidProtectedUnderTilesNote,
        cableEntryOnRoofProperlyDoneState: project.qualityReport.cableEntryOnRoofProperlyDoneState,
        cableEntryOnRoofProperlyDoneNote: project.qualityReport.cableEntryOnRoofProperlyDoneNote,
        cableLaidThroughChimneyState: project.qualityReport.cableLaidThroughChimneyState,
        cableLaidThroughChimneyNote: project.qualityReport.cableLaidThroughChimneyNote,
        cableLaidInProtectiveSleeveAgainstUvRadiationState:
          project.qualityReport.cableLaidInProtectiveSleeveAgainstUvRadiationState,
        cableLaidInProtectiveSleeveAgainstUvRadiationNote:
          project.qualityReport.cableLaidInProtectiveSleeveAgainstUvRadiationNote,
        peCableProperlySecuredState: project.qualityReport.peCableProperlySecuredState,
        peCableProperlySecuredNote: project.qualityReport.peCableProperlySecuredNote,
        documentationProjectReportPresentState:
          project.qualityReport.documentationProjectReportPresentState,
        documentationProjectReportPresentNote:
          project.qualityReport.documentationProjectReportPresentNote,
        customerSpecialRequestsConsideredState:
          project.qualityReport.customerSpecialRequestsConsideredState,
        customerSpecialRequestsConsideredNote:
          project.qualityReport.customerSpecialRequestsConsideredNote,
        flatRooflightningProtectionPresentState:
          project.qualityReport.flatRooflightningProtectionPresentState,
        flatRooflightningProtectionPresentNote:
          project.qualityReport.flatRooflightningProtectionPresentNote,
        properGroundingFlatRoofSystemState:
          project.qualityReport.properGroundingFlatRoofSystemState,
        properGroundingFlatRoofSystemNote: project.qualityReport.properGroundingFlatRoofSystemNote,
        ballastingAccordingToProjectReportState:
          project.qualityReport.ballastingAccordingToProjectReportState,
        ballastingAccordingToProjectReportNote:
          project.qualityReport.ballastingAccordingToProjectReportNote,
        minimumModuleBearingAreaMaintainedState:
          project.qualityReport.minimumModuleBearingAreaMaintainedState,
        minimumModuleBearingAreaMaintainedNote:
          project.qualityReport.minimumModuleBearingAreaMaintainedNote,
        qFlatProperlyConnectedState: project.qualityReport.qFlatProperlyConnectedState,
        qFlatProperlyConnectedNote: project.qualityReport.qFlatProperlyConnectedNote,
        distanceToDisturbingSurfacesMaintainedState:
          project.qualityReport.distanceToDisturbingSurfacesMaintainedState,
        distanceToDisturbingSurfacesMaintainedNote:
          project.qualityReport.distanceToDisturbingSurfacesMaintainedNote,
        flatRoofmoduleClampsProperlyTightenedState:
          project.qualityReport.flatRoofmoduleClampsProperlyTightenedState,
        flatRoofmoduleClampsProperlyTightenedNote:
          project.qualityReport.flatRoofmoduleClampsProperlyTightenedNote,
        modulesNoDamageDirtShadingState: project.qualityReport.modulesNoDamageDirtShadingState,
        modulesNoDamageDirtShadingNote: project.qualityReport.modulesNoDamageDirtShadingNote,
        typeOfCableProtectionOutdoorsState:
          project.qualityReport.typeOfCableProtectionOutdoorsState,
        typeOfCableProtectionOutdoorsNote: project.qualityReport.typeOfCableProtectionOutdoorsNote,
        correctSelectionOfPvConnectorsState:
          project.qualityReport.correctSelectionOfPvConnectorsState,
        correctSelectionOfPvConnectorsNote:
          project.qualityReport.correctSelectionOfPvConnectorsNote,
        cablesDoNotRestOnRoofState: project.qualityReport.cablesDoNotRestOnRoofState,
        cablesDoNotRestOnRoofNote: project.qualityReport.cablesDoNotRestOnRoofNote,
        cableEntryDoneProperlyState: project.qualityReport.cableEntryDoneProperlyState,
        cableEntryDoneProperlyNote: project.qualityReport.cableEntryDoneProperlyNote,
        cablesProtectedFromUvState: project.qualityReport.cablesProtectedFromUvState,
        cablesProtectedFromUvNote: project.qualityReport.cablesProtectedFromUvNote,
        pvStringCableProperlySizedState: project.qualityReport.pvStringCableProperlySizedState,
        pvStringCableProperlySizedNote: project.qualityReport.pvStringCableProperlySizedNote,
        stringCableLabelingState: project.qualityReport.stringCableLabelingState,
        stringCableLabelingNote: project.qualityReport.stringCableLabelingNote,
        peCableProperlySizedState: project.qualityReport.peCableProperlySizedState,
        peCableProperlySizedNote: project.qualityReport.peCableProperlySizedNote,
        flatRoofcablesProperlySecuredState:
          project.qualityReport.flatRoofcablesProperlySecuredState,
        flatRoofcablesProperlySecuredNote: project.qualityReport.flatRoofcablesProperlySecuredNote,
        crimpingProperlyDoneState: project.qualityReport.crimpingProperlyDoneState,
        crimpingProperlyDoneNote: project.qualityReport.crimpingProperlyDoneNote,
        pvSystemLabeledInOrAtDistributionBoardState:
          project.qualityReport.pvSystemLabeledInOrAtDistributionBoardState,
        pvSystemLabeledInOrAtDistributionBoardNote:
          project.qualityReport.pvSystemLabeledInOrAtDistributionBoardNote,
        testReportAvailableWithAllMeasurementsState:
          project.qualityReport.testReportAvailableWithAllMeasurementsState,
        testReportAvailableWithAllMeasurementsNote:
          project.qualityReport.testReportAvailableWithAllMeasurementsNote,
        rcdTypeB300maAvailableInMeterAreaState:
          project.qualityReport.rcdTypeB300maAvailableInMeterAreaState,
        rcdTypeB300maAvailableInMeterAreaNote:
          project.qualityReport.rcdTypeB300maAvailableInMeterAreaNote,
        stringPlanFiledInMeterCabinetState:
          project.qualityReport.stringPlanFiledInMeterCabinetState,
        stringPlanFiledInMeterCabinetNote: project.qualityReport.stringPlanFiledInMeterCabinetNote,
        safetyDistancesMaintainedState: project.qualityReport.safetyDistancesMaintainedState,
        safetyDistancesMaintainedNote: project.qualityReport.safetyDistancesMaintainedNote,
        correctLocationForSystemState: project.qualityReport.correctLocationForSystemState,
        correctLocationForSystemNote: project.qualityReport.correctLocationForSystemNote,
        correctLocationForWallboxState: project.qualityReport.correctLocationForWallboxState,
        correctLocationForWallboxNote: project.qualityReport.correctLocationForWallboxNote,
        customerRequestsDocumentedState: project.qualityReport.customerRequestsDocumentedState,
        customerRequestsDocumentedNote: project.qualityReport.customerRequestsDocumentedNote,
        slsInstalledState: project.qualityReport.slsInstalledState,
        slsInstalledNote: project.qualityReport.slsInstalledNote,
        componentsDamagedState: project.qualityReport.componentsDamagedState,
        componentsDamagedNote: project.qualityReport.componentsDamagedNote,
        crimpingDoneProperlyState: project.qualityReport.crimpingDoneProperlyState,
        crimpingDoneProperlyNote: project.qualityReport.crimpingDoneProperlyNote,
        ussWiredProperlyInCabinetState: project.qualityReport.ussWiredProperlyInCabinetState,
        ussWiredProperlyInCabinetNote: project.qualityReport.ussWiredProperlyInCabinetNote,
        ussBoxWiredProperlyState: project.qualityReport.ussBoxWiredProperlyState,
        ussBoxWiredProperlyNote: project.qualityReport.ussBoxWiredProperlyNote,
        bendingRadiiMaintainedState: project.qualityReport.bendingRadiiMaintainedState,
        bendingRadiiMaintainedNote: project.qualityReport.bendingRadiiMaintainedNote,
        correctWiringOfMateboxState: project.qualityReport.correctWiringOfMateboxState,
        correctWiringOfMateboxNote: project.qualityReport.correctWiringOfMateboxNote,
        correctWiringOfBatteryState: project.qualityReport.correctWiringOfBatteryState,
        correctWiringOfBatteryNote: project.qualityReport.correctWiringOfBatteryNote,
        allComponentsProperlyGroundedState:
          project.qualityReport.allComponentsProperlyGroundedState,
        allComponentsProperlyGroundedNote: project.qualityReport.allComponentsProperlyGroundedNote,
        correctColorCodingAndLabelingState:
          project.qualityReport.correctColorCodingAndLabelingState,
        correctColorCodingAndLabelingNote: project.qualityReport.correctColorCodingAndLabelingNote,
        correctWiringOfPvConnectorsState: project.qualityReport.correctWiringOfPvConnectorsState,
        correctWiringOfPvConnectorsNote: project.qualityReport.correctWiringOfPvConnectorsNote,
        cableDuctProperlySizedAndCutState: project.qualityReport.cableDuctProperlySizedAndCutState,
        cableDuctProperlySizedAndCutNote: project.qualityReport.cableDuctProperlySizedAndCutNote,
        nhHakSelectiveToSlsState: project.qualityReport.nhHakSelectiveToSlsState,
        nhHakSelectiveToSlsNote: project.qualityReport.nhHakSelectiveToSlsNote,
        checkNetworkTypeState: project.qualityReport.checkNetworkTypeState,
        checkNetworkTypeNote: project.qualityReport.checkNetworkTypeNote,
        earthRodConnectedToHpasState: project.qualityReport.earthRodConnectedToHpasState,
        earthRodConnectedToHpasNote: project.qualityReport.earthRodConnectedToHpasNote,
        connectionBetweenHpasAndPasProperlyMadeState:
          project.qualityReport.connectionBetweenHpasAndPasProperlyMadeState,
        connectionBetweenHpasAndPasProperlyMadeNote:
          project.qualityReport.connectionBetweenHpasAndPasProperlyMadeNote,
        insulationProperlyStrippedState: project.qualityReport.insulationProperlyStrippedState,
        insulationProperlyStrippedNote: project.qualityReport.insulationProperlyStrippedNote,
        polarityCheckState: project.qualityReport.polarityCheckState,
        polarityCheckNote: project.qualityReport.polarityCheckNote,
        stringOpenCircuitVoltageState: project.qualityReport.stringOpenCircuitVoltageState,
        stringOpenCircuitVoltageNote: project.qualityReport.stringOpenCircuitVoltageNote,
        shortCircuitCurrentState: project.qualityReport.shortCircuitCurrentState,
        shortCircuitCurrentNote: project.qualityReport.shortCircuitCurrentNote,
        insulationResistanceState: project.qualityReport.insulationResistanceState,
        insulationResistanceNote: project.qualityReport.insulationResistanceNote,
        isConnectorSealingIntactState: project.qualityReport.isConnectorSealingIntactState,
        isConnectorSealingIntactNote: project.qualityReport.isConnectorSealingIntactNote,
        isSurgeProtectionBoxPresenState: project.qualityReport.isSurgeProtectionBoxPresenState,
        isSurgeProtectionBoxPresentNote: project.qualityReport.isSurgeProtectionBoxPresentNote,
        isSurgeProtectionAtHouseEntryState:
          project.qualityReport.isSurgeProtectionAtHouseEntryState,
        isSurgeProtectionAtHouseEntryNote: project.qualityReport.isSurgeProtectionAtHouseEntryNote,
        isLightningProtectionDistanceToConductivePartsOfPVSystemAtLeast0_5mState:
          project.qualityReport
            .isLightningProtectionDistanceToConductivePartsOfPVSystemAtLeast0_5mState,
        isLightningProtectionDistanceToConductivePartsOfPVSystemAtLeast0_5mNote:
          project.qualityReport
            .isLightningProtectionDistanceToConductivePartsOfPVSystemAtLeast0_5mNote,
        isModuleFrameUndamagedState: project.qualityReport.isModuleFrameUndamagedState,
        isModuleFrameUndamagedNote: project.qualityReport.isModuleFrameUndamagedNote,
        isModuleGlassUndamagedState: project.qualityReport.isModuleGlassUndamagedState,
        isModuleGlassUndamagedNote: project.qualityReport.isModuleGlassUndamagedNote,
        isBacksheetUndamagedState: project.qualityReport.isBacksheetUndamagedState,
        isBacksheetUndamagedNote: project.qualityReport.isBacksheetUndamagedNote,
        isModuleNotShadedState: project.qualityReport.isModuleNotShadedState,
        isModuleNotShadedNote: project.qualityReport.isModuleNotShadedNote,
        isConnectorProperlyPluggedState: project.qualityReport.isConnectorProperlyPluggedState,
        isConnectorProperlyPluggedNote: project.qualityReport.isConnectorProperlyPluggedNote,
        isConnectorSealingIntact2State: project.qualityReport.isConnectorSealingIntact2State,
        isConnectorSealingIntact2Note: project.qualityReport.isConnectorSealingIntact2Note,
        isCableRoutedThroughChimneyState: project.qualityReport.isCableRoutedThroughChimneyState,
        isCableRoutedThroughChimneyNote: project.qualityReport.isCableRoutedThroughChimneyNote,
        isChimneySweeperApprovalGivenState:
          project.qualityReport.isChimneySweeperApprovalGivenState,
        isChimneySweeperApprovalGivenNote: project.qualityReport.isChimneySweeperApprovalGivenNote,
        isWallPassageProperlySealedState: project.qualityReport.isWallPassageProperlySealedState,
        isWallPassageProperlySealedNote: project.qualityReport.isWallPassageProperlySealedNote,

        isModuleNotShaded2State: project.qualityReport.isModuleNotShaded2State,
        isModuleNotShaded2Note: project.qualityReport.isModuleNotShaded2Note,
        areModuleClampsNotTwistedState: project.qualityReport.areModuleClampsNotTwistedState,
        areModuleClampsNotTwistedNote: project.qualityReport.areModuleClampsNotTwistedNote,
        isChimneySweeperApprovalGiven2State:
          project.qualityReport.isChimneySweeperApprovalGiven2State,
        isChimneySweeperApprovalGiven2Note:
          project.qualityReport.isChimneySweeperApprovalGiven2Note,
      },
    });
  }
  // console.log(project.measurement);
  // const roughObjSize = JSON.stringify(payload).length / 1024;
  // console.log(roughObjSize);

  if (project.customer) {
    delete project.customer.__typename;
    delete project.customer.createdAt;
    delete project.customer.updatedAt;
  }

  // Preserve existing fields for the "customer" object on the server. The meter list comes from
  // the block project-customer-additional-information and is not part of CustomerInput.
  if (project.customer) {
    const { electricMeterNumbers, ...customerWithoutMeterList } = project.customer;
    payload.customer = {
      ...customerWithoutMeterList,
      // Ensure birthdayAt is set to null if not provided
      birthdayAt: project.customer.birthdayAt ? project.customer.birthdayAt : null,
      number: toCustomerNumberInput(project.customer.number),
    };
  }
  if (project.sales) {
    delete project.sales.__typename;
    project.sales.inCharge = project.sales.inCharge ? project.sales.inCharge.id : null;
    // Exclude handover from generic update payload; managed by dedicated mutation
    const { handover, ...salesWithoutHandover } = project.sales;
    payload.sales = {
      ...salesWithoutHandover,
    };
  }

  if (project.installationAddress) {
    delete project.installationAddress.__typename;
    delete project.installationAddress.createdAt;
    delete project.installationAddress.updatedAt;
    payload.installationAddress = {
      ...project.installationAddress,
    };
  }
  // console.log(project.sales);

  if (project.deferredState) {
    delete project.deferredState.__typename;
    delete project.deferredState._id;
    delete project.deferredState.createdAt;
    delete project.deferredState.updatedAt;

    payload.deferredState = {
      ...project.deferredState,
    };
  }

  if (project.technicalReview) {
    delete project.technicalReview.__typename;
    delete project.technicalReview._id;
    delete project.technicalReview.createdAt;
    delete project.technicalReview.updatedAt;

    payload.technicalReview = {
      ...project.technicalReview,
    };
  }

  if (project.investmentDeductionAllowance) {
    delete project.investmentDeductionAllowance.__typename;
    delete project.investmentDeductionAllowance.calculatedEEGfeedInTariffEurPerKwh;

    payload.investmentDeductionAllowance = {
      ...project.investmentDeductionAllowance,
    };
  }

  if (project.bank) {
    const bankPayload = { ...project.bank };
    delete bankPayload.__typename;
    delete bankPayload._id;
    delete bankPayload.createdAt;
    delete bankPayload.updatedAt;
    delete bankPayload.attachments;
    delete bankPayload.attachmentTypes;

    payload.bank = bankPayload;
  }

  /**
   * new approach: prepare payload and validate by type.
   * merge with existing payload until refactoring is done.
   * @todo derive types from graphql type?
   */

  const typeConfig = {
    ...PROJECT_BASE_INFORMATION_FIELD_TYPES,
    ...PROJECT_CALCULATION_INTERNAL_FIELD_TYPES,
    ...PROJECT_EMPLOYER_COMMUNICATION_FIELD_TYPES,
    ...PROJECT_POWER_COMPANY_FIELD_TYPES,
    ...PROJECT_POWER_COMPANY_COMMUNICATION_FIELD_TYPES,
    ...PROJECT_MEASUREMENT_POINT_OPERATOR_FIELD_TYPES,
    calculatedExpectedTimeExpenditureInDays: 'number',
    calculatedExpectedTimeExpenditureInDaysOverridden: 'boolean',
    projectDescriptionState: 'string',
    mandateState: 'string',
    layoutPlanState: 'string',
    installationImagesState: 'string',
    installationTeamDocumentationState: 'string',
    documentationTransferedToEmployerState: 'string',
    employerInvoiceNumber: 'string',
    technicalReviewDescription: 'string',
    customerDescription: 'string',
    accountingDescription: 'string',
    measurementVerificationState: 'string',
    measurementAcVerificationState: 'string',
    measurementDcVerificationState: 'string',
    measurementApprovementVerificationState: 'string',
    // substructureVerificationState: 'string',
    // dcVerificationState: 'string',
    // acVerificationState: 'string',
    // serviceVerificationState: 'string',
    // serviceApprovementVerificationState: 'string',
    serviceApprovementNote: 'string',
    // acceptanceApprovementVerificationState: 'string',
    acceptanceApprovementNote: 'string',
    // invoiceApprovementVerificationState: 'string',
    ...PROJECT_INVOICE_APPROVAL_FIELD_TYPES,
    accountingAdditionalAcPositions: 'string',

    technichalReviewAt: 'date',
    consumablesOrderedAt: 'date',
    acceptancePlannedAt: 'date',
    acceptanceCompletedAt: 'date',
    invoiceSentToEmployerAt: 'date',
    employerPayedAt: 'date',
    installationTeamPayedAt: 'date',
    documentationSentToEmployerAt: 'date',
    consumablesDeliveredAt: 'date',

    // no client: the API sets the organization on create and denies changing it
    electrician: 'id',
  };

  // console.log(project);
  const validatedPayload = preparePayloadForGraphQL(project, typeConfig);
  // console.log('validatedPayload', validatedPayload);
  const mergedPayload = { ...payload, ...validatedPayload };
  excludedFields.forEach((field) => delete mergedPayload[field]);

  return mergedPayload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Get a smaller subset of project fields
   * @param {*} param0
   * @param {*} payload
   */
  async fetchProjectList({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            projects {
              ...projectListFields
            }
          }
          ${PROJECT_LIST_REQUEST_FIELDS}
        `,
      });
      commit('SET_PROJECTS', response.data.projects);
    } catch (err) {
      throw new Error(err);
    }
  },
  /**
   * Get a smaller subset of project fields filtered
   * @param {*} param0
   * @param {*} payload
   */
  async fetchProjectListFiltered({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            projectsFiltered {
              ...projectListFields
            }
          }
          ${PROJECT_LIST_REQUEST_FIELDS}
        `,
      });
      commit('SET_PROJECTS', response.data.projectsFiltered);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchProjectsByEmployer({ commit, dispatch }, employerId) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsByEmployer($employerId: ID!) {
            projectsByEmployer(employerId: $employerId) {
              ...projectListFields
            }
          }
          ${PROJECT_LIST_REQUEST_FIELDS}
        `,
        variables: {
          employerId: employerId,
        },
      });
      commit('SET_PROJECTS', response.data.projectsByEmployer);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * fetch all projects belonging to the currently logged in client
   * @param {*} param0
   * @param {*} clientId
   */
  async fetchProjectsByClient({ commit, dispatch }, clientId) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsByClient($clientId: ID!) {
            projectsByClient(clientId: $clientId) {
              ...projectListFields
            }
          }
          ${PROJECT_LIST_REQUEST_FIELDS}
        `,
        variables: {
          clientId: clientId,
        },
      });
      commit('SET_PROJECTS', response.data.projectsByClient);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Get a project list for the mobile view
   * @deprecated
   * @param {*} param0
   * @param {*} payload
   */
  async fetchProjectListFilteredMobile({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            projectsFilteredMobile {
              ...projectPaginatedListFields
            }
          }
          ${PROJECT_LIST_MOBILE_REQUEST_FIELDS}
        `,
      });
      commit('SET_PROJECTS', response.data.projectsFilteredMobile);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchProjects({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            projects {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
      });
      commit('SET_PROJECTS', response.data.projects);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Fetch a paginated, sorted and filtered project list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchProjectsPaginatedMobile({ commit, dispatch }, { filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsPaginatedMobile($filters: ProjectFilterInput) {
            projectsPaginatedMobile(filters: $filters) {
              ...projectPaginatedListFields
            }
          }
          ${PROJECT_LIST_MOBILE_REQUEST_FIELDS}
        `,
        variables: {
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });

      commit('SET_PROJECTS', response.data.projectsPaginatedMobile);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /**
   * Fetch a paginated, sorted and filtered project list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchProjectsPaginated(
    { commit, dispatch },
    { page, pageSize, sortField, sortOrder, filters, projectListType = 'PROJECTS' }
  ) {
    console.log('fetchProjectsPaginated()', filters);
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsPaginated(
            $page: Int!
            $pageSize: Int!
            $sortField: String
            $sortOrder: Int
            $filters: ProjectFilterInput
          ) {
            projectsPaginated(
              page: $page
              pageSize: $pageSize
              sortField: $sortField
              sortOrder: $sortOrder
              filters: $filters
            ) {
              projects {
                ...projectPaginatedListFields
              }
              projectCount
            }
          }
          ${PROJECT_PAGINATED_LIST_REQUEST_FIELDS}
        `,
        variables: {
          page: page,
          pageSize: pageSize,
          sortField: sortField,
          sortOrder: sortOrder,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });

      // console.log('response.data.projectsPaginated.projects', response.data.projectsPaginated.projects);

      if (projectListType === 'PROJECTS_WITH_ITEMS') {
        commit('SET_PROJECTS_ITEMS_LIST', response.data.projectsPaginated.projects);
        commit('SET_PROJECTS_ITEMS_LIST_COUNT', response.data.projectsPaginated.projectCount);
      } else {
        commit('SET_PROJECTS', response.data.projectsPaginated.projects);
        commit('SET_PROJECT_COUNT', response.data.projectsPaginated.projectCount);
      }
      // commit('ACTIVATE_PROJECT_CACHE');
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /**
   * Fetch a paginated, sorted and filtered project list in client context
   * @param {*} param0
   * @param {*} payload
   */
  async fetchProjectsPaginatedByClient(
    { commit, dispatch },
    { clientId, page, pageSize, sortField, sortOrder, filters }
  ) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsPaginatedByClient(
            $clientId: ID!
            $page: Int!
            $pageSize: Int!
            $sortField: String
            $sortOrder: Int
            $filters: ProjectFilterInput
          ) {
            projectsPaginatedByClient(
              clientId: $clientId
              page: $page
              pageSize: $pageSize
              sortField: $sortField
              sortOrder: $sortOrder
              filters: $filters
            ) {
              projects {
                ...projectPaginatedListFields
              }
              projectCount
            }
          }
          ${PROJECT_PAGINATED_LIST_REQUEST_FIELDS}
        `,
        variables: {
          clientId: clientId,
          page: page,
          pageSize: pageSize,
          sortField: sortField,
          sortOrder: sortOrder,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });

      // response.data.projectsPaginated.projects.map((project) =>
      //   console.log(project.number, project.customer.lastname)
      // );

      commit('SET_PROJECTS', response.data.projectsPaginatedByClient.projects);
      commit('SET_PROJECT_COUNT', response.data.projectsPaginatedByClient.projectCount);
      // commit('ACTIVATE_PROJECT_CACHE');
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchProjectByNumber({ commit, dispatch }, projectNumber) {
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });

      if (errors) {
        return errors;
      }

      commit('ADD_OR_UPDATE_PROJECT', data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchProjectWithFinModelByNumber({ commit }, projectNumber) {
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query projectByNumberWithFinModel($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              ...projectFields
              ...projectFinModelFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
          ${PROJECT_FIN_MODEL_FIELDS}
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });

      if (errors) {
        return errors;
      }

      commit('ADD_OR_UPDATE_PROJECT', data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Project with the configuration blocks the user has a level on, read through `Project.blocks`
   * (ADR 0006, addendum 2026-10-08). A denied block comes back as an error: the result is then
   * returned and not stored, like in `fetchProjectByNumber`.
   */
  async fetchProjectWithBlocksByNumber({ commit, dispatch, rootGetters }, projectNumber) {
    // Switch on: the levels decide which blocks are requested, so myAccess must be there first.
    if (rootGetters['auth/isOrganizationRoleNavigationEnabled'] && !rootGetters.isMyAccessLoaded) {
      await dispatch('fetchMyAccess');
    }
    const hasLevel = (blockId) => rootGetters.getAccessLevel(blockId) !== null;
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query projectByNumberWithBlocks(
            $projectNumber: Int!
            $withBase: Boolean!
            $withExtended: Boolean!
            $withBaseInformation: Boolean!
            $withPresentations: Boolean!
            $withClarification: Boolean!
            $withCalculationInternal: Boolean!
            $withFinancialModel: Boolean!
            $withPlantData: Boolean!
            $withFinancing: Boolean!
            $withIabTaxData: Boolean!
            $withCustomerMasterData: Boolean!
            $withCustomerInstallationAddress: Boolean!
            $withCustomerTaxBank: Boolean!
            $withCustomerAdditionalInformation: Boolean!
            $withParties: Boolean!
            $withEmployerCommunication: Boolean!
            $withPosts: Boolean!
            $withEmail: Boolean!
            $withTechnicalReviewDc: Boolean!
            $withTechnicalReviewAc: Boolean!
            $withTimeTrackingSummary: Boolean!
            $withRoughPlanning: Boolean!
            $withAppointmentNotes: Boolean!
            $withPowerCompany: Boolean!
            $withPowerCompanyCommunication: Boolean!
            $withMeasurementPointOperator: Boolean!
            $withInvoiceApproval: Boolean!
            $withBankCustomer: Boolean!
            $withBankSelfDisclosure: Boolean!
            $withBankEmploymentIncome: Boolean!
            $withBankSelfEmployment: Boolean!
            $withBankRentalIncome: Boolean!
            $withBankCapitalAssets: Boolean!
            $withBankSummary: Boolean!
            $withDocuments: Boolean!
            $withMeasurementReportBuild: Boolean!
            $withMeasurementReportVerification: Boolean!
            $withMeasurementReportApproval: Boolean!
            $withMeasurementReportData: Boolean!
            $withAcceptanceReportBuild: Boolean!
            $withAcceptanceReportVerification: Boolean!
            $withAcceptanceReportApproval: Boolean!
            $withAcceptanceReportData: Boolean!
            $withServiceReportBuild: Boolean!
            $withServiceReportVerification: Boolean!
            $withServiceReportApproval: Boolean!
            $withServiceReportData: Boolean!
            $withQualityReportData: Boolean!
            $withSalesReportBuild: Boolean!
            $withSalesReportData: Boolean!
          ) {
            projectByNumber(projectNumber: $projectNumber) {
              ...projectFields
              blocks {
                baseConfiguration @include(if: $withBase) {
                  ...projectBaseConfigurationFields
                }
                extendedConfiguration @include(if: $withExtended) {
                  ...projectExtendedConfigurationFields
                }
                baseInformation @include(if: $withBaseInformation) {
                  ...projectBaseInformationFields
                }
                presentations @include(if: $withPresentations) {
                  ...projectPresentationsFields
                }
                clarification @include(if: $withClarification) {
                  ...projectClarificationFields
                }
                calculationInternal @include(if: $withCalculationInternal) {
                  ...projectCalculationInternalFields
                }
                financialModel @include(if: $withFinancialModel) {
                  ...projectFinancialModelFields
                }
                plantData @include(if: $withPlantData) {
                  ...projectPlantDataFields
                }
                financing @include(if: $withFinancing) {
                  ...projectFinancingFields
                }
                iabTaxData @include(if: $withIabTaxData) {
                  ...projectIabTaxDataFields
                }
                customerMasterData @include(if: $withCustomerMasterData) {
                  ...projectCustomerMasterDataFields
                }
                customerInstallationAddress @include(if: $withCustomerInstallationAddress) {
                  ...projectCustomerInstallationAddressFields
                }
                customerTaxBank @include(if: $withCustomerTaxBank) {
                  ...projectCustomerTaxBankFields
                }
                customerAdditionalInformation @include(if: $withCustomerAdditionalInformation) {
                  ...projectCustomerAdditionalInformationFields
                }
                parties @include(if: $withParties) {
                  ...projectPartiesFields
                }
                employerCommunication @include(if: $withEmployerCommunication) {
                  ...projectEmployerCommunicationFields
                }
                posts @include(if: $withPosts) {
                  ...projectPostsFields
                }
                email @include(if: $withEmail) {
                  ...projectEmailFields
                }
                technicalReviewDc @include(if: $withTechnicalReviewDc) {
                  ...projectTechnicalReviewDcFields
                }
                technicalReviewAc @include(if: $withTechnicalReviewAc) {
                  ...projectTechnicalReviewAcFields
                }
                timeTrackingSummary @include(if: $withTimeTrackingSummary) {
                  ...projectTimeTrackingSummaryFields
                }
                roughPlanning @include(if: $withRoughPlanning) {
                  ...projectRoughPlanningFields
                }
                appointmentNotes @include(if: $withAppointmentNotes) {
                  ...projectAppointmentNotesFields
                }
                powerCompany @include(if: $withPowerCompany) {
                  ...projectPowerCompanyFields
                }
                powerCompanyCommunication @include(if: $withPowerCompanyCommunication) {
                  ...projectPowerCompanyCommunicationFields
                }
                measurementPointOperator @include(if: $withMeasurementPointOperator) {
                  ...projectMeasurementPointOperatorFields
                }
                invoiceApproval @include(if: $withInvoiceApproval) {
                  ...projectInvoiceApprovalFields
                }
                bankCustomer @include(if: $withBankCustomer) {
                  ...projectBankCustomerFields
                }
                bankSelfDisclosure @include(if: $withBankSelfDisclosure) {
                  ...projectBankSelfDisclosureFields
                }
                bankEmploymentIncome @include(if: $withBankEmploymentIncome) {
                  ...projectBankEmploymentIncomeFields
                }
                bankSelfEmployment @include(if: $withBankSelfEmployment) {
                  ...projectBankSelfEmploymentFields
                }
                bankRentalIncome @include(if: $withBankRentalIncome) {
                  ...projectBankRentalIncomeFields
                }
                bankCapitalAssets @include(if: $withBankCapitalAssets) {
                  ...projectBankCapitalAssetsFields
                }
                bankSummary @include(if: $withBankSummary) {
                  ...projectBankSummaryFields
                }
                documents @include(if: $withDocuments) {
                  ...projectDocumentsFields
                }
                measurementReportBuild @include(if: $withMeasurementReportBuild) {
                  ...projectMeasurementReportBuildFields
                }
                measurementReportVerification @include(if: $withMeasurementReportVerification) {
                  ...projectMeasurementReportVerificationFields
                }
                measurementReportApproval @include(if: $withMeasurementReportApproval) {
                  ...projectMeasurementReportApprovalFields
                }
                measurementReportData @include(if: $withMeasurementReportData) {
                  ...projectMeasurementReportDataFields
                }
                acceptanceReportBuild @include(if: $withAcceptanceReportBuild) {
                  ...projectAcceptanceReportBuildFields
                }
                acceptanceReportVerification @include(if: $withAcceptanceReportVerification) {
                  ...projectAcceptanceReportVerificationFields
                }
                acceptanceReportApproval @include(if: $withAcceptanceReportApproval) {
                  ...projectAcceptanceReportApprovalFields
                }
                acceptanceReportData @include(if: $withAcceptanceReportData) {
                  ...projectAcceptanceReportDataFields
                }
                serviceReportBuild @include(if: $withServiceReportBuild) {
                  ...projectServiceReportBuildFields
                }
                serviceReportVerification @include(if: $withServiceReportVerification) {
                  ...projectServiceReportVerificationFields
                }
                serviceReportApproval @include(if: $withServiceReportApproval) {
                  ...projectServiceReportApprovalFields
                }
                serviceReportData @include(if: $withServiceReportData) {
                  ...projectServiceReportDataFields
                }
                qualityReportData @include(if: $withQualityReportData) {
                  ...projectQualityReportDataFields
                }
                salesReportBuild @include(if: $withSalesReportBuild) {
                  ...projectSalesReportBuildFields
                }
                salesReportData @include(if: $withSalesReportData) {
                  ...projectSalesReportDataFields
                }
              }
            }
          }
          ${PROJECT_REQUEST_FIELDS}
          ${PROJECT_BASE_CONFIGURATION_FIELDS}
          ${PROJECT_EXTENDED_CONFIGURATION_FIELDS}
          ${PROJECT_BASE_INFORMATION_FIELDS}
          ${PROJECT_PRESENTATIONS_FIELDS}
          ${PROJECT_CLARIFICATION_FIELDS}
          ${PROJECT_CALCULATION_INTERNAL_FIELDS}
          ${PROJECT_FINANCIAL_MODEL_FIELDS}
          ${PROJECT_PLANT_DATA_FIELDS}
          ${PROJECT_FINANCING_FIELDS}
          ${PROJECT_IAB_TAX_DATA_FIELDS}
          ${PROJECT_CUSTOMER_MASTER_DATA_FIELDS}
          ${PROJECT_CUSTOMER_INSTALLATION_ADDRESS_FIELDS}
          ${PROJECT_CUSTOMER_TAX_BANK_FIELDS}
          ${PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_FIELDS}
          ${PROJECT_PARTIES_FIELDS}
          ${PROJECT_EMPLOYER_COMMUNICATION_FIELDS}
          ${PROJECT_POSTS_FIELDS}
          ${PROJECT_EMAIL_FIELDS}
          ${PROJECT_TECHNICAL_REVIEW_DC_FIELDS}
          ${PROJECT_TECHNICAL_REVIEW_AC_FIELDS}
          ${PROJECT_TIME_TRACKING_SUMMARY_FIELDS}
          ${PROJECT_ROUGH_PLANNING_FIELDS}
          ${PROJECT_APPOINTMENT_NOTES_FIELDS}
          ${PROJECT_POWER_COMPANY_FIELDS}
          ${PROJECT_POWER_COMPANY_COMMUNICATION_FIELDS}
          ${PROJECT_MEASUREMENT_POINT_OPERATOR_FIELDS}
          ${PROJECT_INVOICE_APPROVAL_FIELDS}
          ${PROJECT_BANK_CUSTOMER_FIELDS}
          ${PROJECT_BANK_SELF_DISCLOSURE_FIELDS}
          ${PROJECT_BANK_EMPLOYMENT_INCOME_FIELDS}
          ${PROJECT_BANK_SELF_EMPLOYMENT_FIELDS}
          ${PROJECT_BANK_RENTAL_INCOME_FIELDS}
          ${PROJECT_BANK_CAPITAL_ASSETS_FIELDS}
          ${PROJECT_BANK_SUMMARY_FIELDS}
          ${PROJECT_DOCUMENTS_FIELDS}
          ${PROJECT_MEASUREMENT_REPORT_BUILD_FIELDS}
          ${PROJECT_MEASUREMENT_REPORT_VERIFICATION_FIELDS}
          ${PROJECT_MEASUREMENT_REPORT_APPROVAL_FIELDS}
          ${PROJECT_MEASUREMENT_REPORT_DATA_FIELDS}
          ${PROJECT_ACCEPTANCE_REPORT_BUILD_FIELDS}
          ${PROJECT_ACCEPTANCE_REPORT_VERIFICATION_FIELDS}
          ${PROJECT_ACCEPTANCE_REPORT_APPROVAL_FIELDS}
          ${PROJECT_ACCEPTANCE_REPORT_DATA_FIELDS}
          ${PROJECT_SERVICE_REPORT_BUILD_FIELDS}
          ${PROJECT_SERVICE_REPORT_VERIFICATION_FIELDS}
          ${PROJECT_SERVICE_REPORT_APPROVAL_FIELDS}
          ${PROJECT_SERVICE_REPORT_DATA_FIELDS}
          ${PROJECT_QUALITY_REPORT_DATA_FIELDS}
          ${PROJECT_SALES_REPORT_BUILD_FIELDS}
          ${PROJECT_SALES_REPORT_DATA_FIELDS}
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
          withBase: hasLevel(PROJECT_BASE_CONFIGURATION_BLOCK),
          withExtended: hasLevel(PROJECT_EXTENDED_CONFIGURATION_BLOCK),
          withBaseInformation: hasLevel(PROJECT_BASE_INFORMATION_BLOCK),
          withPresentations: hasLevel(PROJECT_PRESENTATIONS_BLOCK),
          withClarification: hasLevel(PROJECT_CLARIFICATION_BLOCK),
          withCalculationInternal: hasLevel(PROJECT_CALCULATION_BLOCK),
          withFinancialModel: hasLevel(PROJECT_FINANCIAL_MODEL_BLOCK),
          withPlantData: hasLevel(PROJECT_PLANT_DATA_BLOCK),
          withFinancing: hasLevel(PROJECT_FINANCING_BLOCK),
          withIabTaxData: hasLevel(PROJECT_IAB_TAX_DATA_BLOCK),
          withCustomerMasterData: hasLevel(PROJECT_CUSTOMER_MASTER_DATA_BLOCK),
          withCustomerInstallationAddress: hasLevel(PROJECT_CUSTOMER_INSTALLATION_ADDRESS_BLOCK),
          withCustomerTaxBank: hasLevel(PROJECT_CUSTOMER_TAX_BANK_BLOCK),
          withCustomerAdditionalInformation: hasLevel(
            PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_BLOCK
          ),
          withParties: hasLevel(PROJECT_PARTIES_BLOCK),
          withEmployerCommunication: hasLevel(PROJECT_EMPLOYER_COMMUNICATION_BLOCK),
          withPosts: hasLevel(PROJECT_POSTS_BLOCK),
          withEmail: hasLevel(PROJECT_EMAIL_BLOCK),
          withTechnicalReviewDc: hasLevel(PROJECT_TECHNICAL_REVIEW_DC_BLOCK),
          withTechnicalReviewAc: hasLevel(PROJECT_TECHNICAL_REVIEW_AC_BLOCK),
          withTimeTrackingSummary: hasLevel(PROJECT_TIME_TRACKING_SUMMARY_BLOCK),
          withRoughPlanning: hasLevel(PROJECT_ROUGH_PLANNING_BLOCK),
          withAppointmentNotes: hasLevel(PROJECT_APPOINTMENT_NOTES_BLOCK),
          withPowerCompany: hasLevel(PROJECT_POWER_COMPANY_BLOCK),
          withPowerCompanyCommunication: hasLevel(PROJECT_POWER_COMPANY_COMMUNICATION_BLOCK),
          withMeasurementPointOperator: hasLevel(PROJECT_MEASUREMENT_POINT_OPERATOR_BLOCK),
          withInvoiceApproval: hasLevel(PROJECT_INVOICE_APPROVAL_BLOCK),
          withBankCustomer: hasLevel(PROJECT_BANK_CUSTOMER_BLOCK),
          withBankSelfDisclosure: hasLevel(PROJECT_BANK_SELF_DISCLOSURE_BLOCK),
          withBankEmploymentIncome: hasLevel(PROJECT_BANK_EMPLOYMENT_INCOME_BLOCK),
          withBankSelfEmployment: hasLevel(PROJECT_BANK_SELF_EMPLOYMENT_BLOCK),
          withBankRentalIncome: hasLevel(PROJECT_BANK_RENTAL_INCOME_BLOCK),
          withBankCapitalAssets: hasLevel(PROJECT_BANK_CAPITAL_ASSETS_BLOCK),
          withBankSummary: hasLevel(PROJECT_BANK_SUMMARY_BLOCK),
          withDocuments: hasLevel(PROJECT_DOCUMENTS_BLOCK),
          withMeasurementReportBuild: hasLevel(PROJECT_MEASUREMENT_REPORT_BUILD_BLOCK),
          withMeasurementReportVerification: hasLevel(
            PROJECT_MEASUREMENT_REPORT_VERIFICATION_BLOCK
          ),
          withMeasurementReportApproval: hasLevel(PROJECT_MEASUREMENT_REPORT_APPROVAL_BLOCK),
          withMeasurementReportData: hasLevel(PROJECT_MEASUREMENT_REPORT_DATA_BLOCK),
          withAcceptanceReportBuild: hasLevel(PROJECT_ACCEPTANCE_REPORT_BUILD_BLOCK),
          withAcceptanceReportVerification: hasLevel(PROJECT_ACCEPTANCE_REPORT_VERIFICATION_BLOCK),
          withAcceptanceReportApproval: hasLevel(PROJECT_ACCEPTANCE_REPORT_APPROVAL_BLOCK),
          withAcceptanceReportData: hasLevel(PROJECT_ACCEPTANCE_REPORT_DATA_BLOCK),
          withServiceReportBuild: hasLevel(PROJECT_SERVICE_REPORT_BUILD_BLOCK),
          withServiceReportVerification: hasLevel(PROJECT_SERVICE_REPORT_VERIFICATION_BLOCK),
          withServiceReportApproval: hasLevel(PROJECT_SERVICE_REPORT_APPROVAL_BLOCK),
          withServiceReportData: hasLevel(PROJECT_SERVICE_REPORT_DATA_BLOCK),
          withQualityReportData: hasLevel(PROJECT_QUALITY_REPORT_DATA_BLOCK),
          withSalesReportBuild: hasLevel(PROJECT_SALES_REPORT_BUILD_BLOCK),
          withSalesReportData: hasLevel(PROJECT_SALES_REPORT_DATA_BLOCK),
        },
      });

      if (errors) {
        return errors;
      }

      commit('ADD_OR_UPDATE_PROJECT', data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Reloads one block of an operation block (polling after a build, reload after an upload or
   * delete) and merges only `blocks.<blockField>` into the stored project. On errors nothing is
   * stored and null is returned, so a failed reload never clears the block on the open page.
   * @param {Object} payload
   * @param {number|string} payload.projectNumber
   * @param {string} payload.blockField field on `ProjectBlocks`, e.g. `presentations`
   * @param {Object} payload.fragment gql fragment on the block type
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectBlockByNumber({ commit }, { projectNumber, blockField, fragment }) {
    return queryProjectBlock(commit, { projectNumber, blockField, fragment });
  },

  /**
   * Reloads the block project-posts (poll, reload after writing or deleting a post). An own action
   * instead of fetchProjectBlockByNumber, so the loading plugin can skip the poll.
   * @param {number|string} projectNumber
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectPostsBlock({ commit }, projectNumber) {
    return queryProjectBlock(commit, {
      projectNumber,
      blockField: 'posts',
      fragment: PROJECT_POSTS_FIELDS,
    });
  },

  /**
   * Reloads the block project-documents (PDF and ZIP polls, reload after an upload or delete). An
   * own action instead of fetchProjectBlockByNumber, so the loading plugin can skip the polls.
   * @param {number|string} projectNumber
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectDocumentsBlock({ commit }, projectNumber) {
    return queryProjectBlock(commit, {
      projectNumber,
      blockField: 'documents',
      fragment: PROJECT_DOCUMENTS_FIELDS,
    });
  },

  /**
   * Reloads the block project-measurement-report-build (PDF and ZIP polls). An own action instead
   * of fetchProjectBlockByNumber, so the loading plugin can skip the polls.
   * @param {number|string} projectNumber
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectMeasurementReportBuildBlock({ commit }, projectNumber) {
    return queryProjectBlock(commit, {
      projectNumber,
      blockField: 'measurementReportBuild',
      fragment: PROJECT_MEASUREMENT_REPORT_BUILD_FIELDS,
    });
  },

  /**
   * Reloads the block project-acceptance-report-build (PDF and ZIP polls). An own action instead
   * of fetchProjectBlockByNumber, so the loading plugin can skip the polls.
   * @param {number|string} projectNumber
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectAcceptanceReportBuildBlock({ commit }, projectNumber) {
    return queryProjectBlock(commit, {
      projectNumber,
      blockField: 'acceptanceReportBuild',
      fragment: PROJECT_ACCEPTANCE_REPORT_BUILD_FIELDS,
    });
  },

  /**
   * Reloads the block project-sales-report-build (PDF poll). An own action instead of
   * fetchProjectBlockByNumber, so the loading plugin can skip the polls.
   * @param {number|string} projectNumber
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectSalesReportBuildBlock({ commit }, projectNumber) {
    return queryProjectBlock(commit, {
      projectNumber,
      blockField: 'salesReportBuild',
      fragment: PROJECT_SALES_REPORT_BUILD_FIELDS,
    });
  },

  /**
   * Reloads the block project-service-report-build (PDF and ZIP polls). An own action instead of
   * fetchProjectBlockByNumber, so the loading plugin can skip the polls.
   * @param {number|string} projectNumber
   * @returns {Object|null} the reloaded block
   */
  async fetchProjectServiceReportBuildBlock({ commit }, projectNumber) {
    return queryProjectBlock(commit, {
      projectNumber,
      blockField: 'serviceReportBuild',
      fragment: PROJECT_SERVICE_REPORT_BUILD_FIELDS,
    });
  },

  /**
   * Attachment size of a mail type through the block project-email. Reloads the block's excerpt
   * in the same query and merges it key by key into `blocks.email`, keeping the templates, so the
   * placeholders and the recipient use the values saved since the page was loaded. The size is
   * not stored. On errors nothing is stored and null is returned.
   * @param {Object} payload
   * @param {number} payload.projectNumber
   * @param {string} payload.messageBoilerplateType
   * @param {string} [payload.messageAttachmentType]
   * @returns {number|null} the attachment size
   */
  async fetchProjectEmailAttachmentSize(
    { state, commit },
    { projectNumber, messageBoilerplateType, messageAttachmentType }
  ) {
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query projectEmailAttachmentSize(
            $projectNumber: Int!
            $messageBoilerplateType: String!
            $messageAttachmentType: String
          ) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              blocks {
                email {
                  ...projectEmailExcerptFields
                  mailAttachmentSize(
                    messageBoilerplateType: $messageBoilerplateType
                    messageAttachmentType: $messageAttachmentType
                  )
                }
              }
            }
          }
          ${PROJECT_EMAIL_EXCERPT_FIELDS}
        `,
        variables: { projectNumber, messageBoilerplateType, messageAttachmentType },
        fetchPolicy: 'network-only',
      });

      if (errors) {
        return null;
      }

      const { id, blocks } = data.projectByNumber;
      const { mailAttachmentSize, ...excerpt } = blocks.email;
      const storedProject = state.projects.find((project) => project.id === id);
      commit('MERGE_PROJECT_FIELDS', {
        id,
        blocks: { email: { ...storedProject?.blocks?.email, ...excerpt } },
      });
      return mailAttachmentSize;
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchPostsOfProjectByNumber({ commit }, projectNumber) {
    try {
      // console.log('fetchPostsOfProjectByNumber()', projectNumber);

      const { data, errors } = await apolloClient.query({
        query: gql`
          query postsOfProjectByNumber($projectNumber: Int!) {
            postsOfProjectByNumber(projectNumber: $projectNumber) {
              ...projectPostFields
            }
          }
          ${PROJECT_POST_FIELDS}
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });

      if (errors) {
        return errors;
      }

      commit('ADD_OR_UPDATE_POSTS', { projectNumber, posts: data.postsOfProjectByNumber });
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Error handling */
  // handleErrors({ dispatch }, errors) {
  //   // console.dir(errors[0]);

  //   if (errors[0].extensions.code === 'FORBIDDEN') {
  //     router.push('/403');
  //     return;
  //   }

  //   dispatch('message', {
  //     message: errors[0].message,
  //     type: 'danger',
  //     lifetime: 1,
  //   });
  // },

  async fetchNextProject({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      commit('ADD_OR_UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchProjectsExportUrl({ commit, dispatch }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsExportUrl {
            projectsExportUrl
          }
        `,
      });
      return response.data.projectsExportUrl;
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchProjectMailAttachmentSize({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectMailAttachmentSize(
            $projectNumber: Int!
            $messageBoilerplateType: String!
            $messageAttachmentType: String
          ) {
            projectMailAttachmentSize(
              projectNumber: $projectNumber
              messageBoilerplateType: $messageBoilerplateType
              messageAttachmentType: $messageAttachmentType
            )
          }
        `,
        variables: {
          projectNumber: payload.projectNumber,
          messageBoilerplateType: payload.messageBoilerplateType,
          messageAttachmentType: payload.messageAttachmentType,
        },
      });
      return response.data.projectMailAttachmentSize;
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * @deprecated
   * @param {} param0
   */
  async fetchInstallationTeams({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "InstallationTeam") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_INSTALLATION_TEAMS', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  /**
   * @deprecated
   * @param {*} param0
   */
  async fetchInstallationTypes({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "InstallationType") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_INSTALLATION_TYPES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },
  async fetchProjectsInNeedsAnalysis({ commit }, { page, pageSize, sortField, sortOrder }) {
    console.log('fetchProjectsInNeedsAnalysis aufgerufen');

    try {
      const response = await apolloClient.query({
        query: gql`
          query projectsInNeedsAnalysis(
            $page: Int!
            $pageSize: Int!
            $sortField: String
            $sortOrder: Int
          ) {
            projectsInNeedsAnalysis(
              page: $page
              pageSize: $pageSize
              sortField: $sortField
              sortOrder: $sortOrder
            ) {
              projects {
                number
                customer {
                  lastname
                  firstname
                }
              }
            }
          }
        `,
        variables: { page, pageSize, sortField, sortOrder },
      });
      commit('SET_PROJECTS_IN_NEEDS_ANALYSIS', response.data.projectsInNeedsAnalysis.projects);
      commit('SET_PROJECTS_COUNT', response.data.projectsInNeedsAnalysis.projectCount);
    } catch (err) {
      console.error(err);
    }
  },
  async fetchOriginalInstallationTypes({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "OriginalInstallationType") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_ORIGINAL_INSTALLATION_TYPES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },
  async fetchSolarSystemTypes({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "SolarSystemType") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_SOLAR_SYSTEM_TYPES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },
  async fetchProjectAttachmentTypes({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "ProjectAttachmentType") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_PROJECT_ATTACHMENT_TYPES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  /**
   *
   * @param {*} param0
   */
  async fetchDocumentStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "DocumentState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_DOCUMENT_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchProjectStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "ProjectState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_PROJECT_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchNetworkSupplyManagementStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "NetworkSupplyManagementState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_NETWORK_SUPPLY_MANAGEMENT_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchNetworkSupplyManagementPermanentRestrictionStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "NetworkSupplyManagementPermanentRestrictionState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit(
        'SET_NETWORK_SUPPLY_MANAGEMENT_PERMANENT_RESTRICTION_STATES',
        response.data.__type.enumValues
      );
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchNetworkSupplyManagementXOverStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "NetworkSupplyManagementXOverState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_NETWORK_SUPPLY_MANAGEMENT_X_OVER_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchNetOutputStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "NetOutputState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_NET_OUTPUT_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchStorageCouplingStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "StorageCouplingState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_STORAGE_COUPLING_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchElectricMeterMountingStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "ElectricMeterMountingState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_ELECTRIC_METER_MOUNTING_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async fetchHouseFuseStates({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "HouseFuseState") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_HOUSE_FUSE_STATES', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  /**
   * create a new project
   * @param {*} param0
   * @param {*} project
   * @returns
   */
  async createProject({ commit, dispatch }, project) {
    const payload = createPayload(project);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createProject($payload: ProjectInput!) {
            createProject(projectInput: $payload) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Projekt gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_PROJECT', '_new'); // remove temporary _new project
      commit('ADD_OR_UPDATE_PROJECT', response.data.createProject);
      return response.data.createProject.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new sales project
   */
  async createSalesProject({ commit, dispatch }, project) {
    const payload = createPayload(project);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createSalesProject($payload: ProjectInput!) {
            createSalesProject(projectInput: $payload) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Projekt gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_PROJECT', '_new');
      commit('ADD_OR_UPDATE_PROJECT', response.data.createSalesProject);
      return response.data.createSalesProject.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateProject(context, project) {
    const savedProject = await saveProject(context, createPayload(project), project.number);
    context.commit('ADD_OR_UPDATE_PROJECT', savedProject);
  },

  /**
   * Page save of the project detail: the fields saved by blocks are neither sent nor taken from
   * the result, so unsaved block edits stay in the stored project.
   */
  async saveProjectPage(context, project) {
    const savedProject = await saveProject(
      context,
      createPayload(project, { excludedFields: PROJECT_FIELDS_SAVED_BY_BLOCKS }),
      project.number
    );
    const savedFields = { ...savedProject };
    PROJECT_FIELDS_SAVED_BY_BLOCKS.forEach((field) => delete savedFields[field]);
    setDefaultInstallationAddress(savedFields);
    context.commit('MERGE_PROJECT_FIELDS', savedFields);
  },

  async updateProjectBaseConfiguration(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectBaseConfiguration(
          $projectNumber: Int!
          $input: ProjectBaseConfigurationInput!
        ) {
          updateProjectBaseConfiguration(projectNumber: $projectNumber, input: $input) {
            ...projectConfigurationRecomputedFields
            blocks {
              baseConfiguration {
                ...projectBaseConfigurationFields
              }
            }
          }
        }
        ${PROJECT_BASE_CONFIGURATION_FIELDS}
        ${PROJECT_CONFIGURATION_RECOMPUTED_FIELDS}
      `,
      mutationName: 'updateProjectBaseConfiguration',
      variables: { projectNumber, input },
      blockField: 'baseConfiguration',
    });
  },

  async updateProjectExtendedConfiguration(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectExtendedConfiguration(
          $projectNumber: Int!
          $input: ProjectExtendedConfigurationInput!
        ) {
          updateProjectExtendedConfiguration(projectNumber: $projectNumber, input: $input) {
            ...projectConfigurationRecomputedFields
            blocks {
              extendedConfiguration {
                ...projectExtendedConfigurationFields
              }
            }
          }
        }
        ${PROJECT_EXTENDED_CONFIGURATION_FIELDS}
        ${PROJECT_CONFIGURATION_RECOMPUTED_FIELDS}
      `,
      mutationName: 'updateProjectExtendedConfiguration',
      variables: { projectNumber, input },
      blockField: 'extendedConfiguration',
    });
  },

  async updateProjectBaseInformation(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectBaseInformation(
          $projectNumber: Int!
          $input: ProjectBaseInformationInput!
        ) {
          updateProjectBaseInformation(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              baseInformation {
                ...projectBaseInformationFields
              }
            }
          }
        }
        ${PROJECT_BASE_INFORMATION_FIELDS}
      `,
      mutationName: 'updateProjectBaseInformation',
      variables: { projectNumber, input },
      blockField: 'baseInformation',
    });
  },

  async updateProjectClarification(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectClarification(
          $projectNumber: Int!
          $input: ProjectDeferredStateInput!
        ) {
          updateProjectClarification(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              clarification {
                ...projectClarificationFields
              }
            }
          }
        }
        ${PROJECT_CLARIFICATION_FIELDS}
      `,
      mutationName: 'updateProjectClarification',
      variables: { projectNumber, input },
      blockField: 'clarification',
    });
  },

  async updateProjectCalculationInternal(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectCalculationInternal(
          $projectNumber: Int!
          $input: ProjectCalculationInternalInput!
        ) {
          updateProjectCalculationInternal(projectNumber: $projectNumber, input: $input) {
            ...projectCalculationRecomputedFields
            blocks {
              calculationInternal {
                ...projectCalculationInternalFields
              }
            }
          }
        }
        ${PROJECT_CALCULATION_INTERNAL_FIELDS}
        ${PROJECT_CALCULATION_RECOMPUTED_FIELDS}
      `,
      mutationName: 'updateProjectCalculationInternal',
      variables: { projectNumber, input },
      blockField: 'calculationInternal',
    });
  },

  /** The recomputed values follow a new area (lease cost per m² of Kalkulation – intern). */
  async updateProjectPlantData(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectPlantData($projectNumber: Int!, $input: ProjectPlantDataInput!) {
          updateProjectPlantData(projectNumber: $projectNumber, input: $input) {
            ...projectCalculationRecomputedFields
            blocks {
              plantData {
                ...projectPlantDataFields
              }
            }
          }
        }
        ${PROJECT_PLANT_DATA_FIELDS}
        ${PROJECT_CALCULATION_RECOMPUTED_FIELDS}
      `,
      mutationName: 'updateProjectPlantData',
      variables: { projectNumber, input },
      blockField: 'plantData',
    });
  },

  async updateProjectFinancing(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectFinancing($projectNumber: Int!, $input: ProjectFinancingInput!) {
          updateProjectFinancing(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              financing {
                ...projectFinancingFields
              }
            }
          }
        }
        ${PROJECT_FINANCING_FIELDS}
      `,
      mutationName: 'updateProjectFinancing',
      variables: { projectNumber, input },
      blockField: 'financing',
    });
  },

  /** The summary's expected hours follow the planned DC duration. */
  async updateProjectTechnicalReviewDc(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectTechnicalReviewDc(
          $projectNumber: Int!
          $input: ProjectTechnicalReviewDcInput!
          $withTimeTrackingSummary: Boolean!
        ) {
          updateProjectTechnicalReviewDc(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              technicalReviewDc {
                ...projectTechnicalReviewDcFields
              }
              timeTrackingSummary @include(if: $withTimeTrackingSummary) {
                ...projectTimeTrackingSummaryFields
              }
            }
          }
        }
        ${PROJECT_TECHNICAL_REVIEW_DC_FIELDS}
        ${PROJECT_TIME_TRACKING_SUMMARY_FIELDS}
      `,
      mutationName: 'updateProjectTechnicalReviewDc',
      variables: {
        projectNumber,
        input,
        withTimeTrackingSummary: hasTimeTrackingSummaryLevel(context.rootGetters),
      },
      blockField: 'technicalReviewDc',
    });
  },

  /** The summary's expected hours follow the planned AC duration. */
  async updateProjectTechnicalReviewAc(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectTechnicalReviewAc(
          $projectNumber: Int!
          $input: ProjectTechnicalReviewAcInput!
          $withTimeTrackingSummary: Boolean!
        ) {
          updateProjectTechnicalReviewAc(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              technicalReviewAc {
                ...projectTechnicalReviewAcFields
              }
              timeTrackingSummary @include(if: $withTimeTrackingSummary) {
                ...projectTimeTrackingSummaryFields
              }
            }
          }
        }
        ${PROJECT_TECHNICAL_REVIEW_AC_FIELDS}
        ${PROJECT_TIME_TRACKING_SUMMARY_FIELDS}
      `,
      mutationName: 'updateProjectTechnicalReviewAc',
      variables: {
        projectNumber,
        input,
        withTimeTrackingSummary: hasTimeTrackingSummaryLevel(context.rootGetters),
      },
      blockField: 'technicalReviewAc',
    });
  },

  /**
   * The result's requirement lists update the markers of the other tabs; `electricianOptions`
   * lands as an unused field on the project, which the page payload does not send.
   */
  async updateProjectRoughPlanning(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectRoughPlanning(
          $projectNumber: Int!
          $input: ProjectRoughPlanningInput!
        ) {
          updateProjectRoughPlanning(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              roughPlanning {
                ...projectRoughPlanningFields
              }
            }
          }
        }
        ${PROJECT_ROUGH_PLANNING_FIELDS}
      `,
      mutationName: 'updateProjectRoughPlanning',
      variables: { projectNumber, input },
      blockField: 'roughPlanning',
    });
  },

  async updateProjectAppointmentNotes(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectAppointmentNotes(
          $projectNumber: Int!
          $input: ProjectAppointmentNotesInput!
        ) {
          updateProjectAppointmentNotes(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              appointmentNotes {
                ...projectAppointmentNotesFields
              }
            }
          }
        }
        ${PROJECT_APPOINTMENT_NOTES_FIELDS}
      `,
      mutationName: 'updateProjectAppointmentNotes',
      variables: { projectNumber, input },
      blockField: 'appointmentNotes',
    });
  },

  /**
   * The result selects the recomputed requirement lists for the markers of the other tabs, and the
   * power company with the fields the page reads, so no value of the previous one remains.
   */
  async updateProjectPowerCompany(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectPowerCompany(
          $projectNumber: Int!
          $input: ProjectPowerCompanyInput!
        ) {
          updateProjectPowerCompany(projectNumber: $projectNumber, input: $input) {
            id
            number
            ...projectConfigurationRecomputedFields
            powerCompany {
              ...powerCompanyOfProjectFields
            }
            blocks {
              powerCompany {
                ...projectPowerCompanyFields
              }
            }
          }
        }
        ${PROJECT_CONFIGURATION_RECOMPUTED_FIELDS}
        ${POWER_COMPANY_OF_PROJECT_FIELDS}
        ${PROJECT_POWER_COMPANY_FIELDS}
      `,
      mutationName: 'updateProjectPowerCompany',
      variables: { projectNumber, input },
      blockField: 'powerCompany',
    });
  },

  async updateProjectPowerCompanyCommunication(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectPowerCompanyCommunication(
          $projectNumber: Int!
          $input: ProjectPowerCompanyCommunicationInput!
        ) {
          updateProjectPowerCompanyCommunication(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              powerCompanyCommunication {
                ...projectPowerCompanyCommunicationFields
              }
            }
          }
        }
        ${PROJECT_POWER_COMPANY_COMMUNICATION_FIELDS}
      `,
      mutationName: 'updateProjectPowerCompanyCommunication',
      variables: { projectNumber, input },
      blockField: 'powerCompanyCommunication',
    });
  },

  /** The result selects the operator with the fields the page reads, so no value of the previous one remains. */
  async updateProjectMeasurementPointOperator(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectMeasurementPointOperator(
          $projectNumber: Int!
          $input: ProjectMeasurementPointOperatorInput!
        ) {
          updateProjectMeasurementPointOperator(projectNumber: $projectNumber, input: $input) {
            id
            number
            measurementPointOperator {
              ...measurementPointOperatorOfProjectFields
            }
            blocks {
              measurementPointOperator {
                ...projectMeasurementPointOperatorFields
              }
            }
          }
        }
        ${MEASUREMENT_POINT_OPERATOR_OF_PROJECT_FIELDS}
        ${PROJECT_MEASUREMENT_POINT_OPERATOR_FIELDS}
      `,
      mutationName: 'updateProjectMeasurementPointOperator',
      variables: { projectNumber, input },
      blockField: 'measurementPointOperator',
    });
  },

  async updateProjectBankCustomer(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectBankCustomer(
          $projectNumber: Int!
          $input: ProjectBankCustomerInput!
        ) {
          updateProjectBankCustomer(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              bankCustomer {
                ...projectBankCustomerFields
              }
            }
          }
        }
        ${PROJECT_BANK_CUSTOMER_FIELDS}
      `,
      mutationName: 'updateProjectBankCustomer',
      variables: { projectNumber, input },
      blockField: 'bankCustomer',
    });
  },

  async updateProjectBankSelfEmployment(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectBankSelfEmployment(
          $projectNumber: Int!
          $input: ProjectBankSelfEmploymentInput!
        ) {
          updateProjectBankSelfEmployment(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              bankSelfEmployment {
                ...projectBankSelfEmploymentFields
              }
            }
          }
        }
        ${PROJECT_BANK_SELF_EMPLOYMENT_FIELDS}
      `,
      mutationName: 'updateProjectBankSelfEmployment',
      variables: { projectNumber, input },
      blockField: 'bankSelfEmployment',
    });
  },

  async updateProjectBankRentalIncome(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectBankRentalIncome(
          $projectNumber: Int!
          $input: ProjectBankRentalIncomeInput!
        ) {
          updateProjectBankRentalIncome(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              bankRentalIncome {
                ...projectBankRentalIncomeFields
              }
            }
          }
        }
        ${PROJECT_BANK_RENTAL_INCOME_FIELDS}
      `,
      mutationName: 'updateProjectBankRentalIncome',
      variables: { projectNumber, input },
      blockField: 'bankRentalIncome',
    });
  },

  async updateProjectBankCapitalAssets(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectBankCapitalAssets(
          $projectNumber: Int!
          $input: ProjectBankCapitalAssetsInput!
        ) {
          updateProjectBankCapitalAssets(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              bankCapitalAssets {
                ...projectBankCapitalAssetsFields
              }
            }
          }
        }
        ${PROJECT_BANK_CAPITAL_ASSETS_FIELDS}
      `,
      mutationName: 'updateProjectBankCapitalAssets',
      variables: { projectNumber, input },
      blockField: 'bankCapitalAssets',
    });
  },

  async updateProjectIabTaxData(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectIabTaxData($projectNumber: Int!, $input: ProjectIabTaxDataInput!) {
          updateProjectIabTaxData(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              iabTaxData {
                ...projectIabTaxDataFields
              }
            }
          }
        }
        ${PROJECT_IAB_TAX_DATA_FIELDS}
      `,
      mutationName: 'updateProjectIabTaxData',
      variables: { projectNumber, input },
      blockField: 'iabTaxData',
    });
  },

  /**
   * Parties the block project-parties can assign, checked by the block instead of the policy of
   * `partiesPaginated` (plan docs/work/_done/2026-10-project-block-parties, decided 2). The result is
   * returned, not stored. On an error the message is shown and the result is empty.
   */
  async fetchProjectPartyCandidates(
    { dispatch },
    { projectNumber, eligibleFor, search, pagination }
  ) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectPartyCandidates(
            $projectNumber: Int!
            $eligibleFor: [PartyEligibleFor!]!
            $search: String
            $pagination: PaginationInput
          ) {
            projectPartyCandidates(
              projectNumber: $projectNumber
              eligibleFor: $eligibleFor
              search: $search
              pagination: $pagination
            ) {
              parties {
                _id
                number
                company
                firstname
                lastname
                zip
                city
                email
              }
              partyCount
            }
          }
        `,
        variables: { projectNumber, eligibleFor, search, pagination },
        fetchPolicy: 'network-only',
      });

      return response.data.projectPartyCandidates;
    } catch (err) {
      dispatch('message', {
        message: err.graphQLErrors?.[0]?.message ?? err.message,
        type: 'danger',
        lifetime: 1,
      });

      return { parties: [], partyCount: 0 };
    }
  },

  /**
   * Project selection of the order and slot forms in Matrix-Authz (number, project state,
   * customer name and address). Returns the projects without storing them, so the project list
   * stays untouched.
   */
  async fetchProjectSelection({ dispatch }, { search, pagination }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectSelection($search: String, $pagination: PaginationInput) {
            projectSelection(search: $search, pagination: $pagination) {
              projects {
                id
                number
                projectState
                customer {
                  firstname
                  lastname
                  street
                  streetNumber
                  streetNumberSuffix
                  zip
                  city
                }
              }
              projectCount
            }
          }
        `,
        variables: { search, pagination },
        fetchPolicy: 'network-only',
      });

      return response.data.projectSelection.projects;
    } catch (err) {
      dispatch('message', {
        message: err.graphQLErrors?.[0]?.message ?? err.message,
        type: 'danger',
        lifetime: 1,
      });

      return [];
    }
  },

  async updateProjectCustomerMasterData(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectCustomerMasterData(
          $projectNumber: Int!
          $input: ProjectCustomerMasterDataInput!
        ) {
          updateProjectCustomerMasterData(projectNumber: $projectNumber, input: $input) {
            ...projectConfigurationRecomputedFields
            blocks {
              customerMasterData {
                ...projectCustomerMasterDataFields
              }
            }
          }
        }
        ${PROJECT_CUSTOMER_MASTER_DATA_FIELDS}
        ${PROJECT_CONFIGURATION_RECOMPUTED_FIELDS}
      `,
      mutationName: 'updateProjectCustomerMasterData',
      variables: { projectNumber, input },
      blockField: 'customerMasterData',
    });
  },

  async updateProjectCustomerInstallationAddress(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectCustomerInstallationAddress(
          $projectNumber: Int!
          $input: ProjectCustomerInstallationAddressInput!
        ) {
          updateProjectCustomerInstallationAddress(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              customerInstallationAddress {
                ...projectCustomerInstallationAddressFields
              }
            }
          }
        }
        ${PROJECT_CUSTOMER_INSTALLATION_ADDRESS_FIELDS}
      `,
      mutationName: 'updateProjectCustomerInstallationAddress',
      variables: { projectNumber, input },
      blockField: 'customerInstallationAddress',
    });
  },

  async updateProjectCustomerTaxBank(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectCustomerTaxBank(
          $projectNumber: Int!
          $input: ProjectCustomerTaxBankInput!
        ) {
          updateProjectCustomerTaxBank(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              customerTaxBank {
                ...projectCustomerTaxBankFields
              }
            }
          }
        }
        ${PROJECT_CUSTOMER_TAX_BANK_FIELDS}
      `,
      mutationName: 'updateProjectCustomerTaxBank',
      variables: { projectNumber, input },
      blockField: 'customerTaxBank',
    });
  },

  async updateProjectCustomerAdditionalInformation(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectCustomerAdditionalInformation(
          $projectNumber: Int!
          $input: ProjectCustomerAdditionalInformationInput!
        ) {
          updateProjectCustomerAdditionalInformation(projectNumber: $projectNumber, input: $input) {
            ...projectConfigurationRecomputedFields
            blocks {
              customerAdditionalInformation {
                ...projectCustomerAdditionalInformationFields
              }
            }
          }
        }
        ${PROJECT_CUSTOMER_ADDITIONAL_INFORMATION_FIELDS}
        ${PROJECT_CONFIGURATION_RECOMPUTED_FIELDS}
      `,
      mutationName: 'updateProjectCustomerAdditionalInformation',
      variables: { projectNumber, input },
      blockField: 'customerAdditionalInformation',
    });
  },

  async updateProjectParties(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectParties($projectNumber: Int!, $input: ProjectPartiesInput!) {
          updateProjectParties(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              parties {
                ...projectPartiesFields
              }
            }
          }
        }
        ${PROJECT_PARTIES_FIELDS}
      `,
      mutationName: 'updateProjectParties',
      variables: { projectNumber, input },
      blockField: 'parties',
    });
  },

  async updateProjectEmployerCommunication(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectEmployerCommunication(
          $projectNumber: Int!
          $input: ProjectEmployerCommunicationInput!
        ) {
          updateProjectEmployerCommunication(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              employerCommunication {
                ...projectEmployerCommunicationFields
              }
            }
          }
        }
        ${PROJECT_EMPLOYER_COMMUNICATION_FIELDS}
      `,
      mutationName: 'updateProjectEmployerCommunication',
      variables: { projectNumber, input },
      blockField: 'employerCommunication',
    });
  },

  async updateProjectInvoiceApproval(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectInvoiceApproval(
          $projectNumber: Int!
          $input: ProjectInvoiceApprovalInput!
        ) {
          updateProjectInvoiceApproval(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              invoiceApproval {
                ...projectInvoiceApprovalFields
              }
            }
          }
        }
        ${PROJECT_INVOICE_APPROVAL_FIELDS}
      `,
      mutationName: 'updateProjectInvoiceApproval',
      variables: { projectNumber, input },
      blockField: 'invoiceApproval',
    });
  },

  /**
   * Changes the approval state of the block project-invoice-approval immediately. Selects and sets
   * only the state, so an unsaved note of the block stays.
   */
  async updateProjectInvoiceApprovalState(context, { projectNumber, verificationState }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectInvoiceApprovalState(
          $projectNumber: Int!
          $verificationState: VerificationState!
        ) {
          updateProjectInvoiceApprovalState(
            projectNumber: $projectNumber
            verificationState: $verificationState
          ) {
            id
            number
            blocks {
              invoiceApproval {
                id
                number
                invoiceApprovementVerificationState
              }
            }
          }
        }
      `,
      mutationName: 'updateProjectInvoiceApprovalState',
      variables: { projectNumber, verificationState },
      blockField: 'invoiceApproval',
      isMergedIntoStoredBlock: true,
    });
  },

  /**
   * Saves one card of the block project-measurement-report-data (sparse input). The cards are bound
   * to the stored block object, which already holds the saved values; it is never replaced, so
   * unsaved input on other cards stays. Only the legacy copy `project.measurement`, still sent by
   * the other protocol tabs through updateProject, takes the saved measurement.
   */
  async updateProjectMeasurementReportData({ commit, dispatch }, { projectNumber, input }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateProjectMeasurementReportData(
            $projectNumber: Int!
            $input: ProjectMeasurementReportDataInput!
          ) {
            updateProjectMeasurementReportData(projectNumber: $projectNumber, input: $input) {
              id
              number
              blocks {
                measurementReportData {
                  id
                  number
                  measurement {
                    ${MEASUREMENT_SELECTION}
                  }
                }
              }
            }
          }
        `,
        variables: { projectNumber, input },
      });
      dispatch('message', {
        message: 'Projekt aktualisiert!',
        type: 'success',
        lifetime: 1,
      });
      const savedProject = response.data.updateProjectMeasurementReportData;
      const savedMeasurement = savedProject.blocks.measurementReportData.measurement;
      commit('MERGE_PROJECT_FIELDS', { id: savedProject.id, measurement: savedMeasurement });
      // The other protocol tabs branch on the roof type; only a card that saved it sets it, so an
      // unsaved roof type elsewhere stays.
      if ('roofType' in input) {
        commit('UPDATE_MEASUREMENT_ROOF_TYPE', {
          id: savedProject.id,
          roofType: savedMeasurement.roofType,
        });
      }
    } catch (err) {
      handleProjectSaveError(dispatch, err);
    }
  },

  /** Requests the verification of one measurement side from the protocol (data block). */
  async requestProjectMeasurementVerification(context, { projectNumber, side }) {
    await saveProjectBlocks(context, {
      mutation: gql`
        mutation requestProjectMeasurementVerification(
          $projectNumber: Int!
          $side: MeasurementVerificationSide!
          $withMeasurementReportData: Boolean!
          $withMeasurementReportVerification: Boolean!
        ) {
          requestProjectMeasurementVerification(projectNumber: $projectNumber, side: $side) {
            id
            number
            ${MEASUREMENT_VERIFICATION_STATE_BLOCKS_SELECTION}
          }
        }
        ${PROJECT_MEASUREMENT_REPORT_VERIFICATION_FIELDS}
      `,
      mutationName: 'requestProjectMeasurementVerification',
      variables: {
        projectNumber,
        side,
        ...measurementVerificationStateVariables(context.rootGetters),
      },
      blockFields: ['measurementReportData', 'measurementReportVerification'],
    });
  },

  /** Changes the AC or DC verification in the block project-measurement-report-verification. */
  async updateProjectMeasurementVerificationState(
    context,
    { projectNumber, side, verificationState }
  ) {
    await saveProjectBlocks(context, {
      mutation: gql`
        mutation updateProjectMeasurementVerificationState(
          $projectNumber: Int!
          $side: MeasurementVerificationSide!
          $verificationState: VerificationState!
          $withMeasurementReportData: Boolean!
          $withMeasurementReportVerification: Boolean!
        ) {
          updateProjectMeasurementVerificationState(
            projectNumber: $projectNumber
            side: $side
            verificationState: $verificationState
          ) {
            id
            number
            ${MEASUREMENT_VERIFICATION_STATE_BLOCKS_SELECTION}
          }
        }
        ${PROJECT_MEASUREMENT_REPORT_VERIFICATION_FIELDS}
      `,
      mutationName: 'updateProjectMeasurementVerificationState',
      variables: {
        projectNumber,
        side,
        verificationState,
        ...measurementVerificationStateVariables(context.rootGetters),
      },
      blockFields: ['measurementReportData', 'measurementReportVerification'],
    });
  },

  async updateProjectMeasurementApproval(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectMeasurementApproval(
          $projectNumber: Int!
          $input: ProjectMeasurementApprovalInput!
        ) {
          updateProjectMeasurementApproval(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              measurementReportApproval {
                ...projectMeasurementReportApprovalFields
              }
            }
          }
        }
        ${PROJECT_MEASUREMENT_REPORT_APPROVAL_FIELDS}
      `,
      mutationName: 'updateProjectMeasurementApproval',
      variables: { projectNumber, input },
      blockField: 'measurementReportApproval',
    });
  },

  /**
   * Changes the approval state of the block project-measurement-report-approval immediately.
   * Selects and sets only the state, so an unsaved note of the block stays.
   */
  async updateProjectMeasurementApprovalState(context, { projectNumber, verificationState }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectMeasurementApprovalState(
          $projectNumber: Int!
          $verificationState: VerificationState!
        ) {
          updateProjectMeasurementApprovalState(
            projectNumber: $projectNumber
            verificationState: $verificationState
          ) {
            id
            number
            blocks {
              measurementReportApproval {
                id
                number
                measurementApprovementVerificationState
              }
            }
          }
        }
      `,
      mutationName: 'updateProjectMeasurementApprovalState',
      variables: { projectNumber, verificationState },
      blockField: 'measurementReportApproval',
      isMergedIntoStoredBlock: true,
    });
  },

  /**
   * Saves one card of the block project-acceptance-report-data (sparse input). The cards are bound
   * to the stored block object, which already holds the saved values; it is never replaced, so
   * unsaved input on other cards stays. Only the legacy copy `project.enpalReport`, still sent by
   * the other protocol tabs through updateProject, takes the saved enpalReport.
   */
  async updateProjectAcceptanceReportData({ commit, dispatch }, { projectNumber, input }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateProjectAcceptanceReportData(
            $projectNumber: Int!
            $input: ProjectAcceptanceReportDataInput!
          ) {
            updateProjectAcceptanceReportData(projectNumber: $projectNumber, input: $input) {
              id
              number
              blocks {
                acceptanceReportData {
                  id
                  number
                  enpalReport {
                    ${ENPAL_REPORT_SELECTION}
                  }
                }
              }
            }
          }
        `,
        variables: { projectNumber, input },
      });
      dispatch('message', {
        message: 'Projekt aktualisiert!',
        type: 'success',
        lifetime: 1,
      });
      const savedProject = response.data.updateProjectAcceptanceReportData;
      commit('MERGE_PROJECT_FIELDS', {
        id: savedProject.id,
        enpalReport: savedProject.blocks.acceptanceReportData.enpalReport,
      });
    } catch (err) {
      handleProjectSaveError(dispatch, err);
    }
  },

  /** Requests one acceptance verification from the protocol (data block). */
  async requestProjectAcceptanceVerification(context, { projectNumber, type }) {
    await saveProjectBlocks(context, {
      mutation: gql`
        mutation requestProjectAcceptanceVerification(
          $projectNumber: Int!
          $type: AcceptanceVerificationType!
          $withAcceptanceReportData: Boolean!
          $withAcceptanceReportVerification: Boolean!
        ) {
          requestProjectAcceptanceVerification(projectNumber: $projectNumber, type: $type) {
            id
            number
            ${ACCEPTANCE_VERIFICATION_STATE_BLOCKS_SELECTION}
          }
        }
        ${PROJECT_ACCEPTANCE_REPORT_VERIFICATION_FIELDS}
      `,
      mutationName: 'requestProjectAcceptanceVerification',
      variables: {
        projectNumber,
        type,
        ...acceptanceVerificationStateVariables(context.rootGetters),
      },
      blockFields: ['acceptanceReportData', 'acceptanceReportVerification'],
    });
  },

  /** Changes one verification in the block project-acceptance-report-verification. */
  async updateProjectAcceptanceVerificationState(
    context,
    { projectNumber, type, verificationState }
  ) {
    await saveProjectBlocks(context, {
      mutation: gql`
        mutation updateProjectAcceptanceVerificationState(
          $projectNumber: Int!
          $type: AcceptanceVerificationType!
          $verificationState: VerificationState!
          $withAcceptanceReportData: Boolean!
          $withAcceptanceReportVerification: Boolean!
        ) {
          updateProjectAcceptanceVerificationState(
            projectNumber: $projectNumber
            type: $type
            verificationState: $verificationState
          ) {
            id
            number
            ${ACCEPTANCE_VERIFICATION_STATE_BLOCKS_SELECTION}
          }
        }
        ${PROJECT_ACCEPTANCE_REPORT_VERIFICATION_FIELDS}
      `,
      mutationName: 'updateProjectAcceptanceVerificationState',
      variables: {
        projectNumber,
        type,
        verificationState,
        ...acceptanceVerificationStateVariables(context.rootGetters),
      },
      blockFields: ['acceptanceReportData', 'acceptanceReportVerification'],
    });
  },

  async updateProjectAcceptanceApproval(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectAcceptanceApproval(
          $projectNumber: Int!
          $input: ProjectAcceptanceApprovalInput!
        ) {
          updateProjectAcceptanceApproval(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              acceptanceReportApproval {
                ...projectAcceptanceReportApprovalFields
              }
            }
          }
        }
        ${PROJECT_ACCEPTANCE_REPORT_APPROVAL_FIELDS}
      `,
      mutationName: 'updateProjectAcceptanceApproval',
      variables: { projectNumber, input },
      blockField: 'acceptanceReportApproval',
    });
  },

  /**
   * Changes the approval state of the block project-acceptance-report-approval immediately.
   * Selects and sets only the state, so an unsaved note of the block stays.
   */
  async updateProjectAcceptanceApprovalState(context, { projectNumber, verificationState }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectAcceptanceApprovalState(
          $projectNumber: Int!
          $verificationState: VerificationState!
        ) {
          updateProjectAcceptanceApprovalState(
            projectNumber: $projectNumber
            verificationState: $verificationState
          ) {
            id
            number
            blocks {
              acceptanceReportApproval {
                id
                number
                acceptanceApprovementVerificationState
              }
            }
          }
        }
      `,
      mutationName: 'updateProjectAcceptanceApprovalState',
      variables: { projectNumber, verificationState },
      blockField: 'acceptanceReportApproval',
      isMergedIntoStoredBlock: true,
    });
  },

  /**
   * Saves one card of the block project-service-report-data (sparse input). The cards are bound to
   * the stored block object, which already holds the saved values; it is never replaced, so
   * unsaved input on other cards stays. Only the legacy copy `project.serviceReport`, still sent by
   * the Gutachten tab through updateProject, takes the saved serviceReport.
   */
  async updateProjectServiceReportData({ commit, dispatch }, { projectNumber, input }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateProjectServiceReportData(
            $projectNumber: Int!
            $input: ServiceReportInput!
          ) {
            updateProjectServiceReportData(projectNumber: $projectNumber, input: $input) {
              id
              number
              blocks {
                serviceReportData {
                  id
                  number
                  serviceReport {
                    ${SERVICE_REPORT_SELECTION}
                  }
                }
              }
            }
          }
        `,
        variables: { projectNumber, input },
      });
      dispatch('message', {
        message: 'Projekt aktualisiert!',
        type: 'success',
        lifetime: 1,
      });
      const savedProject = response.data.updateProjectServiceReportData;
      commit('MERGE_PROJECT_FIELDS', {
        id: savedProject.id,
        serviceReport: savedProject.blocks.serviceReportData.serviceReport,
      });
    } catch (err) {
      handleProjectSaveError(dispatch, err);
    }
  },

  /**
   * Saves one card of the block project-sales-report-data (sparse input). The cards are bound to
   * the stored block object, which already holds the saved values; it is never replaced, so
   * unsaved input on other cards stays. Only the legacy copy `project.salesReport` takes the saved
   * salesReport.
   */
  async updateProjectSalesReportData({ commit, dispatch }, { projectNumber, input }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateProjectSalesReportData($projectNumber: Int!, $input: SalesReportInput!) {
            updateProjectSalesReportData(projectNumber: $projectNumber, input: $input) {
              id
              number
              blocks {
                salesReportData {
                  id
                  number
                  salesReport {
                    ${SALES_REPORT_SELECTION}
                  }
                }
              }
            }
          }
        `,
        variables: { projectNumber, input },
      });
      dispatch('message', {
        message: 'Projekt aktualisiert!',
        type: 'success',
        lifetime: 1,
      });
      const savedProject = response.data.updateProjectSalesReportData;
      commit('MERGE_PROJECT_FIELDS', {
        id: savedProject.id,
        salesReport: savedProject.blocks.salesReportData.salesReport,
      });
    } catch (err) {
      handleProjectSaveError(dispatch, err);
    }
  },

  /**
   * Saves one card of the block project-quality-report-data (sparse input). The cards are bound to
   * the stored block object, which already holds the saved values; it is never replaced, so
   * unsaved input on other cards stays. Only the legacy copy `project.qualityReport` takes the
   * saved qualityReport. The roof type card also saves the roof type; the other protocol tabs
   * branch on it, so it is set in every loaded block (only when the card sent it).
   */
  async updateProjectQualityReportData({ commit, dispatch }, { projectNumber, input }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateProjectQualityReportData(
            $projectNumber: Int!
            $input: ProjectQualityReportDataInput!
          ) {
            updateProjectQualityReportData(projectNumber: $projectNumber, input: $input) {
              id
              number
              blocks {
                qualityReportData {
                  id
                  number
                  qualityReport {
                    ${QUALITY_REPORT_SELECTION}
                  }
                  measurement {
                    roofType
                  }
                }
              }
            }
          }
        `,
        variables: { projectNumber, input },
      });
      dispatch('message', {
        message: 'Projekt aktualisiert!',
        type: 'success',
        lifetime: 1,
      });
      const savedProject = response.data.updateProjectQualityReportData;
      const savedBlock = savedProject.blocks.qualityReportData;
      commit('MERGE_PROJECT_FIELDS', {
        id: savedProject.id,
        qualityReport: savedBlock.qualityReport,
      });
      if ('measurement' in input) {
        commit('UPDATE_MEASUREMENT_ROOF_TYPE', {
          id: savedProject.id,
          roofType: savedBlock.measurement.roofType,
        });
      }
    } catch (err) {
      handleProjectSaveError(dispatch, err);
    }
  },

  /** Requests the service verification from the protocol (data block); the API takes no type. */
  async requestProjectServiceVerification(context, { projectNumber }) {
    await saveProjectBlocks(context, {
      mutation: gql`
        mutation requestProjectServiceVerification(
          $projectNumber: Int!
          $withServiceReportData: Boolean!
          $withServiceReportVerification: Boolean!
        ) {
          requestProjectServiceVerification(projectNumber: $projectNumber) {
            id
            number
            ${SERVICE_VERIFICATION_STATE_BLOCKS_SELECTION}
          }
        }
        ${PROJECT_SERVICE_REPORT_VERIFICATION_FIELDS}
      `,
      mutationName: 'requestProjectServiceVerification',
      variables: {
        projectNumber,
        ...serviceVerificationStateVariables(context.rootGetters),
      },
      blockFields: ['serviceReportData', 'serviceReportVerification'],
    });
  },

  /** Changes the service verification in the block project-service-report-verification. */
  async updateProjectServiceVerificationState(context, { projectNumber, verificationState }) {
    await saveProjectBlocks(context, {
      mutation: gql`
        mutation updateProjectServiceVerificationState(
          $projectNumber: Int!
          $verificationState: VerificationState!
          $withServiceReportData: Boolean!
          $withServiceReportVerification: Boolean!
        ) {
          updateProjectServiceVerificationState(
            projectNumber: $projectNumber
            verificationState: $verificationState
          ) {
            id
            number
            ${SERVICE_VERIFICATION_STATE_BLOCKS_SELECTION}
          }
        }
        ${PROJECT_SERVICE_REPORT_VERIFICATION_FIELDS}
      `,
      mutationName: 'updateProjectServiceVerificationState',
      variables: {
        projectNumber,
        verificationState,
        ...serviceVerificationStateVariables(context.rootGetters),
      },
      blockFields: ['serviceReportData', 'serviceReportVerification'],
    });
  },

  async updateProjectServiceApproval(context, { projectNumber, input }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectServiceApproval(
          $projectNumber: Int!
          $input: ProjectServiceApprovalInput!
        ) {
          updateProjectServiceApproval(projectNumber: $projectNumber, input: $input) {
            id
            number
            blocks {
              serviceReportApproval {
                ...projectServiceReportApprovalFields
              }
            }
          }
        }
        ${PROJECT_SERVICE_REPORT_APPROVAL_FIELDS}
      `,
      mutationName: 'updateProjectServiceApproval',
      variables: { projectNumber, input },
      blockField: 'serviceReportApproval',
    });
  },

  /**
   * Changes the approval state of the block project-service-report-approval immediately. Selects
   * and sets only the state, so an unsaved note of the block stays.
   */
  async updateProjectServiceApprovalState(context, { projectNumber, verificationState }) {
    await saveProjectBlock(context, {
      mutation: gql`
        mutation updateProjectServiceApprovalState(
          $projectNumber: Int!
          $verificationState: VerificationState!
        ) {
          updateProjectServiceApprovalState(
            projectNumber: $projectNumber
            verificationState: $verificationState
          ) {
            id
            number
            blocks {
              serviceReportApproval {
                id
                number
                serviceApprovementVerificationState
              }
            }
          }
        }
      `,
      mutationName: 'updateProjectServiceApprovalState',
      variables: { projectNumber, verificationState },
      blockField: 'serviceReportApproval',
      isMergedIntoStoredBlock: true,
    });
  },

  /**
   * Assign an ItemPackage to a project's solarPlant
   * @param {*} param0
   * @param {*} payload - { projectNumber, itemPackageNumber }
   */
  async assignItemPackageToProject(
    { state, commit, dispatch },
    { projectNumber, itemPackageNumber }
  ) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation assignItemPackageToProject($projectNumber: Int!, $itemPackageNumber: Int!) {
            assignItemPackageToProject(
              projectNumber: $projectNumber
              itemPackageNumber: $itemPackageNumber
            ) {
              ...projectFields
              blocks {
                baseConfiguration {
                  ...projectBaseConfigurationFields
                }
              }
            }
          }
          ${PROJECT_REQUEST_FIELDS}
          ${PROJECT_BASE_CONFIGURATION_FIELDS}
        `,
        variables: {
          projectNumber: projectNumber,
          itemPackageNumber: itemPackageNumber,
        },
      });
      dispatch('message', {
        message: 'Paket zugewiesen!',
        type: 'success',
        lifetime: 2,
      });
      commit(
        'ADD_OR_UPDATE_PROJECT',
        withStoredBlocks(state.projects, response.data.assignItemPackageToProject)
      );
    } catch (err) {
      const message = err.graphQLErrors?.[0]?.message || 'Fehler beim Zuweisen des Pakets!';
      dispatch('message', {
        message: message,
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Remove an ItemPackage assignment from a project's solarPlant
   * @param {*} param0
   * @param {*} payload - { projectNumber, itemPackageNumber }
   */
  async removeItemPackageFromProject(
    { state, commit, dispatch },
    { projectNumber, itemPackageNumber }
  ) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation removeItemPackageFromProject($projectNumber: Int!, $itemPackageNumber: Int!) {
            removeItemPackageFromProject(
              projectNumber: $projectNumber
              itemPackageNumber: $itemPackageNumber
            ) {
              ...projectFields
              blocks {
                baseConfiguration {
                  ...projectBaseConfigurationFields
                }
              }
            }
          }
          ${PROJECT_REQUEST_FIELDS}
          ${PROJECT_BASE_CONFIGURATION_FIELDS}
        `,
        variables: {
          projectNumber: projectNumber,
          itemPackageNumber: itemPackageNumber,
        },
      });
      dispatch('message', {
        message: 'Paket entfernt!',
        type: 'success',
        lifetime: 2,
      });
      commit(
        'ADD_OR_UPDATE_PROJECT',
        withStoredBlocks(state.projects, response.data.removeItemPackageFromProject)
      );
    } catch (err) {
      const message = err.graphQLErrors?.[0]?.message || 'Fehler beim Entfernen des Pakets!';
      dispatch('message', {
        message: message,
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async handoverProjectToManagement({ commit, dispatch }, { projectNumber, note }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation handoverProjectToManagement($projectNumber: Int!, $note: String) {
            handoverProjectToManagement(projectNumber: $projectNumber, note: $note) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          projectNumber,
          note,
        },
      });
      dispatch('message', {
        message: 'Projekt an Projektmanagement übergeben!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_PROJECT', response.data.handoverProjectToManagement);
    } catch (err) {
      const message = err.graphQLErrors?.[0]?.message || 'Fehler bei der Übergabe!';
      dispatch('message', {
        message,
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * currently not implemented on server side!
   * @param {*} param0
   * @param {*} projectNumber
   */
  async deleteProject({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteProject($projectNumber: Int!) {
            deleteProject(projectNumber: $projectNumber)
          }
        `,
        variables: {
          projectNumber: projectNumber,
        },
      });
      dispatch('message', {
        message: 'Projekt gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_PROJECT', projectNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async sendProjectMail({ commit, dispatch }, { projectId, message }) {
    const payload = {
      subject: message.subject,
      body: message.body,
      type: message.type,
      attachmentType: message.attachmentType,
    };

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation sendProjectMail($projectId: ID!, $payload: MessageInput!) {
            sendProjectMail(projectId: $projectId, messageInput: $payload) {
              id
              number
              initialInformationSentToCustomerAt
              missingDocumentsSentToCustomerAt
              appointmentConfirmationSentAt
              operationRegistrationSentAt
              netRegistrationSentAt
              updatedAt
            }
          }
        `,
        variables: {
          projectId: projectId,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Nachricht verschickt!',
        type: 'success',
        lifetime: 1,
      });

      commit('UPDATE_PROJECT', response.data.sendProjectMail);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Versand!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async sendEmployerApprovalMail({ commit, dispatch }, { projectId, message }) {
    const payload = {
      subject: message.subject,
      body: message.body,
      type: message.type,
    };

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation sendEmployerApprovalMail($projectId: ID!, $payload: MessageInput!) {
            sendEmployerApprovalMail(projectId: $projectId, messageInput: $payload)
          }
        `,
        variables: {
          projectId: projectId,
          payload: payload,
        },
      });
      commit('UPDATE_EMPLOYER_CLEARANCE_AT', {
        id: projectId,
        employerClearanceAt: response.data.sendEmployerApprovalMail,
      });

      dispatch('message', {
        message: 'Nachricht verschickt!',
        type: 'success',
        lifetime: 1,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Versand!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Upload an image belonging to the project
   * @param commit
   * @param payload
   * @returns {Promise<void>}
   */
  async uploadProjectAttachment({ commit, dispatch }, payload) {
    // console.log(payload);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation uploadProjectAttachment($attachmentInput: AttachmentWithTypeInput!) {
            uploadProjectAttachment(attachmentInput: $attachmentInput) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          attachmentInput: {
            file: payload.file,
            referenceId: payload.parentId,
            referenceType: payload.parentType,
          },
        },
      });
      console.log(response.data.uploadProjectAttachment);
      commit('UPDATE_PROJECT_ATTACHMENTS', {
        updatedProject: response.data.uploadProjectAttachment,
        referenceType: payload.parentType,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Upload!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Delete Project Atrachment
   * @param commit
   * @param payload
   */
  async deleteProjectAttachment({ commit, dispatch }, payload) {
    // console.log(payload)
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteProjectAttachment(
            $projectId: ID!
            $attachmentId: ID!
            $attachmentType: String!
          ) {
            deleteProjectAttachment(
              projectId: $projectId
              attachmentId: $attachmentId
              attachmentType: $attachmentType
            ) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          projectId: payload.projectId,
          attachmentId: payload.attachmentId,
          attachmentType: payload.attachmentType,
        },
      });
      // console.log(response)
      // commit('UPDATE_PROJECT', response.data.deleteProjectAttachment);
      commit('UPDATE_PROJECT_ATTACHMENTS', {
        updatedProject: response.data.deleteProjectAttachment,
        referenceType: payload.attachmentType,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * NEW: Upload attachment using dynamic structure
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<void>}
   */
  async uploadProjectDynamicAttachment({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation uploadProjectDynamicAttachment($attachmentInput: AttachmentWithTypeInput!) {
            uploadProjectDynamicAttachment(attachmentInput: $attachmentInput) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          attachmentInput: {
            file: payload.file,
            referenceId: payload.parentId,
            referenceType: payload.parentType,
          },
        },
      });
      commit('UPDATE_PROJECT_DYNAMIC_ATTACHMENTS', {
        updatedProject: response.data.uploadProjectDynamicAttachment,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Upload!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * NEW: Delete Project Dynamic Attachment
   * @param commit
   * @param dispatch
   * @param payload
   */
  async deleteProjectDynamicAttachment({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteProjectDynamicAttachment($projectId: ID!, $attachmentId: ID!) {
            deleteProjectDynamicAttachment(projectId: $projectId, attachmentId: $attachmentId) {
              ...projectFields
            }
          }
          ${PROJECT_REQUEST_FIELDS}
        `,
        variables: {
          projectId: payload.projectId,
          attachmentId: payload.attachmentId,
        },
      });
      commit('UPDATE_PROJECT_DYNAMIC_ATTACHMENTS', {
        updatedProject: response.data.deleteProjectDynamicAttachment,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async buildNetRegistration({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildNetRegistration($projectId: ID!) {
            buildNetRegistration(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      // dispatch('message', {
      //   message: 'Netzanmeldung wird erstellt!',
      //   type: 'success',
      //   lifetime: 1,
      // });
      commit('UPDATE_NET_REGISTRATION_BUILD_STATE', {
        projectId: projectId,
        netRegistrationAttachmentsBuildState: response.data.buildNetRegistration,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async requestZipAttachment({ commit, dispatch }, { projectId, attachmentType }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation requestZipAttachment($projectId: ID!, $attachmentType: String!) {
            requestZipAttachment(projectId: $projectId, attachmentType: $attachmentType)
          }
        `,
        variables: {
          projectId: projectId,
          attachmentType: attachmentType,
        },
      });
      return response.data.requestZipAttachment;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async fetchZipBuildState({ commit }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              combinedNetRegistrationAttachmentsZipBuildState
              combinedNetRegistrationAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              netRegistrationAttachmentsGeneratedZipBuildState
              netRegistrationAttachmentsGeneratedZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              operationRegistrationAttachmentsGeneratedZipBuildState
              operationRegistrationAttachmentsGeneratedZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              overviewSchematicAttachmentsGeneratedZipBuildState
              overviewSchematicAttachmentsGeneratedZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              mandateAttachmentsZipBuildState
              mandateAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              overviewSchematicAttachmentsZipBuildState
              overviewSchematicAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              layoutPlanAttachmentsZipBuildState
              layoutPlanAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              projectDescriptionAttachmentsZipBuildState
              projectDescriptionAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              netRegistrationAttachmentsZipBuildState
              netRegistrationAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              operationRegistrationAttachmentsZipBuildState
              operationRegistrationAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              miscAttachmentsZipBuildState
              miscAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              teamACAttachmentsZipBuildState
              teamACAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              teamDCAttachmentsZipBuildState
              teamDCAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              damageAttachmentsZipBuildState
              damageAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              measurementAttachmentsZipBuildState
              measurementAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              operationRegistrationImageAttachmentsZipBuildState
              operationRegistrationImageAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }

              installationTeamDocumentationAttachmentsZipBuildState
              installationTeamDocumentationAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              installationImageAttachmentsZipBuildState
              installationImageAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              offerAttachmentsZipBuildState
              offerAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              invoiceAttachmentsZipBuildState
              invoiceAttachmentsZip {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
              acceptanceReportImageAttachmentsZipBuildState
              acceptanceReportImageAttachmentsZip {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
              measurementReportImageAttachmentsZipBuildState
              measurementReportImageAttachmentsZip {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
              serviceReportImageAttachmentsZipBuildState
              serviceReportImageAttachmentsZip {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
              roofTileAttachments {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
              roofAttachments {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }

              meterCabinetAttachments {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
              attachmentBuildStates {
                attachmentType
                state
              }
              bank {
                attachments {
                  id
                  path
                  filename
                  displayFilename
                  size
                  mimetype
                  attachmentType
                }
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async buildOperationRegistration({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildOperationRegistration($projectId: ID!) {
            buildOperationRegistration(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_OPERATION_REGISTRATION_BUILD_STATE', {
        projectId: projectId,
        operationRegistrationAttachmentsBuildState: response.data.buildOperationRegistration,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Start the build of a qcells offer pdf
   * @param {*} param0
   * @param {*} projectId
   */
  async buildProjectOffer({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildProjectOffer($projectId: ID!) {
            buildProjectOffer(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_PROJECT_OFFER_BUILD_STATE', {
        projectId: projectId,
        projectOfferAttachmentsBuildState: response.data.buildProjectOffer,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Start the build of a qcells offer pdf
   * @param {*} param0
   * @param {*} projectId
   */
  async buildConsumablesList({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildConsumablesList($projectId: ID!) {
            buildConsumablesList(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_CONSUMABLES_LIST_BUILD_STATE', {
        projectId: projectId,
        buildConsumablesListBuildState: response.data.buildConsumablesList,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   *
   * @param {*} param0
   * @param {*} projectNumber
   */
  async fetchConsumablesListAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              consumablesListAttachmentsBuildState
              consumablesListAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Start the build of a measurement report pdf
   * @param {*} param0
   * @param {*} projectId
   */
  async buildMeasurementReport({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildMeasurementReport($projectId: ID!) {
            buildMeasurementReport(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_MEASUREMENT_REPORT_BUILD_STATE', {
        projectId: projectId,
        measurementReportAttachmentsBuildState:
          response.data.measurementReportAttachmentsBuildState,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Start the build of a short teaser (Kurzteaser) PDF
   * @param {*} param0
   * @param {*} projectId
   */
  async buildShortTeaser({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildShortTeaser($projectId: ID!) {
            buildShortTeaser(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_ATTACHMENT_BUILD_STATE', {
        projectId,
        attachmentType: 'SHORT_TEASER',
        buildState: response.data.buildShortTeaser,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Start the build of a master presentation (Masterpräsentation) PDF
   * @param {*} param0
   * @param {*} projectId
   */
  async buildMasterPresentation({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildMasterPresentation($projectId: ID!) {
            buildMasterPresentation(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_ATTACHMENT_BUILD_STATE', {
        projectId,
        attachmentType: 'MASTER_PRESENTATION',
        buildState: response.data.buildMasterPresentation,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Start the build of a sales report (Vertriebsbericht) PDF
   * @param {*} param0
   * @param {*} projectId
   */
  async buildSalesReport({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildSalesReport($projectId: ID!) {
            buildSalesReport(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_ATTACHMENT_BUILD_STATE', {
        projectId,
        attachmentType: 'SALES_REPORT',
        buildState: response.data.buildSalesReport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Start the build of a service report pdf
   * @param {*} param0
   * @param {*} projectId
   */
  async buildServiceReport({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildServiceReport($projectId: ID!) {
            buildServiceReport(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_SERVICE_REPORT_BUILD_STATE', {
        projectId: projectId,
        serviceReportAttachmentsBuildState: response.data.serviceReportAttachmentsBuildState,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Start the build of a service report pdf
   * @param {*} param0
   * @param {*} projectId
   */
  async buildAssessmentReport({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildAssessmentReport($projectId: ID!) {
            buildAssessmentReport(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_ASSESSMENT_REPORT_BUILD_STATE', {
        projectId: projectId,
        assessmentReportAttachmentsBuildState: response.data.assessmentReportAttachmentsBuildState,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Update build state of the measurement report pdf
   * @param {*} param0
   * @param {*} projectNumber
   */
  async fetchAssessmentReportAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              assessmentReportAttachmentsBuildState
              assessmentReportAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber);
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },
  /**
   * Update build state of the measurement report pdf
   * @param {*} param0
   * @param {*} projectNumber
   */
  async fetchServiceReportAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              serviceReportAttachmentsBuildState
              serviceReportAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Update build state of the measurement report pdf
   * @param {*} param0
   * @param {*} projectNumber
   */
  async fetchMeasurementReportAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              measurementReportAttachmentsBuildState
              measurementReportAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Refresh project attachment build states and attachments (e.g. for polling after async PDF build)
   * @param {*} param0
   * @param {*} param1 - { projectNumber, attachmentType }
   * @returns {string} BuildState for the given attachmentType
   */
  async fetchAttachmentBuildState({ commit }, { projectNumber, attachmentType }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              attachmentBuildStates {
                attachmentType
                state
              }
              attachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
                attachmentType
                createdAt
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber, 10),
        },
        fetchPolicy: 'network-only',
      });
      const updatedProject = response.data.projectByNumber;
      commit('UPDATE_PROJECT', updatedProject);
      const entry = updatedProject.attachmentBuildStates?.find(
        (item) => item.attachmentType === attachmentType
      );
      return entry?.state || 'NOT_STARTED';
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Request an async build of the financial model (Finanzierungsmodell) XLSX export
   * @param {*} param0
   * @param {*} payload - contains projectNumber
   */
  async buildFinModelExport({ commit, dispatch, state }, { projectNumber }) {
    if (!projectNumber) {
      throw new Error('projectNumber is undefined or null');
    }

    const parsedProjectNumber = parseInt(projectNumber, 10);
    const project = state.projects.find((entry) => entry.number == parsedProjectNumber);
    if (!project?.id) {
      throw new Error('project not found in store');
    }

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildFinModelExport($projectNumber: Int!) {
            buildFinModelExport(projectNumber: $projectNumber)
          }
        `,
        variables: {
          projectNumber: parsedProjectNumber,
        },
      });
      commit('UPDATE_ATTACHMENT_BUILD_STATE', {
        projectId: project.id,
        attachmentType: 'FIN_MODEL',
        buildState: response.data.buildFinModelExport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen des Finanzierungsmodells!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async buildOverviewSchematic({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildOverviewSchematic($projectId: ID!) {
            buildOverviewSchematic(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_OVERVIEW_SCHEMATIC_BUILD_STATE', {
        projectId: projectId,
        overviewSchematicAttachmentsBuildState: response.data.buildOverviewSchematic,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async buildReport({ commit, dispatch }, projectId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildReport($projectId: ID!) {
            buildReport(projectId: $projectId)
          }
        `,
        variables: {
          projectId: projectId,
        },
      });
      commit('UPDATE_REPORT_BUILD_STATE', {
        projectId: projectId,
        reportAttachmentsBuildState: response.data.buildReport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async fetchNetRegistrationAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              netRegistrationAttachmentsBuildState
              netRegistrationAttachmentsGenerated {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchProjectOfferAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              projectOfferAttachmentsBuildState
              projectOfferAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchOperationRegistrationAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              operationRegistrationAttachmentsBuildState
              operationRegistrationAttachmentsGenerated {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchOverviewSchematicAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              overviewSchematicAttachmentsBuildState
              overviewSchematicAttachmentsGenerated {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Update Build State for PDF Reports
   * Partial project update!
   * @param {*} param0
   * @param {*} projectNumber
   */
  async fetchReportAttachmentsBuildState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              reportAttachmentsBuildState
              reportAttachments {
                id
                path
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber)
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Start portal registration
   * @param {*} param0
   * @param {*} projectId
   */
  async startPortalRegistration({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation startPortalRegistration($projectNumber: Int!) {
            startPortalRegistration(projectNumber: $projectNumber) {
              id
              number
              referenceId
              type
              state
              powerCompany {
                company
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      commit('UPDATE_PORTAL_REGISTRATION_STATE', {
        projectNumber: projectNumber,
        registrations: response.data.startPortalRegistration,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteRegistration({ commit, dispatch }, { projectNumber, registrationId }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteRegistration($projectNumber: Int!, $registrationId: ID!) {
            deleteRegistration(projectNumber: $projectNumber, registrationId: $registrationId) {
              id
              number
              referenceId
              type
              state
              powerCompany {
                company
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
          registrationId: registrationId,
        },
      });
      commit('UPDATE_PORTAL_REGISTRATION_STATE', {
        projectNumber: projectNumber,
        registrations: response.data.deleteRegistration,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Update State for portal registrations
   * Partial project update!
   * @param {*} param0
   * @param {*} projectNumber
   * @deprecated
   */
  async fetchPortalRegistrationState({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectByNumber($projectNumber: Int!) {
            projectByNumber(projectNumber: $projectNumber) {
              id
              number
              portalRegistrationState
              registrations {
                id
                number
                referenceId
                type
                state
                createdAt
                powerCompany {
                  company
                }
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectByNumber);
      commit('UPDATE_PROJECT', response.data.projectByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async saveReportSignature({ commit, dispatch }, { projectId, payload, signatureType }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation saveReportSignature($projectId: ID!, $payload: String, $signatureType: String!) {
            saveReportSignature(
              projectId: $projectId
              payload: $payload
              signatureType: $signatureType
            )
          }
        `,
        variables: {
          projectId: projectId,
          payload: payload,
          signatureType: signatureType,
        },
      });
      return response.data.saveReportSignature;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Create a post
   * @param commit
   * @param post
   * @returns {Promise<void>}
   */
  async addPostToProject({ commit }, post) {
    const validatedPayload = preparePayloadForGraphQL(post, {
      content: 'string',
      user: 'id',
      project: 'id',
    });
    // console.log('addPostToProject', validatedPayload);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation addPostToProject($payload: CreatePostInput!) {
            addPostToProject(postInput: $payload) {
              id
              content
              createdAt
              updatedAt
              project {
                id
                number
              }
              user {
                id
                number
                firstname
                lastname
                email
                avatar {
                  path
                }
              }
              visits {
                user {
                  number
                  firstname
                  lastname
                  email
                  avatar {
                    path
                  }
                }
                createdAt
                updatedAt
                visitedAt
              }
            }
          }
        `,
        variables: {
          payload: validatedPayload,
        },
      });
      // console.log(validatedPayload.project, response.data.addPostToProject);

      commit('UPDATE_PROJECT_POSTS', {
        projectId: validatedPayload.project,
        posts: response.data.addPostToProject,
      });
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /**
   * delete a post from a project
   * @param commit
   * @param payload
   * @param id
   * @returns {Promise<void>}
   */
  async deletePostFromProject({ commit }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deletePostFromProject($postId: ID!, $projectId: ID!) {
            deletePostFromProject(postId: $postId, projectId: $projectId) {
              id
              content
              createdAt
              updatedAt
              project {
                id
                number
              }
              user {
                id
                number
                firstname
                lastname
                email
                avatar {
                  path
                }
              }
              visits {
                user {
                  number
                  firstname
                  lastname
                  email
                  avatar {
                    path
                  }
                }
                createdAt
                updatedAt
                visitedAt
              }
            }
          }
        `,
        variables: {
          postId: payload.postId,
          projectId: payload.projectId,
        },
      });
      // console.log(response)
      commit('UPDATE_PROJECT_POSTS', {
        projectId: payload.projectId,
        posts: response.data.deletePostFromProject,
      });
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /**
   * fetch if project data is outdated
   *
   * @param {*} param0
   * @param {*} projectNumber
   * @deprecated
   */
  async fetchProjectIsOutdated({ commit, dispatch }, projectNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectIsOutdated($projectNumber: Int!) {
            projectIsOutdated(projectNumber: $projectNumber)
          }
        `,
        variables: {
          projectNumber: projectNumber,
        },
      });
      // console.log(response.data.projectIsOutdated);
      return response.data.projectIsOutdated;
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * fetch projectEditingState
   *
   * @param {*} param0
   * @param {*} projectNumber
   * @deprecated
   */
  async fetchProjectEditingState({ commit, dispatch }, projectNumber) {
    // console.log('fetchProjectEditingState()', projectNumber);
    try {
      const response = await apolloClient.query({
        query: gql`
          query projectEditingState($projectNumber: Int!) {
            projectEditingState(projectNumber: $projectNumber) {
              projectIsOutdated
              recentProjectUsers {
                _id
                action
                createdAt
                user {
                  firstname
                  lastname
                  email
                }
              }
            }
          }
        `,
        variables: {
          projectNumber: parseInt(projectNumber),
        },
      });
      // console.log(response.data.projectIsOutdated);
      return response.data.projectEditingState;
    } catch (err) {
      throw new Error(err);
    }
  },

  async updateProjectVerificationState({ commit }, { projectNumber, verificationStateInput }) {
    const validatedPayload = preparePayloadForGraphQL(verificationStateInput, {
      verificationState: 'string',
      verificationStateType: 'string',
    });

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateProjectVerificationState(
            $projectNumber: Int!
            $verificationStateInput: VerificationStateInput!
          ) {
            updateProjectVerificationState(
              projectNumber: $projectNumber
              verificationStateInput: $verificationStateInput
            )
          }
        `,
        variables: {
          verificationStateInput: validatedPayload,
          projectNumber: projectNumber,
        },
      });
      // console.log(response.data.updateProjectVerificationState);

      commit('UPDATE_PROJECT_VERIFICATION_STATE', {
        projectNumber: projectNumber,
        verificationStateType: verificationStateInput.verificationStateType,
        verficationState: response.data.updateProjectVerificationState,
      });
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /**
   * create a new project object in store and return it
   * @param {*} param0
   * @param {*} project
   */
  async initProject({ commit }) {
    commit('ADD_OR_UPDATE_PROJECT', defaultProject());
  },

  projectLocalUpdate({ commit }, project) {
    commit('ADD_OR_UPDATE_PROJECT', project);
  },

  projectCleanup({ commit }) {
    commit('REMOVE_PROJECT', '_new');
  },

  addItem({ commit }, itemData) {},
};

/**
 * Mutations
 */
const mutations = {
  SET_PROJECTS_ITEMS_LIST: (state, projects) =>
    projects ? (state.projectsItemsList = projects) : (state.projectsItemsList = []),

  SET_PROJECTS_ITEMS_LIST_COUNT: (state, count) => (state.projectsItemsListCount = count),

  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_PROJECTS_IN_NEEDS_ANALYSIS(state, projects) {
    state.projectsInNeedsAnalysis = projects;
  },
  SET_PROJECTS_COUNT(state, count) {
    state.projectCount = count;
  },
  SET_PROJECT_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_PROJECTS: (state, projects) =>
    projects ? (state.projects = projects) : (state.projects = []),
  ADD_PROJECT: (state, project) => state.projects.push(project),
  ADD_OR_UPDATE_PROJECT: (state, updatedProject) => {
    // const existingProject = state.projects.find((project) => project.id === updatedProject.id);
    setDefaultInstallationAddress(updatedProject);
    const projectIndex = state.projects.findIndex((project) => project.id === updatedProject.id);
    // if (existingProject) {
    if (projectIndex !== -1) {
      // Object.assign(existingProject, {}, updatedProject);
      Vue.set(state.projects, projectIndex, updatedProject);
    } else {
      state.projects.push(updatedProject);
    }
  },
  /**
   * Merges a partial project (result of a block mutation) into the stored project. Nested objects
   * such as `solarPlant` are merged one level deep; the project object stays the same.
   */
  MERGE_PROJECT_FIELDS: (state, projectFields) => {
    const project = state.projects.find((project) => project.id === projectFields.id);
    if (!project) {
      return;
    }
    Object.entries(projectFields).forEach(([field, value]) => {
      const isNestedMerge = isPlainObject(value) && isPlainObject(project[field]);
      if (!isNestedMerge) {
        Vue.set(project, field, value);
        return;
      }
      Object.entries(value).forEach(([nestedField, nestedValue]) => {
        Vue.set(project[field], nestedField, nestedValue);
      });
    });
  },
  /** Sets fields on the stored block object `project.blocks[blockField]`; the object stays. */
  MERGE_PROJECT_BLOCK_FIELDS: (state, { id, blockField, blockFields }) => {
    const block = state.projects.find((project) => project.id === id)?.blocks?.[blockField];
    if (!block) {
      return;
    }
    Object.entries(blockFields).forEach(([field, value]) => {
      Vue.set(block, field, value);
    });
  },
  UPDATE_PROJECT: (state, updatedProject) => {
    const project = state.projects.find((project) => project.id === updatedProject.id);
    Object.assign(project, updatedProject);
  },
  UPDATE_EMPLOYER_CLEARANCE_AT: (state, updatedProject) => {
    const project = state.projects.find((project) => project.id === updatedProject.id);
    Vue.set(project, 'employerClearanceAt', updatedProject.employerClearanceAt);
  },
  SET_PROJECT_TYPES: (state, projectTypes) => {
    state.projectTypes = [];
    projectTypes.map((projectType) => {
      state.projectTypes.push(projectType.name);
    });
  },
  ADD_OR_UPDATE_POSTS(state, { projectNumber, posts }) {
    const project = state.projects.find((p) => p.number === projectNumber);
    if (project) {
      project.posts = posts;
    }
  },
  REMOVE_PROJECT: (state, projectId) => {
    const index = state.projects.findIndex((project) => project.id === projectId);
    state.projects.splice(index, 1);
  },
  ADD_EMPTY_PROJECT_CONTACT: (state, updatedProject) => {
    const company = state.projects.find((project) => project.number == updatedProject.number);
    company.contacts.push({});
  },
  REMOVE_PROJECT_CONTACT: (state, { updatedProject, index }) => {
    const company = state.projects.find((project) => project.number == updatedProject.number);
    company.contacts.splice(index, 1);
  },
  SET_DOCUMENT_STATES: (state, documentStates) => {
    state.documentStates = [];
    documentStates.map((documentState) => {
      state.documentStates.push(documentState.name);
    });
  },
  SET_PROJECT_STATES: (state, projectStates) => {
    state.projectStates = [];
    projectStates.map((projectState) => {
      state.projectStates.push(projectState.name);
    });
  },
  SET_NETWORK_SUPPLY_MANAGEMENT_STATES: (state, networkSupplyManagementStates) => {
    state.networkSupplyManagementStates = [];
    networkSupplyManagementStates.map((networkSupplyManagementState) => {
      state.networkSupplyManagementStates.push(networkSupplyManagementState.name);
    });
  },
  SET_NETWORK_SUPPLY_MANAGEMENT_PERMANENT_RESTRICTION_STATES: (
    state,
    networkSupplyManagementPermanentRestrictionStates
  ) => {
    state.networkSupplyManagementPermanentRestrictionStates = [];
    networkSupplyManagementPermanentRestrictionStates.map(
      (networkSupplyManagementPermanentRestrictionState) => {
        state.networkSupplyManagementPermanentRestrictionStates.push(
          networkSupplyManagementPermanentRestrictionState.name
        );
      }
    );
  },
  SET_NETWORK_SUPPLY_MANAGEMENT_X_OVER_STATES: (state, networkSupplyManagementXOverStates) => {
    state.networkSupplyManagementXOverStates = [];
    networkSupplyManagementXOverStates.map((networkSupplyManagementXOverState) => {
      state.networkSupplyManagementXOverStates.push(networkSupplyManagementXOverState.name);
    });
  },
  SET_NET_OUTPUT_STATES: (state, netOutputStates) => {
    state.netOutputStates = [];
    netOutputStates.map((netOutputState) => {
      state.netOutputStates.push(netOutputState.name);
    });
  },
  SET_STORAGE_COUPLING_STATES: (state, storageCouplingStates) => {
    state.storageCouplingStates = [];
    storageCouplingStates.map((storageCouplingState) => {
      state.storageCouplingStates.push(storageCouplingState.name);
    });
  },
  SET_ELECTRIC_METER_MOUNTING_STATES: (state, electricMeterMountingStates) => {
    state.electricMeterMountingStates = [];
    electricMeterMountingStates.map((electricMeterMountingState) => {
      state.electricMeterMountingStates.push(electricMeterMountingState.name);
    });
  },
  SET_HOUSE_FUSE_STATES: (state, houseFuseStates) => {
    state.houseFuseStates = [];
    houseFuseStates.map((houseFuseState) => {
      state.houseFuseStates.push(houseFuseState.name);
    });
  },
  SET_SALUTATIONS: (state, salutations) => {
    state.salutations = [];
    salutations.map((salutation) => {
      state.salutations.push(salutation.name);
    });
  },
  SET_INSTALLATION_TEAMS: (state, installationTeams) => {
    state.installationTeams = [];
    installationTeams.map((installationTeam) => {
      state.installationTeams.push(installationTeam.name);
    });
  },
  SET_INSTALLATION_TYPES: (state, installationTypes) => {
    state.installationTypes = [];
    installationTypes.map((installationType) => {
      state.installationTypes.push(installationType.name);
    });
  },
  SET_ORIGINAL_INSTALLATION_TYPES: (state, originalInstallationTypes) => {
    state.originalInstallationTypes = [];
    originalInstallationTypes.map((originalInstallationType) => {
      state.originalInstallationTypes.push(originalInstallationType.name);
    });
  },
  SET_SOLAR_SYSTEM_TYPES: (state, solarSystemTypes) => {
    state.solarSystemTypes = [];
    solarSystemTypes.map((solarSystemType) => {
      state.solarSystemTypes.push(solarSystemType.name);
    });
  },
  SET_PROJECT_ATTACHMENT_TYPES: (state, projectAttachmentTypes) => {
    state.projectAttachmentTypes = [];
    projectAttachmentTypes.map((projectAttachmentType) => {
      state.projectAttachmentTypes.push(projectAttachmentType.name);
    });
  },

  /**
   * Sets the saved roof type on the legacy `project.measurement` and on every loaded block with a
   * `measurement` object (the protocol data blocks read it as reference value), without replacing
   * an object, so unsaved input stays.
   */
  UPDATE_MEASUREMENT_ROOF_TYPE: (state, { id, roofType }) => {
    const project = state.projects.find((project) => project.id === id);
    if (!project) return;
    if (isPlainObject(project.measurement)) {
      Vue.set(project.measurement, 'roofType', roofType);
    } else {
      Vue.set(project, 'measurement', { roofType });
    }
    // `blocks` also holds `__typename`; only block objects can carry measurement.
    Object.values(project.blocks ?? {})
      .filter((block) => isPlainObject(block) && isPlainObject(block.measurement))
      .forEach((block) => Vue.set(block.measurement, 'roofType', roofType));
  },

  UPDATE_PROJECT_ATTACHMENTS: (state, { updatedProject, referenceType }) => {
    const project = state.projects.find((project) => project.id === updatedProject.id);
    if (!project) return;
    if (UNIFIED_PROJECT_ATTACHMENT_TYPE_VALUES.includes(referenceType)) {
      Vue.set(project, 'attachments', updatedProject.attachments);
      if (updatedProject.attachmentBuildStates !== undefined) {
        Vue.set(project, 'attachmentBuildStates', updatedProject.attachmentBuildStates);
      }
      return;
    }
    Vue.set(project, referenceType, updatedProject[referenceType]);
    // Blocks that show the list (e.g. the measurement data block, the documents block) take it
    // too, without a reload that would replace the block object and drop unsaved input.
    // `blocks` also holds `__typename`; only block objects can show the list.
    Object.values(project.blocks ?? {})
      .filter((block) => isPlainObject(block) && referenceType in block)
      .forEach((block) => Vue.set(block, referenceType, updatedProject[referenceType]));
  },

  /**
   * NEW: Update project with new dynamic attachments structure
   */
  UPDATE_PROJECT_DYNAMIC_ATTACHMENTS: (state, { updatedProject }) => {
    const project = state.projects.find((project) => project.id === updatedProject.id);
    // Update both dynamic attachments and legacy fields
    Vue.set(project, 'attachments', updatedProject.attachments);
    Vue.set(project, 'attachmentTypes', updatedProject.attachmentTypes);
    if (updatedProject.bank) {
      Vue.set(project, 'bank', updatedProject.bank);
    }
  },

  UPDATE_NET_REGISTRATION_BUILD_STATE: (
    state,
    { projectId, netRegistrationAttachmentsBuildState }
  ) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.netRegistrationAttachmentsBuildState = netRegistrationAttachmentsBuildState;
  },
  UPDATE_OPERATION_REGISTRATION_BUILD_STATE: (
    state,
    { projectId, operationRegistrationAttachmentsBuildState }
  ) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.operationRegistrationAttachmentsBuildState = operationRegistrationAttachmentsBuildState;
  },
  UPDATE_PROJECT_OFFER_BUILD_STATE: (state, { projectId, projectOfferAttachmentsBuildState }) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.projectOfferAttachmentsBuildState = projectOfferAttachmentsBuildState;
  },
  UPDATE_CONSUMABLES_LIST_BUILD_STATE: (
    state,
    { projectId, consumablesListAttachmentsBuildState }
  ) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.consumablesListAttachmentsBuildState = consumablesListAttachmentsBuildState;
  },

  UPDATE_MEASUREMENT_REPORT_BUILD_STATE: (
    state,
    { projectId, measurementReportAttachmentsBuildState }
  ) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.measurementReportAttachmentsBuildState = measurementReportAttachmentsBuildState;
  },

  UPDATE_ATTACHMENT_BUILD_STATE: (storeState, { projectId, attachmentType, buildState }) => {
    const project = storeState.projects.find((p) => p.id === projectId);
    if (!project) return;
    if (!project.attachmentBuildStates) {
      Vue.set(project, 'attachmentBuildStates', []);
    }
    const existingEntry = project.attachmentBuildStates.find(
      (item) => item.attachmentType === attachmentType
    );
    if (existingEntry) {
      Vue.set(existingEntry, 'state', buildState);
    } else {
      project.attachmentBuildStates.push({
        attachmentType,
        state: buildState,
      });
    }
  },

  UPDATE_SERVICE_REPORT_BUILD_STATE: (state, { projectId, serviceReportAttachmentsBuildState }) => {
    const project = state.projects.find((project) => project.id === projectId);
    if (project) {
      project.serviceReportAttachmentsBuildState = serviceReportAttachmentsBuildState;
    }
  },
  UPDATE_ASSESSMENT_REPORT_BUILD_STATE: (
    state,
    { projectId, assessmentReportAttachmentsBuildState }
  ) => {
    const project = state.projects.find((project) => project.id === projectId);
    if (project) {
      project.assessmentReportAttachmentsBuildState = assessmentReportAttachmentsBuildState;
    }
  },

  UPDATE_OVERVIEW_SCHEMATIC_BUILD_STATE: (
    state,
    { projectId, overviewSchematicAttachmentsBuildState }
  ) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.overviewSchematicAttachmentsBuildState = overviewSchematicAttachmentsBuildState;
  },
  UPDATE_REPORT_BUILD_STATE: (state, { projectId, reportAttachmentsBuildState }) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.reportAttachmentsBuildState = reportAttachmentsBuildState;
  },
  UPDATE_PORTAL_REGISTRATION_STATE: (state, { projectNumber, registrations }) => {
    const project = state.projects.find((project) => project.number === projectNumber);
    project.registrations = registrations;
  },
  SET_PROJECT_COUNT: (state, projectCount) => (state.projectCount = projectCount),
  UPDATE_PROJECT_POSTS: (state, { projectId, posts }) => {
    const project = state.projects.find((project) => project.id === projectId);
    project.posts = posts;
  },
  UPDATE_PROJECT_VERIFICATION_STATE: (
    state,
    { projectNumber, verificationStateType, verficationState }
  ) => {
    const project = state.projects.find((project) => project.number === projectNumber);
    project[verificationStateType] = verficationState;
  },
};

/**
 * Getters
 */
const getters = {
  getProjectsItemsList: (state) => state.projectsItemsList,
  getProjectsItemsListCount: (state) => state.projectsItemsListCount,
  getProjects: (state) => {
    // map latest installation appointment to first level
    // state.projects.map((project) => {
    // project.solarPlant.solarPlantOverallPower = project.solarPlant.solarPlantOverallPower
    //   ? project.solarPlant.solarPlantOverallPower
    //   : solarPlantTotalPanelPower(project.solarPlant.solarPanels);
    //   project.initialInformationSent = project.initialInformationSentToCustomerAt ? 'YES' : 'NO';
    // });

    return state.projects;
  },
  getProjectsSortedByLastname: (state) => {
    const projects = state.projects;
    return projects.sort((a, b) => {
      if (!a.customer.lastname || !b.customer.lastname) {
        return 0;
      }
      var nameA = a.customer.lastname.toLowerCase(); // Groß-/Kleinschreibung ignorieren
      var nameB = b.customer.lastname.toLowerCase(); // Groß-/Kleinschreibung ignorieren
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  },
  getDocumentStates: (state) => state.documentStates,
  getProjectStates: (state) => state.projectStates,
  getNetworkSupplyManagementStates: (state) => state.networkSupplyManagementStates,
  getNetworkSupplyManagementPermanentRestrictionStates: (state) =>
    state.networkSupplyManagementPermanentRestrictionStates,
  getNetworkSupplyManagementXOverStates: (state) => state.networkSupplyManagementXOverStates,
  getNetOutputStates: (state) => state.netOutputStates,
  getStorageCouplingStates: (state) => state.storageCouplingStates,
  getElectricMeterMountingStates: (state) => state.electricMeterMountingStates,
  getHouseFuseStates: (state) => state.houseFuseStates,
  getInstallationTeams: (state) => state.installationTeams,
  getInstallationTypes: (state) => state.installationTypes,
  getOriginalInstallationTypes: (state) => state.originalInstallationTypes,
  getSolarSystemTypes: (state) => state.solarSystemTypes,
  getProjectAttachmentTypes: (state) => state.projectAttachmentTypes,
  getProject: (state) => (projectId) => state.projects.find((project) => project.id == projectId),
  getProjectsInNeedsAnalysis: (state) => state.projectsInNeedsAnalysis,
  // getProjectsInNeedsAnalysisCount: (state) => state.projectsInNeedsAnalysis.length,

  getProjectByNumber: (state) => (projectNumber) => {
    const project = state.projects.find((project) => project.number == projectNumber);
    return project;
  },

  /**
   * Build state from {@link attachmentBuildStates} for a project and attachment type string
   */
  getAttachmentBuildState: (state) => (projectId, attachmentType) => {
    const project = state.projects.find((p) => p.id === projectId);
    if (!project || !project.attachmentBuildStates) {
      return 'NOT_STARTED';
    }
    const entry = project.attachmentBuildStates.find(
      (item) => item.attachmentType === attachmentType
    );
    return entry?.state || 'NOT_STARTED';
  },

  getProjectCount: (state) => state.projectCount,

  /**
   * Get attachments by type for a project
   */
  getProjectAttachmentsByType: (state) => (projectNumber, attachmentType) => {
    const project = state.projects.find((project) => project.number == projectNumber);
    if (!project || !project.attachments) return [];

    return project.attachments.filter((attachment) => attachment.attachmentType === attachmentType);
  },

  /**
   * Get all attachments grouped by type for a project
   */
  getProjectAttachmentsGroupedByType: (state) => (projectNumber) => {
    const project = state.projects.find((project) => project.number == projectNumber);
    if (!project || !project.attachments) return {};

    const grouped = {};
    project.attachments.forEach((attachment) => {
      const type = attachment.attachmentType || 'unknown';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(attachment);
    });

    return grouped;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
